// Staff Training Manual — static content.
// Sections are role-gated via the `audience` field:
//   - "all"        → visible to admin, prescriber, IT, CS
//   - "prescriber" → visible to admin, prescriber, IT (prescriber-specific workflow)
//   - "admin"      → visible to admin + IT only
//   - "it"         → visible to admin + IT only
// Prescribers never see admin/IT-only sections.

export const TRAINING_CATEGORIES = [
  { key: "getting-started", label: "Getting Started", audience: "all" },
  { key: "prescriber", label: "Prescriber Workflow", audience: "prescriber" },
  { key: "labflo", label: "LabFlo Pharmacy Integration", audience: "all" },
  { key: "admin-ops", label: "Admin Operations", audience: "admin" },
  { key: "admin-marketing", label: "Marketing & Loyalty (Admin)", audience: "admin" },
  { key: "it-triage", label: "IT / Error Triage", audience: "it" },
  { key: "compliance", label: "Compliance & HIPAA", audience: "admin" },
  { key: "faq", label: "FAQs & Pitfalls", audience: "all" },
];

// Each section belongs to a category. `body` is React-rendered via simple markdown-lite
// (paragraphs, **bold**, - bullets, 1. numbered, > callouts, `code`).
export const TRAINING_SECTIONS = [
  // -------- GETTING STARTED (all) --------
  {
    id: "gs-login",
    category: "getting-started",
    title: "Logging in to the Staff Portal",
    audience: "all",
    body: `Staff access the portal at **/cliniclogin** (not the patient /login page).

**Steps**
1. Enter your User ID (format: **GF######**, e.g. GF000002).
2. Enter your password.
3. Click **Sign In**.
4. You are routed automatically to the dashboard for your role (Admin → Admin Dashboard; Prescriber → Prescriber Dashboard; IT → IT Dashboard).

**If you forget your password**
- Use the "Forgot password?" link below the login form. A reset email is sent to your personal email on file.
- Admins can also reset any staff member's password from **Users → Edit user → Reset Password**.

**Account lockout**
- After **10 failed attempts in 15 minutes** your account is temporarily locked for brute-force protection. Wait 15 minutes or have an admin unlock the account from **Users**.`,
  },
  {
    id: "gs-roles",
    category: "getting-started",
    title: "Roles & What You Can Do",
    audience: "all",
    body: `GLPFlo has four staff roles. Your role is set by Super Admin.

- **admin** — Full access. All tabs, all data, all settings.
- **prescriber** — Reviews and approves orders, manages assigned patients, signs eRx prescriptions. No admin/marketing/settings access.
- **customer_service** — Can view/assist patients, see Tracking Queue, send transactional emails. No approval rights.
- **it** — Error triage, system health, audit log review, email engineering. Read-only on most clinical data.

> If a tab is missing from your sidebar, you do not have the role/permission for it. Contact Super Admin if you believe your access is wrong.`,
  },
  {
    id: "gs-theme",
    category: "getting-started",
    title: "Light vs Dark Theme & Status Badge",
    audience: "all",
    body: `Staff dashboards default to **dark theme** (easier on eyes for long shifts). Toggle with the **sun/moon** button in the top-right header.

**Status Badge** (top-left of sidebar)
- Green dot = System Healthy.
- Yellow/orange pulse = Degraded (one or more subsystems failing).
- Click the badge → jumps you to the **System Health** tab.
- Click the **bell icon** next to the badge to opt in to **desktop notifications** when the system flips Healthy ↔ Degraded. Your browser will ask for permission on first click.`,
  },

  // -------- PRESCRIBER WORKFLOW (prescriber audience — visible to admin/IT/prescriber) --------
  {
    id: "rx-orders-overview",
    category: "prescriber",
    title: "Orders Tab — Reviewing Pending Requests",
    audience: "prescriber",
    body: `Your primary queue. Every order needing a clinical decision lands here.

**Layout**
- Each row shows: Patient name, Medication, Dose, Date submitted, Status, and a chevron to expand.
- Rows are **zebra-striped** and the whole row is clickable — it opens the patient profile modal.
- A purple **"PS"** badge means the order has passed automated **pre-screening** (no relevant allergies, no contraindicated medical history detected). The clinician is still expected to review every order before approving — the badge is informational only.

**Actions**
- Click the chevron to expand and see the patient's full questionnaire answers.
- Click **Approve** → generates the eRx, syncs to LabFlo, emails the patient, and moves the order to "approved → sent_to_pharmacy".
- Click **Deny** → prompts for a reason, emails the patient with your notes, and closes the order.

> **Pitfall:** Once you click Approve, the eRx is **immediately transmitted to LabFlo**. You cannot un-send it from here — contact LabFlo support to cancel a shipment in flight.`,
  },
  {
    id: "rx-ship-to-clinic",
    category: "prescriber",
    title: "Ship to Clinic vs Ship to Patient (eRx modal toggle)",
    audience: "prescriber",
    body: `On the eRx modal, you'll see a **Ship This Order To** toggle with two buttons:

- **Ship to Patient** *(default)* — medication ships to the patient's verified home address.
- **Ship to Clinic** — medication ships to one of your registered clinic addresses for **in-office administration**.

**Setting up your clinics** — switch to the **My Clinics** sidebar tab and add each clinic you practice at (name + address + phone). Mark one as *default* and it'll be pre-selected whenever you flip "Ship to Clinic" on.

**What changes downstream** — when you pick a clinic:
- The eRx PDF gets a yellow "**Dispense To**" banner with the clinic name, full address, and phone, plus a "for supervised in-office administration" note.
- The LabFlo ship-to fields are overridden with the clinic address.
- The patient name on the shipping label is tagged *"<Patient> c/o <Clinic name>"* so it's clear the parcel is for the practice, not the patient's home.

**When to use it** — peptide titration visits, supervised first-dose administration, or any scenario where you want the medication delivered to the office instead of the patient.

**You can't ship to a clinic that doesn't exist yet** — adding/editing/deleting clinics is self-service from My Clinics. There's no admin gating.`,
  },
  {
    id: "rx-dispense-templates",
    category: "prescriber",
    title: "Dispense Templates dropdown (auto-append syringe verbiage)",
    audience: "prescriber",
    body: `Above the **Rx Notes** field in the eRx modal you'll see a dropdown labeled **"+ Append dispense template"**.

**What it does** — pick one of the admin-curated templates and the canonical "dispense X syringes / Y alcohol pads / sharps container" verbiage is appended (not replaced) to the bottom of your Rx Notes. One click, no typos.

**Examples of templates** — "Standard insulin syringes (1mL)", "Tuberculin syringes (3mL)", "Compound MIC injections".

**Editing / managing** — only admins can add or change templates. If you need a new one, ping admin from the Live Chat tab. They live under **Settings → Medications → Dispense Templates** in the admin dashboard.`,
  },
  {
    id: "tier-review-queue",
    category: "prescriber",
    title: "Tier Review queue (patient changed plan, doses need a second look)",
    audience: "prescriber",
    body: `When a patient changes plans (e.g., Monthly → Royal), any queued rxs the prescriber had pre-generated for them get flagged \`tier_review_status=pending\` — because the new tier may need a different dose ladder.

**Where you see them** — new **Tier Review** sidebar tab on the prescriber dashboard. Amber badge shows the live count of pending decisions.

**Per-row decision modal** — tap **Decide** to open:
- **Keep doses as-is** — most common path when the patient is upgrading and the existing ladder is still appropriate. One click, done.
- **Rewrite dose** — type a new dose and the modal updates this rx's \`series_dose\`, \`requested_strength\`, and \`requested_strength_label\` in place. Use when the new tier needs an adjusted ladder.

**Why it matters** — without this, a patient who upgraded to Royal mid-titration might receive their next shipment at the wrong dose. The badge guarantees no patient hits their next refill before the prescriber has weighed in.

**SLA** — recommend reviewing within 24 hours of the flag appearing.  The badge persists until every flagged rx has been decided.`,
  },
  {
    id: "plan-upgrade-downgrade",
    category: "patient",
    title: "Plan upgrade / downgrade (Monthly ↔ Subscribe ↔ Royal)",
    audience: "patient",
    body: `Patients can change their plan at any time from the **Subscriptions** card in their portal. Tap "Change plan" → pick a tier → confirm.

**When the change takes effect** — the END of the current billing cycle. The patient finishes their paid cycle on the current tier; the new tier kicks in on the next renewal. No proration, no surprise charges, no refunds.

**Special rule: Royal → Monthly downgrade** — Royal pre-pays 2 months. Patient keeps full Royal benefits until the cycle ends, then drops to Monthly. No refund for the unused half.

**Pending rx series from old tier** — if the patient's prescriber had already queued multi-month rxs for them, those get flagged \`tier_review_status=pending\` and surfaced on the prescriber's queue. The prescriber decides per-patient whether to keep the old doses or rewrite at the new tier's ladder.

**Undo** — the patient can change their mind anytime before the cycle ends by tapping the "Undo & keep current plan" button on the pending-change banner.`,
  },
  {
    id: "patient-prior-prescription",
    category: "patient",
    title: "Uploading a Prior Prescription (skip the starter dose)",
    audience: "patient",
    body: `Already on a GLP-1 from another provider and want to keep your current strength?

**Where** — Documents tab → Upload Document → select document type **"Prior Prescription"**.

**What to upload** — a photo or PDF of your current script (the prescription label, the original rx, or a screenshot from your previous telehealth portal — any of these work).

**What happens next** — your prescriber will review the prior prescription as part of your next order. If verified, they may approve you straight at your maintenance dose instead of starting you on the safe titration starter.

**Don't have one handy?** — No problem. We default everyone to the safe starter dose; the prior-rx upload is an optional fast-track only.`,
  },
  {
    id: "patient-whats-included",
    category: "patient",
    title: "What's in your GLPFlo delivery (the box at your door)",
    audience: "patient",
    body: `Once your first payment goes through, a **"What's in your GLPFlo delivery"** card appears on your dashboard. It tells you exactly what's inside the cold-chain insulated box you'll receive — medication, syringes, alcohol pads, sharps pouch, dosing schedule, etc.

**Per-order overrides** — if your prescriber or our support team has tailored the contents for a specific order (e.g., extra syringes, an injection guide booklet), you'll see a purple "Customized for this order" badge on that order's detail screen.

**Why it matters** — no surprises when the package arrives at your door, and an at-a-glance way for our support team to confirm what's expected if anything looks missing.`,
  },
  {
    id: "plan-upgrade-downgrade-staff",
    category: "admin-ops",
    title: "Plan upgrade / downgrade (staff override)",
    audience: "admin",
    body: `Need to change a patient's plan on their behalf (e.g., they called in)?

**Where** — open the patient profile → Orders tab → scroll to the purple **"Subscription & plan tier · staff override"** card.

**How** — pick the patient's subscription from the dropdown, pick the target tier, click "Apply override." Audit-logged as \`plan_change_requested\` with \`requested_by_role=admin\`.

**Same rules as patient side**:
- Switch happens at the END of the current billing cycle.
- Royal → Monthly only at cycle end (no mid-cycle refund).
- Any queued future rxs from the old tier are flagged for prescriber review.

The patient sees the same pending-change banner in their portal and can cancel it themselves if they want.`,
  },
  {
    id: "manual-subscriptions-ops",
    category: "admin-ops",
    title: "Manual Subscriptions — the ops-driven billing tracker",
    audience: "admin",
    body: `While Intuit auto-recurring is unreliable, we track every recurring charge **manually** from **Admin → Pharmacy Partners → Manual Subscriptions**. Nothing auto-bills unless you set it up explicitly — the tab is your single-source dashboard for everything subscription-adjacent.

**The main table** — every tracked sub with patient, medication, cycle, amount, next charge date, last outcome. Overdue rows (next-charge < now) are highlighted red. The row is **clickable** — click anywhere on the row (or the little clock icon on the right) to open the full history drawer.

**Charge now** — fires an Intuit saved-card charge instantly, appends to \`charges[]\`, rolls the next charge forward by \`cycle_days\`. If the card is good, the patient gets a receipt email within seconds.

**Export all** (top right) — one-click CSV of every sub with aggregated counters (lifetime paid, total/paid/failed charges, last reminder, pending-scheduled count). Hand this to Finance every month for reconciliation.

**New subscription** — opens the create form. You need the patient's Mongo \`_id\` and the Intuit card-on-file ID (admin uploads the token when the patient saves a card at checkout).

**Pause / cancel** — pause stops Charge-now from firing. Cancel is soft (row stays for audit). Neither affects historical charges.

**Daily admin digest** — every morning at **ADMIN_DIGEST_HOUR : ADMIN_DIGEST_MINUTE** (default 7:30 America/New_York) you get an email listing every active sub due in the next 48h. Open the tab, run Charge-now on each row. Nothing in there is auto-fired by the digest.`,
  },
  {
    id: "manual-subs-charges-drawer",
    category: "admin-ops",
    title: "Charges History drawer — the per-sub timeline",
    audience: "admin",
    body: `Click any row in **Manual Subscriptions** to open the right-side drawer. It shows every event that ever happened on that sub as a vertical **timeline**.

**Filter chips** at the top:
- **All** — charges + reminders interleaved.
- **Paid** — only successful charges.
- **Failed** — only declined/error attempts.
- **Reminders** — only "please update your card" dispatches (promoted to top-level timeline nodes).

**Per-charge card** shows amount, processor, status chip, invoice, charge-ID, who fired it, "scheduled" badge if it was auto-fired by the scheduler (vs manual Charge-now), receipt-emailed flag, and any error.

**Action buttons** on each attempt:
- **Paid charges** → **Resend receipt** re-emails the receipt PDF to the patient on file. Toast shows the actual destination email. Also lives on **UserReceiptsModal** → one button per receipt row.
- **Failed charges** → **Send reminder** opens a dialog where you can toggle Email/SMS, write an optional 500-char custom message (empty = default), and dispatch. Each dispatch logs to \`notification_events[]\` and renders as a sub-node under the charge with per-channel delivery status.
- **24h cooldown** — once a reminder for a given charge delivers successfully on any channel, the button greys out for 24 hours. Server also returns **429 Reminder cooldown — try again in Xh Ym** if you try to call the API directly. Cooldown is per-\`charged_at\` ref — different charge attempts on the same sub aren't blocked.

**CSV button** in the drawer header downloads just this sub's \`charges[]\` with every column including \`trigger\` (manual / scheduled).`,
  },
  {
    id: "manual-subs-scheduler",
    category: "admin-ops",
    title: "Charge Retry Scheduler — one-shot auto-fire of Charge-now",
    audience: "admin",
    body: `Picking the right day to charge a patient (pay-day, deferred Royal, catch-up) is manual work. The **scheduler** removes that: pick a date/time, walk away, and the backend fires Charge-now at (or just after) your chosen time.

**How it works**
1. From the Manual Subscriptions table — click the little **calendar-clock icon** on an active row (shows a badge if there are already pending-scheduled entries), OR
2. From inside the Charges History drawer — click **Schedule charge** in the indigo "Scheduled charges" section.
3. Pick a local date/time in the picker (defaults to tomorrow 09:00). Optionally add a note ("Patient asked to defer to pay-day") — the note shows in the audit trail.
4. Click **Schedule charge**. The entry lands in \`scheduled_charges[]\` with status \`pending\`.

**The cron** sweeps every 5 minutes. For every \`pending\` entry whose \`run_at\` ≤ now:
1. Atomically flips the row to \`firing\` (idempotency guard — a slow tick can't double-charge).
2. Reloads the sub, runs it through the SAME \`_perform_charge\` helper as a manual Charge-now (so pricing / discounts / receipts all behave identically).
3. Stamps the outcome back on the entry (\`fired\` on success, \`failed\` on decline/error) with charge_id, invoice, status, error.
4. The resulting \`charges[]\` entry has \`trigger: "scheduled"\` so you can tell it apart in the timeline.

**Cancel a pending entry** — the X button on any \`pending\` row. Already-fired/failed/cancelled entries stay forever for the audit trail.

**Guardrails**
- Sub must be \`active\` with a \`card_on_file_id\` to schedule.
- Cancelled or paused subs auto-skip at fire time (the entry marks itself failed with the reason).
- \`run_at\` must be in the future (60-second clock-skew tolerance).

**When something fires** — the patient gets the normal receipt email on success. On decline/error, use the drawer's **Send reminder** button (now unblocked because the attempt is failed) to notify them.`,
  },
  {
    id: "prescriber-new-order-sms-super-admin",
    category: "admin-ops",
    title: "Prescriber new-order SMS alerts (super-admin only)",
    audience: "admin",
    body: `Prescribers can be opted-in to receive a text message the moment ANY new patient order lands in the queue. This is a **super-admin-gated** control — prescribers can't flip it on themselves anywhere in their dashboard.

**Where to toggle** — Admin → Teams → Prescribers → click **Edit** on any prescriber row → scroll to the indigo **"New-order alerts"** card. There are TWO independent channel toggles (SMS and Email), each with its own editable body and its own test button. Only a super-admin sees the controls; regular admins get read-only chips, prescribers see nothing in their own dashboard.

**Edit the message** — each channel has a textarea with the default body prefilled as placeholder. Leave it blank to use the default. The only placeholder is \`{greeting}\` which the backend substitutes with "Dr. Lastname" (or the next-best fallback). Email subject is fixed ("New GLPFlo order ready for review"); the "Open prescriber dashboard" button is appended automatically.

**Test buttons** — **Send a test text** fires one real SMS to the super-admin's OWN phone (via Twilio when the from-number is configured). **Send a test email** fires one real email to the super-admin's OWN inbox (via SendGrid). Use them to confirm each pipeline before enabling the toggle on a prescriber. Each test fire writes an audit entry (\`sms_test_sent\` / \`email_test_sent\`).

**Independent channels** — turn on SMS only, email only, or both. If the SMS toggle is on but the phone number is blank, SMS skips. If the email toggle is on but the email is blank, email skips. Email works TODAY (SendGrid is live); SMS waits on the Twilio from-number.

**Who gets fanned out to** — every active prescriber with the toggle ON. Sandbox orders are skipped (training mustn't ping the on-call list). If a prescriber doesn't have a phone number on their user record, they're silently skipped (log line only).

**Audit** — every toggle change writes an \`admin_update_user\` entry to the audit log with the old/new value, so compliance can trace who turned alerts on or off and when.

**Fix a bad number inline** — the same card now has an **Alert phone number** input (E.164 preferred, e.g. \`+15125551234\`). If the prescriber's phone is missing you'll see a red "missing" chip next to the label — super admin can paste the number right there, Save Changes, and the fan-out picks it up next time an order lands. If this field stays blank the prescriber is silently skipped on fan-out (log line only).

**Live preview** — right below the phone field is a mock SMS bubble showing EXACTLY what will go out ("Hi Dr. Patel, A new order has been submitted for review. Please login to verify!") — no guessing. Preview greys out when the toggle is off or the phone is blank.

**Send a test text** — the small button in the preview header fires ONE real SMS to **your own** super-admin phone (not the prescriber's). Use it before enabling the toggle to confirm Vonage + your carrier are delivering. If the test fails, check your own My Profile phone first, then the Vonage logs. Each test fire is written to the audit log as \`sms_test_sent\`.`,
  },
  {
    id: "patient-sms-triggers",
    category: "admin-ops",
    title: "Patient SMS triggers — what we send and when",
    audience: "admin",
    body: `GLPFlo texts the patient at every meaningful lifecycle step. All bodies are generic (never mention medication name / dose / diagnosis — the PHI guard refuses those keywords anyway) and all are gated by the patient's **Profile → Notifications** opt-in prefs plus the "transactional" tier default. Patients can STOP at any time.

**Active triggers**
- **Order placed** — fires the moment the patient hits Submit on \`/api/orders\`. Body: *"GLPFlo: order #XXXX received — we'll text again once your prescriber signs off. Track status in your Companion app."*
- **Rx approved** — fires when a prescriber clicks Approve. Body: *"GLPFlo: good news — your prescriber has approved order #XXXX. Open the app for next steps and tracking."* Email already fires via \`send_order_status_email\`; SMS is the complement.
- **Tracking shipped** — fires from the LifeFile webhook via \`send_sms_template("shipping_with_coa", ...)\`. Includes carrier + tracking number only.
- **Payment receipt** — fires right after a successful card charge via \`services/order_receipt.send_payment_receipt_sms\`. Body shows amount + receipt number + statement descriptor. Gated by \`sms_payment_receipt_opt_in\` on the user doc (default ON).
- **Payment failed** — admin-triggered via the Manual Subscriptions drawer **Send reminder** button (24h cooldown). Full custom-message support.
- **Cart abandonment nudge** — cron sweeps every 15 min. Patients who registered ≥ 30 min ago but have no paid order get an email + SMS "finish your order" nudge. If they still haven't ordered after 24 h, a second nudge fires. Each stage is recorded in \`cart_abandonment_nudges[]\` on the user doc so a reboot can't double-send.

**Where the plumbing lives** — \`services/sms_notifications.py\` (patient-facing + prescriber fanout) and \`services/order_receipt.py\` (receipts). All call \`services.sms.send_sms\` under the hood, which honours the patient's Notifications prefs + the global PHI guard.`,
  },
  {
    id: "rx-series-my-commission",
    category: "prescriber",
    title: "My Commission This Month (your personal widget)",
    audience: "prescriber",
    body: `Every time you log in you'll see an **emerald commission card at the top of your dashboard** showing your projected pay for the window (default 30 days).

**What the big number means** — it's the sum of \`series_generated × rate-per-tier\` from your own series only.  Each Monthly / Bundle Subscribe series you generate fires the Monthly rate.  Each Royal series fires the Royal rate.  You're paid once per series, regardless of how many shipments fall out of it.

**Live updates** — the card refetches every time you switch the 7/30/90-day window.  As you approve a new series, your number climbs immediately on the next page load.

**Strictly a preview** — payroll still runs through your existing comp process and ledger.  Use the card to track yourself in real-time and make sure you're hitting your targets, not as the source of truth for what you'll be paid.

**Disagreements?** The rate panel shows the live admin-set rates at the bottom of the card.  If they look wrong, ping super admin to adjust before the comp cycle closes.`,
  },
  {
    id: "rx-series-commission-preview",
    category: "admin-ops",
    title: "Prescriber Commission Preview",
    audience: "admin",
    body: `Pair the prescribing-efficiency widget with literal $-projection per prescriber per month.

**Where to set rates** — Admin → Loyalty & Coupons → "Prescriber Commission Rates" card.  Two inputs:
- Monthly / Bundle Subscribe series — defaults $50 per series (covers all 3 monthly rxs)
- Royal Tier series — defaults $75 per series (covers the 2-shipment royal pair)

Rates are flat-per-series, fire **once** when the prescriber generates the multi-month rx (independent of how many shipments later release).  Want a flat rate across both tiers?  Just enter the same number in both fields.

**Where you see the result** — the Overview → Rx Series widget now ranks prescribers by **projected comp** (emerald $ number on each row) instead of raw ship/pay.  System-wide "Projected comp pool" stat shows the total payroll obligation for the window.

**Audit** — every rate change writes \`commission_settings_updated\` to the audit log with the old and new values, so disputes are easy to reconcile.`,
  },
  {
    id: "rx-series-stale-digest",
    category: "admin-ops",
    title: "Stale Queued Rx Weekly Digest",
    audience: "admin",
    body: `If a patient writes a multi-month series but never comes back to pay, those queued orders sit in the pharmacy queue indefinitely.  Once they cross **90 days old** the system surfaces them in a weekly admin digest so we can either re-engage the patient or formally expire the rx before it hits the 365-day regulatory ceiling.

**Schedule** — every **Monday 09:00 UTC** automatically.  Recipient resolves in order: \`STALE_RX_DIGEST_TO\` → \`ADMIN_EMAIL_TO\` → \`ADMIN_EMAIL\` (so it works out-of-the-box on a fresh tenant).

**Admin dashboard widget** — *Admin → Loyalty & Marketing → Pricing* now includes a **"Stale Rx Digest"** card that shows:
- Live stale count (amber when > 0, healthy emerald when = 0)
- 90-day threshold + Mon 09:00 UTC schedule chips
- Oldest 5 rows preview (created date, patient, medication, series position, prescriber)
- **Refresh** button — re-pulls the list without page reload
- **Send digest now** button — bypasses the schedule and fires the email immediately. Disabled when count = 0.

**API surface** — \`GET /api/admin/rx-series/stale?days=90\` returns the raw rows. \`POST /api/admin/rx-series/digest/send-now\` force-sends; returns \`{sent:false, reason:'no stale rxs'}\` if nothing to send.`,
  },
  {
    id: "rx-series-efficiency-widget",
    category: "admin-ops",
    title: "Rx Series — Prescribing Efficiency widget",
    audience: "admin",
    body: `Lives at the top of the **Admin → Overview** tab.

**Why the metric is "shipments per pay event," not "future revenue"** — prescribers are paid ONCE per series.  Three monthly rxs = one rx-write ceremony = one pay event for the prescriber.  A Royal continuation = one pay event for two shipments.  So the meaningful productivity number for prescriber morale is:

\`\`\`
ship/pay = (parent order + queued + released) / series_generated
\`\`\`

A prescriber consistently sitting at **3.0×** is correctly using the auto-series feature on every monthly request.  Anything < 1.5× means they're still hand-writing single-month rxs and need re-onboarding to the new workflow.

The "Queued pipeline" dollar figure is for finance/operations only — it shows how much locked-in patient revenue is waiting on next-cycle payments.

**Approval-mix snapshot bar** (right under the top stat strip) — horizontal split bar showing the tenant-wide ratio of pre-screened vs standard-review series approvals in the selected window. Purple slice = pre-screened, blue = standard. Counts and percentages are right-aligned. Hidden when there are no series in the window.

**30-day PS adoption sparkline** (directly below the snapshot bar) — daily mini-chart of approval activity for the last 30 days. Each column = one calendar day:
- **Height** = total approvals that day (max-normalized so the busiest day fills the chart).
- **Color split inside the column** = pre-screened share (purple) vs standard-review share (blue).
- Days with 0 approvals render as a thin baseline (preserves the spacing so you can read the date axis honestly).
- Hover any column to see \`YYYY-MM-DD · X PS / Y Std · pct%\`.
- Right-aligned "**Window avg X%**" pill is the rolling 30-day average pre-screened share.

**Calibration drift alert** (auto-banner under the sparkline) — fires only when the most-recent 7-day PS% differs from the prior 7-day PS% by **≥ 15 percentage points** AND both windows have **≥ 5 series each**. Color-coded amber for drops, emerald for rises. Includes a context paragraph on likely causes (cohort onboarding, screening-rule tweak, patient-mix shift). The alert also emails admin once per direction per week — see "PS Drift Email Alert" module.

**Per-prescriber leaderboard** (bottom) — the top 5 prescribers by series count. Each row now also shows that prescriber's individual **PS / Std mix mini-bar** (\`8 PS / 2 Std · 80%\` plus a thin purple+blue split bar). The percentage color flips between blue and purple at 50% for instant reading. Surfaces individual workflow patterns — one prescriber rejecting most PS-eligible orders (over-cautious), another rubber-stamping them (under-reviewing). Hidden on mobile to keep rows scannable.`,
  },
  {
    id: "patient-tour-stats",
    category: "admin-ops",
    title: "Patient Welcome Tour Stats",
    audience: "admin",
    body: `Top of *Admin → Overview*. Tracks the 4-step first-visit welcome tour shown to every newly-registered patient.

**Why two rates?**
- **Completion rate** = \`completed / (completed + skipped)\` — share of patients who finished the tour vs bailed out. The most useful "is the tour pulling its weight?" number.
- **Engagement rate** = \`(completed + replayed) / started\` — broader engagement including users who came back for a second look. A high engagement rate with a low completion rate means patients are interested but maybe the copy of one step is confusing.

**Step funnel** — the 4 bars under the rates show how many patients got past each step (Account #, Statuses, Receipts, Subs). Look for the biggest drop-off cliff — that's the step where the explanation isn't landing. If patients consistently skip after Step 2 (Statuses), that's a signal to rewrite Step 2.

**Data plumbing**
- Tracked server-side via \`patient_tour_events\` collection on \`/api/patient/tour-event\`. Independent of PostHog consent so we capture data even from patients who reject analytics cookies.
- Same events also fired to PostHog via \`analytics.capture\` for cross-funnel correlation with downstream conversions.
- **No PHI stored** — only the event type, step index, timestamp, and tour version.

**Window** — last 30 days. Card auto-hides on Overview when there are zero events (clean look for fresh installs).`,
  },
  {
    id: "rx-commission-rates",
    category: "admin-ops",
    title: "Prescriber Commission Rates (Tenant Defaults)",
    audience: "admin",
    body: `*Admin → Loyalty & Marketing → Pricing → Prescriber Commission Rates*

Tenant-wide pay rates per Rx series. Prescribers are paid **once per series**, not per shipment.

**Three rates**
- **Monthly / Bundle Subscribe series** — default $50 per 3-rx series.
- **Royal Tier series** — default $75 per 2-rx series.
- **Pre-Screened approval** — default $50. Replaces the Monthly/Royal rate when the FIRST order in the series came through the Pre-Screened queue (automated screening raised no flags). Same future-month series auto-generation as Monthly/Royal — only the per-series pay rate differs. Set lower than Monthly if you want to reward thorough manual review; set higher if you want to incentivize clearing pre-screened first.

**How to change** — Edit the value in any of the three inputs and click **Save Rates**. Change takes effect immediately for **new** series. Already-generated series keep the rate that was effective when they were generated (preserved in audit log).

**Audit** — Every rate change writes a \`commission_settings_updated\` audit row with the new values, the admin who changed them, and IP.

**API surface** — \`GET /api/admin/commission-settings\` reads the current rates. \`PUT\` writes them.`,
  },
  {
    id: "rx-commission-overrides",
    category: "admin-ops",
    title: "Per-Prescriber Commission Rate Overrides",
    audience: "admin",
    body: `Directly below the tenant-rates card on the same Pricing page. Override any of the three rates for an individual prescriber — useful for senior physicians on negotiated rates, new NPs on a discounted ramp, or contractor differentials.

**Read the table**
- One row per prescriber. Each rate cell shows the **effective** value (override if set, else tenant default).
- A bold **indigo** cell + bold rate value = custom override. Gray cell + regular weight = inheriting tenant default.
- A small **"Custom"** indigo badge next to the prescriber name = at least one field is overridden.

**Edit a row**
1. Click **Edit** on the prescriber's row.
2. Each input shows the current override value (or blank if inheriting). Type a new value or leave blank to keep that single field inheriting.
3. Click **Save**.

**Revert a row** — Click **Revert** (only shown when the row has any override). Removes the override row entirely; prescriber inherits all three tenant defaults again.

**Important — when overrides apply**
- Overrides apply on the prescriber's **NEXT series**. Already-generated series keep the rate that was effective at series-generation time.
- This is intentional — it prevents retroactive comp disputes.

**Audit** — Every save writes \`commission_override_updated\`. Every revert writes \`commission_override_removed\`. Both include the prescriber, the new/old values, the admin, and IP.

**API surface** — \`GET /api/admin/commission-overrides\` returns all prescribers with effective rates + per-field override flags. \`PUT /api/admin/commission-overrides/{pid}\` upserts (pass null to clear a single field). \`DELETE\` removes the entire override row.`,
  },
  {
    id: "rx-ps-drift-email",
    category: "admin-ops",
    title: "PS Drift Email Alert (Weekly)",
    audience: "admin",
    body: `Companion to the dashboard drift banner — emails admin when the system-wide pre-screened % drifts meaningfully week-over-week, even if no one is watching the Overview tab.

**Schedule** — every **Monday 08:00 UTC** (1 hour before the stale Rx digest, so both arrive together in the inbox). Job id: \`weekly_ps_drift_check\`.

**When it fires** — only when ALL of the following are true:
- 14+ days of approval data exist.
- The most-recent 7-day PS% differs from the prior 7-day PS% by **≥ 15 percentage points**.
- Both 7-day windows contain **≥ 5 series each** (low-volume signals are filtered out).

**Debounce** — at most one email per direction (drop / rise) per 7 days, tracked in the \`commission_drift_alerts\` collection. Prevents a flapping signal from spamming.

**Email contents** — color-coded HTML email (amber for drops, emerald for rises) with a summary headline matching the dashboard banner copy exactly, a follow-up context paragraph, and a numeric breakdown table (Recent 7d vs Prior 7d: PS count, Total approvals, PS %).

**Recipient resolution** — \`STALE_RX_DIGEST_TO\` → \`ADMIN_EMAIL_TO\` → \`ADMIN_EMAIL\`.

**Manual fire** — \`POST /api/admin/rx-series/drift-check/run-now\` (admin only). Bypasses debounce, computes drift, sends email if actionable, logs with \`manual:true\`. Useful for previewing the email template post-deploy. Returns \`{sent:false, reason:"no actionable drift"}\` if below threshold.`,
  },
  {
    id: "rx-series-auto-titration",
    category: "prescriber",
    title: "Auto-Generated Rx Series (3-Month Titration & Royal Continuation)",
    audience: "prescriber",
    body: `When you approve a patient's FIRST order in any paid tier — **Monthly**, **Bundle Subscribe**, or **Royal** — a follow-up modal opens automatically and lets you queue the patient's **upcoming months** in one step. Designed to cut your prescribing time and keep the patient covered even if they don't auto-subscribe yet.

**What gets generated**
- **Monthly / Bundle Subscribe** → 2 additional 28-day rxs (months 2 & 3) with a default titration ladder pre-filled from the medication's strengths list. You can edit any of the 3 doses before saving.
- **Royal** → 1 additional shipment (months 3–4) at whatever Royal-strength ladder you confirm.

**What patients see** — nothing. Queued rxs are flagged \`is_queued_future\` and are **hidden from the patient portal** until they're released.

**When queued rxs auto-release to the pharmacy**
- Stripe **invoice.paid** (subscription auto-renewal) → next queued rx flips to \`pending_review\`.
- Patient places a manual reorder for the same medication → same release.
- If the patient cancels their subscription and just orders ad-hoc later, the queued rx is still sitting there waiting — it auto-releases on the next paid order.

**Release timing setting (super admin only)**
- Admin → Settings → **Rx Series** sub-tab → **Rx Series Auto-Release Window** card.
- Default 21 days. Configurable 1–365.
- This is the informational window staff sees on each queued rx ("auto-releases ~2026-06-05"). The actual trigger is the payment event, not a date — the date is just the expected next-bill date.

**Where staff see queued rxs**
- **Admin → Orders tab → Status filter → "Queued · Future Release"** shows everyone's queued rxs.
- **Patient profile → Orders tab → "Projected coming months" amber callout** shows that patient's queued rxs.

**To skip generating a series** click "Skip — don't generate series" in the modal. You can always come back and write a fresh single rx for the next month manually.`,
  },
  {
    id: "rx-auto-approve",
    category: "prescriber",
    title: "Pre-Screened (PS) Badge — What It Means",
    audience: "prescriber",
    body: `An order is classified **"pre-screened"** if the patient's record has:
- **No allergy concerns** — allergies field is empty, "NKDA", or "N/A".
- **No red-flag medical history** — no keywords like THYROID CANCER, MEN2, PANCREATITIS, GASTROPARESIS.
- **No automated drug-drug interaction flags** against the requested medication.

Otherwise it is classified **"standard review"** and requires careful manual review of the flagged item(s).

> **Important — clinician judgment is required either way.** The PS badge is **informational, never an approval**. You must still review every order and click Approve/Deny manually. The badge simply tells you "automated screening did not raise a flag on this record."

**Where you see it**
- **Sidebar slot #2** — a dedicated **"Pre-Screened"** tab sits directly below "Order Queue", giving you a focused subset of the main queue.
- **"PS today: X of Y (pct%)"** purple chip on the Order Queue header — at-a-glance read of how many of today's pending orders are pre-screened. Use it to set review cadence (clear PS bucket first, then spend more time on flagged items).
- **"PS" badge** on the right side of any pre-screened order row.
- **"Show pre-screened orders"** checkbox toggles them in or out of your main queue if you want a flagged-only view.

**Commission impact** — Pre-screened approvals pay using the **Pre-Screened rate** (admin-tunable, separate from Monthly / Royal rates). See the "Commission rates" module under My Profile → My Commission.`,
  },
  {
    id: "rx-ddi-engine",
    category: "prescriber",
    title: "GLP-1 Drug-Drug Interaction (DDI) Engine & HIGH-Severity Acknowledgment",
    audience: "prescriber",
    body: `GLPFlo runs a built-in **GLP-1-specific DDI rules engine** against the patient's self-reported **Current Medications** before you can approve their order. Designed as a safety net layered on top of your clinical judgment, NOT a replacement.

**Severities**
- **HIGH** — flagged interactions that materially raise risk (e.g. **Insulin** + GLP-1 → severe hypoglycemia risk; **Warfarin** + GLP-1 → INR monitoring required).
- **MODERATE** — interactions worth noting (e.g. **Oral contraceptives** + GLP-1 → delayed absorption due to gastric emptying).
- **LOW** — informational (timing tweaks, monitoring suggestions).

**Where you see it** — when you open any order for review, a colored **DDI Warning Banner** appears at the top of the modal if any rule fires. Each row lists the interacting medication, severity chip, plain-English description, and clinical recommendation.

**HIGH-severity acknowledgment gate**
> You **cannot approve** an order with one or more **HIGH-severity** flags until you tick the acknowledgment checkbox(es) inside the DDI banner. The Approve button stays disabled.
- The acknowledgment is per-interaction (one checkbox per HIGH-severity row), so you can't accidentally ack a whole list with one click.
- Acked keys are saved on the order as \`ddi_acknowledged_keys\` and audit-logged. They appear on the eRx audit trail next to your sign-off.

**MODERATE and LOW severity** — informational; do not block approval. You still must read them.

**False positive?** — If a rule fires that you believe is clinically inappropriate, approve the order after acking, then notify IT/Compliance so the rule set in \`/app/backend/services/ddi_glp1.py\` can be refined.`,
  },


  {
    id: "rx-my-commission",
    category: "prescriber",
    title: "My Commission Widget & Pay Rates",
    audience: "prescriber",
    body: `Top-right of the **Order Queue** tab shows a live **"Projected paycheck"** card that summarises what you've earned in the current window.

**How prescriber pay works** — you are paid **once per Rx series**, not per shipment. A 3-month Monthly series = one rx-write ceremony = one pay event. A 2-shipment Royal continuation = one pay event for two shipments.

**Three rate buckets** (set by admin in Loyalty & Marketing → Pricing → Commission Rates)
- **Monthly** — paid for a series whose first order is a standard-review approval at the Monthly / Monthly-Subscribe tier.
- **Royal** — paid for a series whose first order is a standard-review approval at the Royal 90-day tier.
- **Pre-Screened** — replaces the Monthly/Royal rate when the first order in the series came through the Pre-Screened queue (automated screening raised no flags). Same series auto-generation as Monthly/Royal — only the per-series rate differs.

**Where you see your rates**
- The widget's rate-breakdown line shows all three: \`From 8 series · Monthly $50.00 · Royal $75.00 · Pre-Screened $50.00\`.
- The five mini-stat tiles below: **Series**, **Pre-Scr.** (purple, count of pre-screened series), **Queued**, **Released**, **Ship/pay**.

**Per-prescriber overrides** — Admin can override any of the three rates for an individual prescriber (e.g. a senior physician on a negotiated Royal rate). If your rates differ from the tenant defaults you'll see your custom values reflected in the widget automatically. Already-generated series keep the rate that was effective when they were generated — overrides only apply to NEW series.`,
  },

  {
    id: "rx-erx-pdf",
    category: "prescriber",
    title: "Viewing & Downloading the eRx PDF",
    audience: "prescriber",
    body: `Every approved order generates a printable prescription document (eRx).

**To view**
1. On any approved order row, click **View eRx PDF**.
2. A new tab opens with the formatted prescription (Prescriber → Patient → Allergies → Medications/Diagnosis → Orders).
3. Use your browser's **Print** to save as PDF or send to a physical printer.

**What's on the eRx**
- Full prescriber info (name, type, NPI, DEA, phone, address).
- Full patient demographics (name, DOB, gender, address, allergies, medical history, current meds, conditions).
- Medication name, dose, quantity, route/form, refills, directions, diagnosis, rx notes.
- Prescribed date, expiration date, rx origin (always "Electronic").
- LabFlo sync status (synced / pending / failed).`,
  },
  {
    id: "rx-patient-search",
    category: "prescriber",
    title: "Patient Records — Search & Profile Edit",
    audience: "prescriber",
    body: `The **Patient Records** tab is your cross-reference view of every patient you've ever written an eRx for.

**Searching**
- Type in the **Patient / DOB** search box at the top — filters live by name, email, or date of birth.
- Click any row (anywhere on the row, not just the name) to open the patient profile modal.

**Patient profile modal**
- Shows demographics, allergies, medical history, current meds, conditions.
- By default **fields are read-only** for safety.
- To edit: tick the **"Unlock to edit"** checkbox at the top.
- Make changes → a **Save Changes** button appears → click it to persist.
- Uncheck "Unlock to edit" to re-lock without losing unsaved edits.

> **Pitfall:** Unlocking to edit is audit-logged. Every field change creates an audit trail visible to admins in **Audit Logs**.`,
  },
  {
    id: "rx-tracking",
    category: "prescriber",
    title: "Tracking Shipments for Your Patients",
    audience: "prescriber",
    body: `When LabFlo ships an order, the tracking number flows back into GLPFlo automatically via webhook (see LabFlo Integration section).

**To track a patient's shipment**
1. Go to **Orders** → find the approved order.
2. If shipped, the row shows carrier (UPS/USPS/FedEx/DHL) + tracking number + **Track** button.
3. Click **Track** → opens the carrier's live tracking page in a new tab.

The patient also receives an **automatic shipping email** with the tracking link the moment LabFlo marks the order shipped.

**If the Track button is missing**
- The order hasn't been shipped yet (LabFlo is still processing).
- Check **System Health** (or ask IT) if it's been >48h since approval — may indicate LabFlo webhook delivery failure.`,
  },
  {
    id: "rx-erx-history",
    category: "prescriber",
    title: "ERX History Tab",
    audience: "prescriber",
    body: `Read-only historical record of every eRx you've signed.

**Use it to**
- Audit your own activity (monthly rx volume, medication mix).
- Search by patient to find prior prescriptions when deciding on a refill.
- View LabFlo sync status + tracking for past orders.

Filters: date range, medication, patient.`,
  },
  {
    id: "rx-documents",
    category: "prescriber",
    title: "Documents Tab — Patient File Uploads",
    audience: "prescriber",
    body: `Patients occasionally upload supporting documents (lab results, ID photos, insurance cards).

**How to review**
1. Go to **Documents** tab.
2. Filter by patient name.
3. Click the document icon to open/preview; click Download to save a local copy.
4. Files are served via signed URLs and are HIPAA-encrypted at rest.

> **Pitfall:** Do NOT forward patient documents externally via email. If you need to share with LabFlo, use the integrated pharmacy tenant channel — do not attach to a regular email.`,
  },
  {
    id: "rx-chat",
    category: "prescriber",
    title: "Staff Chat",
    audience: "prescriber",
    body: `Internal staff-only chat (not patient-facing).

- All staff (admin + prescriber + IT + CS) can see and post.
- Use it for quick coordination ("patient GF-xxx called, please review order ASAP").
- **Do not** post PHI in free-text — reference by order ID or patient ID instead of full name where possible.
- Messages are audit-logged and retained for 2 years per HIPAA policy.`,
  },
  {
    id: "rx-profile",
    category: "prescriber",
    title: "Your Profile — NPI, DEA, Signature",
    audience: "prescriber",
    body: `The **My Profile** tab is where you maintain the credentials that appear on every eRx you sign.

**Required fields**
- Full Name (as it should appear on prescriptions)
- NPI number (10 digits)
- Practice phone + practice address (street, city, state, ZIP)
- Prescriber type (physician / nurse_practitioner / physician_assistant)
- For NP/PA: **supervising physician name** (mandatory per many state rules)

> **Pitfall:** If NPI is missing/invalid, the eRx will still generate BUT LabFlo may reject it. Keep this field current.`,
  },
  {
    id: "rx-triage-mode",
    category: "prescriber",
    title: "Triage Mode — focus the queue on what actually needs you",
    audience: "prescriber",
    body: `Triage Mode is a one-click toggle (top of the Orders tab, **testid: \`triage-mode-toggle\`**) that collapses your full pending queue down to **only the rows that match at least one "attention" filter**, then sorts those rows by **your personal priority order**.

**Attention filters (canonical keys)**

- \`sla_reassigned\` — order was reassigned to you by the 36-hour SLA sweep (was idle on another prescriber).
- \`halfway_warned\` — you've held the claim for ~18 h and a halfway warning has fired. Act now or it will be reassigned at 36 h.
- \`above_starter\` — patient is requesting a dose **above** their starter strength. Clinical judgement required.
- \`patient_note\` — patient attached a note that hasn't been acknowledged yet.
- \`pending_review\` — standard pending bucket (everything else).

**Customizing your priority order**

Click the small **"Edit order"** link next to the Triage toggle (testid: \`triage-edit-toggle\`) to drag/up-down the five filter keys. The order you set is saved to your profile (\`PUT /api/prescriber/triage-preferences\`) and used to break ties — a row matching two filters is sorted by whichever appears higher in your list.

> **Pro tip:** Most prescribers put \`sla_reassigned\` and \`halfway_warned\` at the top — those are the rows that will get reassigned to someone else if you don't act. \`above_starter\` is usually next (clinical judgement), then \`patient_note\`, then \`pending_review\` last.

The toggle and your filter order both persist (\`localStorage.glpflo_triage_mode\` + server-stored \`filter_order\`), so refreshing the dashboard keeps you in Triage Mode if you were in it.`,
  },
  {
    id: "rx-sla-reassignment",
    category: "prescriber",
    title: "36-hour SLA auto-reassignment + 18-hour halfway warning",
    audience: "prescriber",
    body: `Every claimed order has a soft 36-hour SLA. If you hold a claim that long without writing the eRx, the order is **automatically unclaimed** and bounced back to the open queue so another prescriber can pick it up.

**18-hour halfway warning**

At ~18 hours of idle time the backend (APScheduler job \`assignment_sla_sweep\`) writes \`sla_warning_sent_at\` onto the order and surfaces an in-app warning chip on the row. The order also matches the \`halfway_warned\` triage filter so it floats to the top of your Triage Mode view.

**36-hour reassignment**

At 36 hours the sweep:
1. Releases your claim (\`assigned_to\` cleared, \`assignment_source\` set to \`sla_reassigned\`).
2. Posts a system note to the order timeline.
3. Returns the row to the open queue. The next prescriber who claims it sees the \`sla_reassigned\` triage badge so they know it's been waiting.

**What you should do if you see the warning**

- Write the eRx **now** if the chart is ready, or
- **Unclaim** the order yourself (Esc inside the order modal, or the Unclaim button) so a colleague can grab it without waiting for the 36-h cutoff.

> Admins can tune the SLA window per tenant in **Admin → Assignment SLA** (testid: \`assignment-sla-card\`). Defaults: warn at 18 h, reassign at 36 h.`,
  },
  {
    id: "rx-live-lock",
    category: "prescriber",
    title: "Live-Lock Pill — see who's actively viewing an order",
    audience: "prescriber",
    body: `When you open an order detail modal a **soft lock** is registered against that order. Other prescribers viewing the queue will see a **Live-Lock pill** (testid: \`live-lock-pill-<orderId>\`) on the row showing your name + a "looking now" pulse.

**How locks work**

- Lock acquired on \`POST /api/orders/<id>/lock\` when you open the detail modal.
- A heartbeat fires every ~20 s to keep the lock fresh.
- Lock released automatically when you close the modal (\`POST /api/orders/<id>/lock/release\`), or hit \`Esc\`, or the heartbeat stops for 60 s.

**Important: locks are advisory, not hard locks.**

Two prescribers can still both write an eRx on the same order — the server uses a final atomic check at approval time and the second writer will get a 409 conflict. The pill is there to **prevent the conflict** by signalling "someone else is on this", not to block you. If you genuinely need to take over (the other prescriber stepped away), open the detail anyway — your lock will replace theirs.

> Admins see the same pill on the Admin Orders queue and can force-release a stuck lock via the order detail action menu.`,
  },
  {
    id: "rx-keyboard-shortcuts",
    category: "prescriber",
    title: "Keyboard shortcuts — power-user queue navigation",
    audience: "prescriber",
    body: `The prescriber Orders tab has a full keyboard layer. Press **\`?\`** anywhere on the page (when you're not typing in an input) to bring up the cheat-sheet overlay. There's also a floating **Shortcuts** pill at the bottom-right (testid: \`keyboard-shortcut-help-btn\`).

**Available shortcuts**

| Key | Action |
| --- | --- |
| \`?\` | Toggle the shortcuts overlay |
| \`T\` | Toggle Triage Mode on/off |
| \`J\` / \`↓\` | Focus the next row in the queue |
| \`K\` / \`↑\` | Focus the previous row |
| \`Enter\` | Open the focused row's "View Details" modal |
| \`A\` | Open the **Write ERX** action modal for the focused row (approve flow) |
| \`D\` | Open the Write ERX modal **and auto-jump to the Deny sub-panel** |
| \`Esc\` | Close the open modal · release the soft lock |

**Behavior notes**

- Shortcuts are **disabled** while you're typing in any \`<input>\`, \`<textarea>\`, \`<select>\`, or contenteditable field — so typing notes won't trigger A/D.
- The focused row gets an **amber inset ring** (testid attribute \`data-focused="1"\`). It also auto-scrolls into view so J/K work across long queues.
- Action keys (A / D / Enter) require a focused row first. Use J/K to move into the queue.
- The shortcut layer is active only on the Orders tab — switching tabs disables it.

> **Pitfall:** Browsers may already use some single-key shortcuts in dev tools or extensions. If A/D don't fire, click an empty area of the dashboard (not an input) to make sure focus is on the page, then try again.`,
  },
  {
    id: "rx-comp-v3",
    category: "prescriber",
    title: "Compensation Engine v3 — pay-per-order, supervisor splits, tier permissions",
    audience: "prescriber",
    body: `Commission is calculated **per approved order**, not per month. The amount depends on three things: the medication's tier, your role/permission set, and (for supervised non-MD prescribers) any supervisor split applied to the order.

**Pay-per-order amounts**

Set by admin in **Tenant Defaults** (\`rx-commission-rates\`) and overridable per-prescriber (\`rx-commission-overrides\`). When you click an order's "Write ERX" button the server snapshots your effective rate at that moment so retroactive rate changes don't affect already-approved orders.

**Tier permissions (who can claim what)**

Non-MD prescribers (NPs, PAs, ND) carry an \`allowed_tiers\` list on their user record (set in **Admin → Prescriber Permissions**, testid: \`prescriber-permissions-card\`). Claim attempts are server-enforced:

- If you don't have the tier in \`allowed_tiers\`, the **Claim** button is hidden and the server returns 403 if you try via API.
- MDs / DOs / admins have implicit "all tiers" access — the field is ignored for them.

**Supervisor splits (NP/PA only)**

If you have a \`supervisor_id\` + \`supervisor_split_pct\` set on your record, every approved order auto-creates a paired commission record for your supervisor at the configured percentage (typically 10–20 %). Both rows appear under that month's payroll preview — yours and theirs.

**Refill routing**

Refill orders are routed back to the **previous prescriber on file** whenever possible. If that prescriber is inactive / out, the refill falls through to the open queue and the next claimer collects the commission. See the order timeline for the routing decision.

> **My Commission widget** on your sidebar tallies the current month's earned-to-date in real time — refresh after each approved order to see it update.`,
  },

  // -------- LABFLO (all) --------
  {
    id: "lf-overview",
    category: "labflo",
    title: "How LabFlo Integration Works (End-to-End)",
    audience: "all",
    body: `GLPFlo sends orders out and receives shipping updates back. Two-way integration.

**Outbound** (GLPFlo → LabFlo)
- Prescriber approves an order.
- GLPFlo POSTs to \`https://labflo.org/api/external/orders\` with the full eRx payload and X-API-Key.
- LabFlo responds with its own \`order_id\` + \`rx_number\`, stored on the GLPFlo eRx record.
- The order is now in LabFlo's fulfillment queue.

**Inbound** (LabFlo → GLPFlo)
- LabFlo dispatches the shipment.
- LabFlo POSTs to \`/api/webhooks/labflo\` with event \`order.shipped\` (plus tracking number, carrier, label URL).
- GLPFlo verifies the **HMAC-SHA256 signature** in \`X-LabFlo-Signature\` header.
- On match, GLPFlo updates the eRx + order (status=shipped, tracking_number, carrier, tracking_url) and sends the patient a shipping email.
- When delivered, LabFlo posts \`order.delivered\` — status flips to delivered, delivered_at timestamp set.

Every inbound webhook is logged to \`db.labflo_webhooks\` for audit + manual replay.`,
  },
  {
    id: "lf-tracking-queue",
    category: "labflo",
    title: "Tracking Queue (Admin/Prescriber/CS)",
    audience: "all",
    body: `Single-pane view of every shipped order.

**Find it:** Admin sidebar → **Tracking Queue**. Prescribers/CS see the same tab.

**Controls**
- Search box: searches patient name, email, medication, tracking number, ERX ID.
- Status filter chips: **All / Shipped / Delivered / In Progress**.
- KPI cards at top: shipped, delivered, in progress, other counts.
- Each row has a carrier-specific **Track** link.

**When to use it**
- Patient calls asking "where's my order?" → search by email → click Track.
- Daily ops check: how many shipments pending delivery >5 days? Filter by Shipped, sort by shipped_at.`,
  },
  {
    id: "lf-webhook-replay",
    category: "labflo",
    title: "LabFlo Webhook Replay (Admin/IT)",
    audience: "admin",
    body: `If a webhook fires but our processing breaks (DB hiccup, app restart mid-flight), the payload is still persisted. You can re-process it without asking LabFlo to resend.

**Steps**
1. Admin sidebar → **LabFlo Webhooks**.
2. Find the affected webhook (search by ERX ID or tracking number).
3. Click **Replay** → re-runs \`_process_labflo_payload\` against the saved payload.
4. The row shows updated replay_count and last_replay_result.

> **Important:** Replays **do NOT re-send** the patient shipping email — only live webhooks do. This prevents patient spam during ops recovery.

**Common causes of needing replay**
- Backend was restarting when webhook arrived (very rare — LabFlo retries automatically 3x).
- ERX ID mismatch (e.g., ERX record was deleted post-send — fix the record, then replay).
- Webhook signature mismatched due to rotated secret (fix secret, replay).`,
  },
  {
    id: "lf-pharmacy-tenants",
    category: "labflo",
    title: "Pharmacy Dropdown — Live from LabFlo",
    audience: "all",
    body: `The "Send to pharmacy" dropdown in the prescriber order action panel is now **live from LabFlo** — no more hard-coded list.

**How it works**
- Prescriber dashboard calls \`GET /api/pharmacy-tenants\` on mount.
- Backend proxies \`GET https://labflo.org/api/external/pharmacies\` with our \`X-API-Key\`.
- Result is **cached in-process for 5 minutes** (honours LabFlo's \`Cache-Control: max-age=300\` header) and conditional GETs use \`ETag\`/\`If-None-Match\` for cheap refreshes.
- Successful pulls are mirrored to MongoDB \`db.pharmacy_tenants\` so historical orders/eRx still resolve their pharmacy name even if LabFlo is briefly unreachable.

**Today the dropdown shows: Polomar Specialty Pharmacy** (the only authorized pharmacy on our LabFlo tenant). When LabFlo onboards more partners, they'll appear automatically — no code change needed.

**Diagnostic endpoint** (admin/prescriber only)
- \`GET /api/pharmacy-tenants/status\` returns \`{source: "cache"|"labflo"|"stale_cache"|"db_fallback", age_seconds, etag, count}\`.
- Use this to confirm we're hitting LabFlo live vs serving stale cache during an incident.

**Force a refresh**
- Prescribers/admins: hit \`GET /api/pharmacy-tenants?refresh=true\` — bypasses the 5-min cache and fetches LabFlo immediately.

**Trust badge auto-population**
- The "Compounded by Polomar Specialty Pharmacy" badge on receipt PDFs, shipping emails, patient dashboards, and the public landing hero **all use this same live data**. One source of truth.

> Legacy mock seeds (\`pharm-001/002/003\`) are auto-flagged \`is_legacy: true / inactive\` on backend startup so they no longer appear in the dropdown but historical orders that referenced them still resolve their pharmacy name correctly.`,
  },

  // -------- ADMIN OPS (admin) --------
  {
    id: "ad-users",
    category: "admin-ops",
    title: "Users Tab — Create, Edit, Deactivate Staff & Patients",
    audience: "admin",
    body: `Single place to manage every human account.

**Create a new staff member**
1. Click **+ New Employee**.
2. Fill: Name, Role (prescriber / admin / it / customer_service), Prescriber Type (if prescriber), Personal Email (for password reset), Password.
3. For NPs/PAs, also enter Supervising Physician Name.
4. Click Create → system assigns a **GF###### user ID** automatically.
5. Share the user ID + password with the new hire via a secure channel (never email the password).

**Reset a user's password**
- Click the user row → **Edit** → **Reset Password** → set new password → Save. User must change on next login (optional flag).

**Deactivate a user**
- Toggle the **Active** switch off. They can no longer log in. Existing eRx records / orders are preserved.

**Filter**
- Role filter dropdown (patient / admin / prescriber / it / customer_service) narrows the list.`,
  },
  {
    id: "ad-orders",
    category: "admin-ops",
    title: "Orders Tab (Admin View)",
    audience: "admin",
    body: `Global view of **every** order across all patients and prescribers. Unlike the Prescriber Orders tab, you cannot approve/deny here — this is an oversight view.

**Key columns**
- Order ID, Patient, Prescriber, Medication, Status, Approval Method (auto/standard), Payment Status, Created.

**Filters**
- Status: pending_review / approved / denied / shipped / delivered / cancelled.
- Payment Status: paid / unpaid / refunded.
- Date range.

**What you can do**
- Click an order → expand to see full detail.
- Export CSV for revenue reconciliation.
- Flag for review (admin note) — shows up in audit logs.`,
  },
  {
    id: "ad-medications",
    category: "admin-ops",
    title: "Medications Management",
    audience: "admin",
    body: `Defines the menu of products patients see at checkout.

**Add a medication**
1. **+ New Medication**.
2. Fill: Name, Category (GLP-1 / Weight Loss / etc.), Description, Price, Subscription frequency (weekly/monthly), Active flag, Image URL, Dose presets.
3. Save.

**Dose presets** appear on the patient checkout dropdown. Use them for standard strengths (e.g., Semaglutide 0.25mg / 0.5mg / 1mg).

**Deactivate** (don't delete) meds when you discontinue — this preserves historical orders. Set **Active = false** and the med disappears from the patient-facing catalog.`,
  },
  {
    id: "ad-rx-templates",
    category: "admin-ops",
    title: "Rx Templates",
    audience: "admin",
    body: `Prescribers use these as pre-filled starting points when writing eRx.

**Structure of a template**
- Name (e.g., "Semaglutide Standard Starter")
- Default dose, quantity, unit, refills, directions, diagnosis
- Medication(s) it applies to (tag)

**Why use them**
- Reduce data entry for common prescriptions.
- Enforce standardized directions ("Inject 0.25mg SC weekly x 4 weeks, then titrate").
- Minimize typos on routine rx.

Edit freely — templates do NOT retroactively change existing eRx records.`,
  },
  {
    id: "ad-erx",
    category: "admin-ops",
    title: "ERX Records (Admin)",
    audience: "admin",
    body: `Global searchable archive of every electronic prescription ever issued.

**Filter by** prescriber, patient, medication, LabFlo sync status, date range.

**What you can do**
- View the full eRx PDF.
- Check LabFlo sync status + manual resync trigger.
- Flag a record for compliance review.
- **Cannot** edit clinical content — that's a signed record. Any data fix requires a new eRx.`,
  },
  {
    id: "ad-audit",
    category: "admin-ops",
    title: "Audit Logs",
    audience: "admin",
    body: `Every significant action in the system is logged here. This is your HIPAA compliance safety net.

**Events logged include**
- Login / logout, password changes, role changes.
- Order approval / denial, eRx generation.
- Patient record edits (by whom, when, what field).
- Document views, document uploads.
- Admin config changes (email templates, domains, compliance recipients).
- Webhook events, webhook replays.

**Severity**
- Normal (info) rows render plain.
- **client_error** rows get a pulsing red severity badge.
- Click any row to see full detail in the side panel.

**Email Engineering** button (in the severity banner of any client_error row) — instantly emails the stack trace + context to the engineering contact configured in Settings.

**Retention** — audit logs are preserved **7 years** per HIPAA (configurable in Settings → Compliance).`,
  },
  {
    id: "ad-banners",
    category: "admin-ops",
    title: "Banners tab — every marketing strip in one place",
    audience: "admin",
    body: `The **Admin → Content → Banners** tab is the single control panel for every announcement / promotional strip on the marketing site. Three grouped sections, top to bottom:

**1. Currently-serving states strip** (top card)
The dark strip at the very top of the landing + pricing pages that reads "Currently serving: PA · WI · GA · FL · More states coming soon".
- **Active / Disabled** toggle — hides or shows the strip site-wide.
- **Label prefix** — default "Currently serving".
- **State codes** — comma-separated 2-letter codes, auto-uppercased, deduped, capped at 20.
- **Trailing note** — appears after the pills (e.g. "More states coming soon"). Optional.
- Click **Save changes** to push edits. Config lives at \`db.app_config._id="serving_states"\`; public endpoint \`GET /api/public/serving-states\`.

**2. Top-of-page strips** (three toggles, second card)
- **Orders-paused strip** — orange "System notice" with email-me-when-back capture. Toggle uses the existing \`/system/pause\` + \`/system/resume\` endpoints (audit-logged). While paused, an inline input lets you edit the message. Only super-admin can pause / resume.
- **Compare-plans strip** — black COMPARE row with Starter $79.99 · Semaglutide $149/mo · Tirzepatide $249/mo. Config: \`strip_toggles.compare_plans_enabled\`.
- **Million-Mile Movement ticker** — purple community step counter with Share button. Config: \`strip_toggles.million_mile_enabled\`. Toggling to Disabled hides the ticker on Landing + Pricing immediately (front-end polls this endpoint on load).

**3. Promotional Banners list** (bottom section — CRUD)
Every row here maps 1:1 to a doc in \`db.promo_banners\`. Each row shows type / status / start-end dates + **Pause / Activate / Delete** buttons. Three banner **types** you can create with **+ Create Banner**:

- **Rolling** — the green marquee that scrolls left-to-right at the very top of the landing page. Multiple rolling entries are concatenated with bullets. Example: \`banner-save\` ("Provider consultation included — transparent pricing, no hidden fees").
- **Popup** — a mid-page modal (e.g. \`banner-free-consult\` "Consultation Included — Your provider visit is built into the price…").
- **Promo Strip** — the below-hero rotating strip on landing + pricing (5-second auto-rotate). Supports **icon** (Truck / Badge $ / Sparkles / Shield / Gift / Percent), **subtitle**, and colored background. Seeded rows: \`strip-free-shipping\` and \`strip-no-membership\`.

**Create Banner dialog** — text, type, background color palette, optional subtitle (promo-strip only), icon picker (promo-strip only), start/end dates. Live preview shown below the form. Save inserts a new \`promo_banners\` doc; hidden immediately if no rolling/popup/promo-strip banners of that type are Active.

**Ops tips**
- Keep rolling text under 140 chars for mobile.
- Use start / end dates to schedule seasonal promos. Expired banners auto-hide even if Status = active.
- The seed migration re-inserts strip-free-shipping / strip-no-membership on backend startup only if they don't already exist — admin edits (status / text / colors) are preserved across restarts.
- FL / referral / abandoned-cart / low-stock banners are auto-managed elsewhere — do **not** create manual duplicates here.
- Serving-states + strip-toggles + promo_banners docs all live under different collections (\`app_config\` and \`promo_banners\`) so there's no risk of a schema collision.`,
  },
  {
    id: "ad-fact-myth",
    category: "admin-ops",
    title: "Fact or Myth",
    audience: "admin",
    body: `Educational card deck shown to patients on landing + dashboard. Each card presents a statement and reveals "Fact" or "Myth" + explanation.

Manage: title, statement, answer (fact / myth), explanation, category, active flag.

Aim for 8-12 active cards; the frontend shuffles them.`,
  },
  {
    id: "ad-physicians",
    category: "admin-ops",
    title: "Physicians (Public Directory)",
    audience: "admin",
    body: `Profiles shown on the public **/our-physicians** page. Separate from staff user accounts (this is marketing content, not login accounts).

Fields: Photo URL, Name, Title, Specialty, Bio, State licenses, Active flag.`,
  },
  {
    id: "ad-pharmacy-docs",
    category: "admin-ops",
    title: "Pharmacy Docs",
    audience: "admin",
    body: `Upload partnered pharmacy information documents (e.g., LabFlo licensure, Polomar pharmacy info) that patients can view from their dashboard.

Upload PDF → Give display name + display order → Active flag.`,
  },
  {
    id: "ad-reviews",
    category: "admin-ops",
    title: "Reviews",
    audience: "admin",
    body: `Patient testimonials shown on the landing page.

**Workflow**
1. Patient submits review via dashboard.
2. Status = "pending" — not visible publicly.
3. You review → Approve or Reject.
4. Approved reviews rotate on the landing carousel (up to 12 visible).

Flag abusive/PHI-leaking reviews immediately.`,
  },
  {
    id: "ad-baa",
    category: "admin-ops",
    title: "BAA — Business Associate Agreements",
    audience: "admin",
    body: `HIPAA requires a signed BAA with every vendor handling PHI.

**Tracking**
- Add a vendor → vendor name, contact, BAA effective date, expiration date, file upload.
- Dashboard widget shows **upcoming expirations** (<60 days) — renew before expiry.
- Expired BAAs highlight red until renewed or the vendor is removed.

Required vendors: Stripe, SendGrid, LabFlo, MongoDB Atlas (if applicable), AWS/cloud provider.`,
  },
  {
    id: "ad-wellness-panel",
    category: "admin-ops",
    title: "Patient Wellness Panel (in chart)",
    audience: "admin",
    body: `Embedded wellness summary inside the **Patient Detail Modal** — visible to **admin, prescriber, and customer service** roles. Default tab when you open any patient chart.

**Where to find it**
- Click any patient name from Orders, Users, Live Chat, or any list that opens the unified patient modal.
- The first tab is **Wellness** (Activity icon) — default. The other tabs (Notes, Orders, Calls, Texts, Receipts) work as before.

**What you'll see**
- **Clinical suggestion badge** (read-only) — purple-bordered card at the top that *only* renders when the data triggers a rule. Three rules tracked:
  - **🚨 Rapid loss alert** (rose) — fires when patient is losing >2%/week sustained over ≥4 weeks (above the 1-2%/wk clinical target). Suggests slowing pace.
  - **⚠️ Tolerance warn** (amber) — fires when ≥3 severe side-effect logs in the last 14 days. Suggests holding/lowering dose. Common chips listed.
  - **💡 Plateau info** (green) — fires when patient lost <2 lbs over their last 4 on-time weigh-ins AND has been on their medication for ≥4 weeks. Suggests escalating dose.
  - Each card has TWO actions:
    - **💊 Apply [template]** (purple button) — when (a) the rule maps to a matching Rx template AND (b) the patient has a pending order, clicking this opens the order's eRx review modal with the matched template pre-loaded into all fields (medication, dosage, quantity, directions, diagnosis) and the suggestion note prepended to **Rx Notes** for context. The prescriber still reviews and submits as usual — this is a one-click shortcut that saves the copy-paste cycle, **not an auto-action**. Template mapping: plateau → next dose UP (e.g., 0.5mg → 1.0mg); tolerance/rapid-loss → previous dose DOWN. If no pending order exists, the button label changes to "Copy [template]" — clicking copies the suggestion note to clipboard for use at next visit.
    - **Copy note** (subtle) — drops just the suggested chart note into your clipboard. Use for free-form notes or outside the eRx flow.
  - Click "Suggested chart note" to expand the full text.
  - **Important**: These are *non-binding hints*, not clinical decisions. The prescriber always decides; the platform never auto-changes a prescription.
- **4 stat cards**: Weigh-in day · Current streak · Coupons earned · Severe symptoms in last 30 days. The "severe symptoms" card turns rose with "review needed" if there's at least one severe log — useful for pre-visit triage.
- **Streak progress bar**: Visual of current streak toward the next $10 reward; "Last missed" banner if a recent miss reset their streak.
- **12-week weight trend chart**: Line chart with first-vs-current delta pill (green if losing weight, amber if gaining). Three quick stats: starting weight, current weight, total log count. Reference line at starting weight makes plateaus easy to spot.
- **Side effects (last 30 days)**: Severity-graded cards (severe red / moderate amber / mild green) with chip badges (nausea, fatigue, etc.) and patient notes. Sorted newest-first.

**Use cases**
- **Pre-visit prep** (prescriber): Open chart 5 min before the call — see if they've been tracking, spot dose-tolerance issues from severe logs, plan dose adjustments.
- **Refill review**: Plateau patient on max dose? Switch them to tirzepatide. Lost too fast? Lower dose. The chart tells you in 5 seconds.
- **CS escalation triage**: A patient calls with a side-effect concern — pull up their chart, see if it's a pattern (3 severe logs in 2 weeks) or a one-off, route accordingly.

**Endpoints**
- \`GET /api/wellness/admin/{patient_id}/summary\` — one-shot summary used by the panel
- \`GET /api/wellness/admin/{patient_id}/logs\` — full paginated log history (use for audits or deeper drill-down)

> **Permissions**: \`admin\`, \`prescriber\`, \`customer_service\`, \`it\`, \`super_admin\` — patients return 403.`,
  },
  {
    id: "ad-wellness",
    category: "admin-ops",
    title: "Wellness Tracker & Streak Coupons",
    audience: "admin",
    body: `Patient-facing weekly weigh-in tracker with a 5-week streak that mints a stackable **$10 off coupon**. Side-effect logging with severity escalation also lives here.

**How streaks work**
- Patient picks a single day-of-week (e.g., Monday) in their Wellness Settings.
- They have until 11:59:59 PM UTC of that DOW to log a weight that week. After that → streak resets to 0 at 12:01 AM the following day.
- Hit 5 consecutive on-time weeks → \`WIGH-XXXXX\` coupon ($10 off, stackable, expires in 90 days). Streak resets.
- **Cap: 4 coupons per rolling 12 months** (i.e., $40/year max). After cap, streak still tracks but no new coupon mints until the oldest one rolls off.

**Stacking** — Streak coupons are flagged \`stackable: true\` so they apply *on top of* the base discount (FL state discount or non-stackable promo code). The checkout endpoint sums them. Patient can have multiple unredeemed stackable coupons; all are auto-applied.

**Cron job** — \`daily_streak_reset_job\` runs at 01:00 UTC daily. For each patient whose weigh-in DOW was *yesterday* and who didn't log → resets streak + fires \`et-missed-weigh-in\` email. Idempotent (one fire per missed week). To trigger manually: \`POST /api/wellness/admin/run-streak-check\` (admin only).

**Email/SMS templates** (admin-editable in Email Templates / SMS Templates tabs):
- \`et-missed-weigh-in\` (auto-fires) — encourages restart, vars: \`{{patient_name}}\`, \`{{weigh_in_dow}}\`
- \`et-streak-reward\` (auto-fires on 5th log) — vars: \`{{patient_name}}\`, \`{{coupon_code}}\`, \`{{coupon_amount}}\`, \`{{coupon_expires}}\`
- \`sms-missed-weigh-in\` / \`sms-streak-reward\` — **manual** Click-to-Text templates for staff follow-ups via Vonage (no automated SMS sender wired yet)

**Side-effect log** — Patients tap chips (nausea, fatigue, GI, headache, etc.) + severity (mild/moderate/severe). Severe + agent online → opens chat with category=clinical and pre-fill. Severe + no agents online → modal shows customer service phone with **"Call 911 for emergency"** prominent. All logs stored in \`wellness_logs\` collection — visible in patient chart at \`GET /api/wellness/admin/{patient_id}/logs\`.

**🗂️ Admin sidebar navigation (12 main tabs)** — Reorganized 2026-04-27. The left sidebar now shows 12 grouped main tabs; multi-section tabs reveal a horizontal sub-tab strip at the top of the page when opened (Option B layout). Mapping:

1. **Overview**
2. **Reports & Health** → Analytics · Reports · Prescriber→LabFlo · ERX Records · Audit Logs · LabFlo Webhooks · System Health · Error Reports
3. **Orders** → Orders · Tracking Queue
4. **Team** → Users · Compliance · Time Clock
5. **Medications** → Medications · Rx Templates
6. **Live Chat**
7. **Templates** → Chat Templates · Email Templates · SMS Templates
8. **Content** → Banners · Fact or Myth · Physicians · Reviews
9. **Pharmacy** → Pharmacy Docs · BAA
10. **Loyalty & Marketing** → Loyalty & Coupons · Marketing Codes (the Subscription Discount % toggle now lives at the top of the Loyalty & Coupons sub-tab — it was moved here from Settings on 2026-04-27)
11. **Training Manual**
12. **Settings** (no longer contains Subscription Discount)

**🎚️ Editable loyalty rules (updated 2026-04-28)** — The "Rules" card inside **Admin → Loyalty & Marketing → Loyalty & Coupons** is now fully editable. Live thresholds + reward amounts for: One-time loyalty ($ + N orders) · Subscription resubscribe ($ + N months) · Royal Tier ($ + N consecutive 90-day + alt M sub months) · Abandoned cart ($ + N days unpaid) · Referral ($ + max cap). Testids: \`loyalty-rules-card\`, \`loyalty-rule-{group-key}\`, \`loyalty-rule-input-{field}\`, \`loyalty-rules-save-btn\`, \`loyalty-rules-reset-btn\`, \`loyalty-rules-discard-btn\`.

**Where edits propagate:** All values feed a single Mongo doc (\`db.app_config.loyalty_rules\`) via \`backend/routes/loyalty_rules.py:get_loyalty_rules()\`. The cron loyalty job (\`_evaluate_loyalty_for_patient\`), abandoned-cart job (\`check_abandoned_carts\`), referral reward trigger (\`_trigger_referral_reward_if_first_paid\`), signup-time install bonus in \`routes/auth.py::register\`, and \`/api/referrals/mine\` all pull from this doc on every call — **no restart required, changes take effect on the next cron tick (or manual "Run Loyalty Check")**. The Companion App referral banner reads \`reward_amount\` from \`/api/referrals/mine\` and \`companion_install_bonus_points\` from \`/api/loyalty-rules/public\` via the shared \`useLoyaltyRules()\` hook, so the "+X bonus points on signup 🎁" line auto-updates the moment a super admin edits the value in the Loyalty Rules card. Cache invalidation is triggered by \`invalidateLoyaltyRulesCache()\` after every successful admin save. A public read endpoint \`/api/loyalty-rules/public\` is available for any patient-facing pricing copy that needs to mirror the config. Each save is audit-logged as \`loyalty_rules_updated\`.

**🎁 Companion install bonus (NEW)** — Super-admin-editable \`companion_install_bonus_points\` (default 1000) in the Loyalty Rules card. Fires in \`routes/auth.py\` right after the referral doc is inserted on new patient signup — inserts a synthetic \`stepathon_step_logs\` row with \`source: "referral_install_bonus"\` and \`referred_user_id\` set, so the referrer's Step-a-Thon points ledger picks it up on next fetch. Idempotent (guarded by an existing-log check). Set to 0 to disable entirely. Surfaces that auto-update: CompanionReferralBanner (Companion home), CompanionRewardsScreen ("Refer + earn" tile), /companion/install install page banner, /referrals hub badge, share message text on all three surfaces.

Sub-tabs persist in \`activeTab\` (the leaf key), so existing CTAs like \`setActiveTab("health")\`, \`setActiveTab("baa")\`, \`setActiveTab("tracking")\` continue to work — they auto-route to the correct main tab. Testids: main tabs \`admin-nav-{key}\`, sub-tab strip \`admin-subtabs\`, individual sub-tabs \`admin-subtab-{key}\`.

**Companion App** (\`/companion\`) — Mobile-first patient PWA. Now split into **FREE tier** (available to any logged-in patient, no subscription required) and **SUBSCRIPTION tier** (paid unlocks). Five bottom-nav tabs: **Home** (today's summary — free step-a-thon card + subscriber calorie/macro rings gated behind \`LockedSectionTile\`), **Rewards** (FREE — points hero, active step-a-thon events with in-tab step logger, shareable tier badges, rewards catalogue, top-5 leaderboard, auto-step-tracker opt-in, push-nudge opt-in), **Body** (FREE — weight, tape measurements, side-effect chips, injection logging, weekly shot reminders), **Food** (subscription — calorie log by meal + USDA search + barcode scanner), **Move** (subscription — auto-estimated calorie burn via MET formula). Non-subscribers see \`LockedScreen\` overlays on Food/Move with dual CTAs (Unlock via subscribe or Redeem lifetime code). Endpoints — free: \`GET/POST /api/wellness/shot-reminders\`, \`GET/POST/DELETE /api/push/*\`, \`POST /api/stepathon/events/{slug}/log\`, \`POST /api/stepathon/rewards/{id}/redeem\`. Subscription-only: \`POST /api/wellness/calories\`, \`POST /api/wellness/exercise\`, \`GET /api/wellness/macros/today\`. All logs flow into the same \`wellness_logs\` collection — show up immediately on the staff Wellness Tab. Patient can install to home screen (Companion-scoped service worker at \`/public/sw.companion.js\` with web-push handlers).

**Landing-page advertisement** — The public landing page (\`/\`) carries a dedicated Companion App marketing section (testid: \`companion-app-section\`) between Testimonials and the Install App pill. Green **"Free to install · no subscription needed"** badge on top. Two feature blocks below: **Free (everyone)** — 4 emerald-icon rows (Auto step counter + rewards, Weekly shot reminders, Body measurements + weight, Daily rewards nudge). **Subscribers also unlock** — 4 amber-icon rows (Food logging + macro rings, Exercise / Move logging, Fit-Quiz weight projection, AI care-team chat). Three CTAs: **Install free Companion App** → \`/companion/install\` (primary, emerald), **Start a subscription** → \`/register\` (amber), **Already a member? Open the app** → \`/login\`. Use this surface to answer "what does the app do?" — the free tier is designed to be a top-of-funnel acquisition hook.

**🎁 Lifetime-access codes (admin Easter-egg)** — Admins can generate one-time codes that grant **lifetime free Companion App access** to any patient (subscriber OR non-subscriber). Live in **Loyalty & Coupons → Companion App Lifetime Access** card. Use cases:
- VIP patients / case-by-case retention saves
- Refund replacements when we can't refund the cash
- Beta testers, partner pharmacies, employees, friends-and-family

**How to issue**:
1. Admin Dashboard → **Loyalty & Coupons** tab → scroll to "Companion App Lifetime Access — Easter-egg codes"
2. Set quantity (1–50) and an optional internal note (e.g., "Sarah K. Sept-2025 complaint resolution")
3. Click **Generate**. A single code is auto-copied to your clipboard. Email/text it to the patient.
4. Patient redeems on **Patient Profile → Companion App access code** card by typing the code (format \`COMP-XXXX-XXXX-XXXX\`).
5. Code is single-use; the table will show "Redeemed" + the patient's email/date.

**Revoking**: Click the trash icon next to any **active** code to revoke it. Already-redeemed codes cannot be revoked (the grant stands).

**Email it directly (Mail icon)**: Each active row also has a purple Mail icon next to Copy/Revoke. Click it → small dialog asks for the recipient's email + (optional) name → click **Send** to fire a branded GLPFlo SendGrid email (\`et-companion-access-code\` template) containing the code in a dashed-purple "Your code" box plus a tailored CTA. If the recipient already has a GLPFlo account, the email links them to **Sign in & redeem**; otherwise it prompts them to **Create your account** first. After sending, the row shows "Emailed to {address}" beneath the action buttons. All sends are audit-logged as \`companion_code_emailed\` and \`email_sent_to\`/\`email_sent_at\`/\`email_sent_by_id\` are stored on the code document.

**Backend collections**: \`companion_access_codes\` (the issued codes), \`companion_access_grants\` (one row per patient who has lifetime access — unique on user_id). \`subscription_gating.has_active_subscription()\` short-circuits to \`reason="lifetime_grant"\` if the patient has a grant, so all wellness/foods/companion endpoints unlock automatically.

**🎯 Companion App referral banner** — The Companion App home screen carries a yellow "Refer a friend, both get $20" banner above the macro rings (testid \`companion-referral-banner\`). One-tap native share via Web Share API + clipboard fallback. Tapping the banner body navigates to \`/referrals\` for the full hub. The reward amount auto-syncs with whatever \`/api/referrals/mine\` returns (\`reward_amount\`).

> **Pitfall:** If you change the wellness DOW for a test patient via DB, also reset \`current_streak\` and \`last_logged_date\` — otherwise the streak math gets confused.`,
  },
  {
    id: "ad-emails",
    category: "admin-ops",
    title: "Email Templates",
    audience: "admin",
    body: `Rich-text templates for every transactional email (welcome, order approved/denied, shipped, delivered, refill reminder, loyalty discount, abandoned cart, **missed weigh-in**, **streak reward earned**).

**Editing**
1. Click the template row.
2. Edit Subject + Body (WYSIWYG with variable placeholders like \`{{patient_name}}\`, \`{{tracking_url}}\`).
3. Save.
4. Click **Send Test** → enter your email → receive a live test render with sample data.

**Reuse from Chat Templates** ✨ — Above the rich-text editor (and also in the **Send Custom Email** modal) you'll find the **Insert from Chat Templates** chip. Click it to open the same canned-response library that powers slash commands in Live Chat — pick one and it's pasted in at your cursor as a fresh paragraph. Single source of truth = no voice/tone drift between channels. The same chip lives inside **SMS Templates** for outbound texts. \`{{patient_name}}\` placeholders are preserved verbatim and substituted at send-time.

> **Pitfall:** If you break a variable placeholder (e.g., typo \`{{patientname}}\`), the email still sends but with the literal text. Always send a test before marking a template "active".`,
  },
  {
    id: "ad-livechat",
    category: "admin-ops",
    title: "Live Chat Console — Sign-On, FIFO Queue & Pop-Out",
    audience: "all",
    body: `The **Live Chat** tab on every staff dashboard is a full live-chat workspace. Patients only see chat as available when at least one staff member is signed on here.

**What patients see**
- A coloured pill next to "GLPFlo Support" in the chat-widget header tells them at a glance: green pulse **ONLINE** = an agent can answer now, amber **OFFLINE** = the team has stepped away. While the widget is loading they see a neutral "Checking…" pill — never a falsely-enabled input.
- When **OFFLINE**, the input is disabled (locked plus a "No agents online — email us instead" placeholder) and the body shows an amber email-fallback card. Patients can't type into the void.
- The widget polls availability every **15 seconds** while open, and re-checks once more right before it sends a message — so if you sign off mid-conversation, the patient is told within seconds and steered to email.

**Going live**
- Click the **Live Chat** tab → re-enter your portal password (yes, even though you're already logged in — this is a separate consent signal).
- A green pulse + "You're live" banner confirms patients can reach you.
- You stay live until you click **Sign off**, even after closing the tab. Auto-stale after 5 minutes of inactivity.

**The console layout**
- **Left sidebar** has three sections:
  1. **Unassigned queue · FIFO** — oldest first. Click any row to claim it.
  2. **Your active chats** — conversations you've claimed.
  3. **All other agents** (admin only) — shadow-monitor any active chat.
- **Right pane** — full message thread + composer.
- **Top right** — Mute toggle (silences the new-message audio ping), Pop out, Sign off.

**Claim rules (FIFO + ownership)**
- The FIRST staff to claim a chat owns it. Concurrent claims get a friendly 409 error showing who beat them to it.
- Once claimed, **only the assigned agent** can type. Even admins can't barge in — they have to **reassign** first using the dropdown in the chat header.
- Admins can shadow-read (yellow "View only — owned by X" banner) and reassign or close at any time.

**Canned responses (slash commands)**
- Type **\`/\`** at the start of the input → a picker opens with all active canned responses.
- Filter by typing more — e.g. \`/refi\` matches \`/refill\`. \`↑↓\` to navigate, **Tab** or **Enter** to insert, **Esc** to dismiss.
- The template **expands into your input** so you can edit before sending. \`{{patient_name}}\` auto-substitutes from the active conversation.
- Each send increments the canned response's usage counter so admins can see which templates are pulling weight.
- **Default templates seeded**: \`/greeting\` \`/refill\` \`/track\` \`/sideeffects\` \`/missed\` \`/billing\` \`/hsa\` \`/cancel\` \`/escalate\` \`/close\`.

**Adding / editing canned responses (admin)**
- **Admin → Chat Templates tab** (sidebar nav, MessageCircle icon, just below Live Chat) — full CRUD UI:
  - **New Response** button opens a dialog: \`slash\`, \`title\`, \`body\` (up to 2000 chars, supports \`{{patient_name}}\`), \`category\` (general / orders / clinical / billing), \`sort_order\` (lower = higher in picker), \`active\` checkbox.
  - Each row shows usage count + last-used timestamp so you can see which templates are pulling weight and prune dead ones.
  - **Pencil** icon to edit, **rotate** icon to deactivate/reactivate (deactivated rows stay in the DB but disappear from staff's slash picker), **trash** icon to permanently delete.
- Backing endpoints (admin-only) if you ever need to script bulk imports:
  - \`POST /api/chat/canned-responses\` body \`{slash, title, body, category, sort_order, active}\`
  - \`PATCH /api/chat/canned-responses/{id}\` partial update
  - \`DELETE /api/chat/canned-responses/{id}\`
  - \`GET /api/chat/canned-responses/all\` (admin) — includes deactivated rows
- Body supports \`{{patient_name}}\` substitution.

**Pop-out window**
- Click **Pop out** → \`/staff-chat\` opens in a new browser tab without portal chrome.
- Keep responding to patients in that window while using the rest of the portal in the original tab.
- Closing the popped-out window does **not** sign you off — you stay live in the main portal.

**Ending a chat**
- Either the assigned agent or an admin can click **End chat**. The patient sees the conversation marked closed.

> **Pitfall**: Forgetting to sign off when you close your laptop. The 5-minute auto-stale catches this, but you'll have shown as available to patients during that window. Always click **Sign off** when stepping away.`,
  },
  {
    id: "ad-receipts",
    category: "admin-ops",
    title: "Receipts — Preview, Resend & Send-History",
    audience: "all",
    body: `Patients can now preview, print, email, forward, share, and download any paid receipt directly in-browser. Staff have visibility into who sent what, when, and to which address.

**Patient experience** (helpful to know when supporting them)
- Click the **purple receipt icon** on the dashboard, the **"View"** button on Profile → Receipts, or **"View receipt"** in the order detail modal.
- A modal opens with the PDF rendered inline + 5 actions: **Print · Email me a copy · Forward to another email · Share · Download**.
- "Forward to another email" prompts for any address — used for HSA/FSA reimbursement, accountants, spouses.

**HIPAA controls (automatic)**
- **Rate limit:** patients capped at **5 sends per receipt per rolling hour**. 6th attempt → HTTP 429 with friendly message. Staff are exempt.
- **Audit log:** every send writes a row to \`audit_logs\` with action \`receipt_email_sent\` (default recipient) or \`receipt_email_forwarded\` (override recipient) + recipient address + IP + sender role.

**Where staff see this**
1. Open any patient via the **Patient Detail Modal** (click their name in any order/queue row).
2. Switch to the **Receipts** tab.
3. If a receipt has been re-sent, you'll see a small purple caption underneath: *"Re-sent N times · last to <email> (date)"*.
4. **Click that caption** — an inline table slides open showing every send: When · To · Type (Forward/Self) · Initiated by · IP.

**Staff resending a receipt** (e.g., during a support call)
- The same Forward flow works for staff — staff are exempt from the 5/hour rate limit, but every send is still audit-logged with your name and role.
- Use this when a patient says "I never got my receipt" — first check the send-history drilldown to confirm the recipient address; if it was wrong, forward to the corrected address.

**Endpoints (for IT/Admin reference)**
- \`POST /api/receipts/{session_id}/email\` — body \`{to_email}\` optional; default = patient on file.
- \`GET /api/receipts/{session_id}/email-history\` — staff-only, full per-receipt timeline.
- \`GET /api/receipts/by-user/{user_id}\` — now includes \`forward_count\`, \`last_forward_to\`, \`last_forward_at\`.`,
  },
  {
    id: "ad-trust-badge",
    category: "admin-ops",
    title: "Polomar Trust Badge — Where It Appears",
    audience: "all",
    body: `The "Compounded by **Polomar Specialty Pharmacy** · Licensed 503A/503B compounding pharmacy partner" badge automatically appears across 4 patient touchpoints:

1. **Public landing page hero** — purple pill below the savings line.
2. **Patient dashboard active-medication card** — small purple line under the medication type.
3. **Receipt PDF** — under the order totals.
4. **Shipping/tracking email** — purple-bordered card below the "Track Your Shipment" CTA.

**One source of truth**
- Pharmacy name is resolved via \`_get_pharmacy_display(order)\` — uses the order's linked LabFlo tenant when present, else falls back to the **first active LabFlo pharmacy** in the live cache.
- Today that's always Polomar. When LabFlo onboards more partners, badges will resolve per-order automatically without any code change.

**Why this matters for support calls**
- Patients sometimes ask "who is making my medication?" — the badge gives them a credible pharmacy name + license type. Direct them to **\`/pharmacy-info\`** for full Polomar details + COA samples.`,
  },

  // -------- ADMIN MARKETING (admin) --------
  {
    id: "mk-loyalty",
    category: "admin-marketing",
    title: "Loyalty & Coupons",
    audience: "admin",
    body: `Automatic + manual discount system.

**Automatic programs**
- **90-day loyalty:** patient reaches 90 days of continuous subscription → auto-emailed a loyalty coupon.
- **Abandoned cart:** patient adds to cart but doesn't pay within 7 days → auto-emailed a recovery coupon.
- **Referral:** $20 credit to both referrer and referred patient on referred-patient's first paid order (capped at 10 referrals per referrer). Edit the live amount in **Admin → Loyalty & Coupons → Rules** (\`db.app_config.loyalty_rules.referral_amount\`).

**Manual coupons**
- Create one-off codes (e.g., press campaign) with discount %, valid dates, max redemptions.
- Track redemptions + revenue impact on the **Campaign Analytics** subview.`,
  },
  {
    id: "mk-marketing-codes",
    category: "admin-marketing",
    title: "Marketing Codes — Bulk Generation",
    audience: "admin",
    body: `For paid campaigns that need unique codes per recipient (influencer handouts, email blasts).

**Bulk generate (1–500 codes at once)**
1. Enter a **Campaign name** (e.g., "IG-creator-April").
2. Enter discount % + expiration.
3. Enter quantity (1–500).
4. Click **Generate** → codes are created, each unique, all tagged to the campaign.
5. **Download CSV** to hand to the campaign team.

**Analytics**
- **Campaign Analytics** tab shows ROI per campaign (redemptions, revenue, cost basis).
- **Time-series trend chart** — 7/30/60/90-day windows filterable by campaign.
- Top 10 campaigns ribbon shows best performers at a glance.`,
  },
  {
    id: "mk-prescriber-labflo",
    category: "admin-marketing",
    title: "Prescriber → LabFlo Report",
    audience: "admin",
    body: `_Retired — combined into **Reports & Health → Pharmacy Fulfilment** cockpit (partner-agnostic per-partner breakdown + CSV export)._`,
  },

  // -------- IT TRIAGE (it) --------
  {
    id: "it-errors",
    category: "it-triage",
    title: "Error Reports Tab — Ack / Resolve Workflow",
    audience: "it",
    body: `Frontend JS errors + backend unhandled exceptions stream here in real time.

**Status states**
- **Open** — new, unattended.
- **Ack** (acknowledged) — you're aware and investigating. Auto-send pings are suppressed while Ack'd.
- **Resolved** — fix deployed / verified, root cause noted.

**Workflow**
1. Click a row → see stack trace, user context, URL, user agent, timestamp.
2. Click **Ack** when you start investigating.
3. Deploy fix.
4. Click **Resolve** with a short note ("fixed in commit abc123 — null check in LabFloSync").
5. Group similar errors using the automatic fingerprint hash — one fix Acks them all.`,
  },
  {
    id: "it-auto-send",
    category: "it-triage",
    title: "Auto-Send Mode & Error Digest",
    audience: "it",
    body: `Two ways errors reach engineering beyond the in-app Error Reports tab.

**Auto-Send mode** (per error group)
- Toggle ON → every new occurrence of this error auto-emails engineering contact.
- Suppressed while the group is Ack'd or Resolved.
- Use sparingly — only for production-breaking issues.

**Daily Error Digest**
- Scheduled 9am UTC (configurable in Settings).
- Summarizes last 24h of error activity: top groups by volume, new groups, still-open groups.
- Opt-in per engineering contact.
- Click **Send Digest Now** for an on-demand version.

**Email Engineering button**
- Appears in AuditLogDetailModal for any \`client_error\` row.
- One-click sends the full stack trace + context email.`,
  },
  {
    id: "it-health",
    category: "it-triage",
    title: "System Health Dashboard",
    audience: "it",
    body: `Real-time status of all subsystems.

**Checks**
- MongoDB reachability + latency
- Stripe API reachability
- SendGrid API reachability
- LabFlo API reachability
- Background worker heartbeat (last <90s)
- Scheduled cron jobs last-run timestamps (compliance report, refill reminders, error digest)

**Thresholds**
- Green = all checks passing.
- Yellow = one non-critical subsystem failing.
- Red = critical failure (DB down, auth down).

**Notifications**
- Click the bell icon in the sidebar to opt in to desktop browser notifications on state changes (Healthy ↔ Degraded).
- First click requests browser permission.`,
  },
  {
    id: "it-triage-contact",
    category: "it-triage",
    title: "Engineering Contact Routing",
    audience: "admin",
    body: `Configurable in **Settings → Engineering Contact**.

**Fields**
- Primary email (required) — receives all Email Engineering clicks + digests.
- CC emails (optional comma-separated).
- On-call escalation email (for critical severity only).

Test the routing with **Send Test Alert** button — fires a fake critical alert to verify delivery.`,
  },

  // -------- COMPLIANCE (admin) --------
  {
    id: "cm-compliance",
    category: "compliance",
    title: "Compliance Reports (Automated + On-Demand)",
    audience: "admin",
    body: `Monthly HIPAA compliance summary auto-generated and emailed to compliance recipients.

**Includes**
- Staff login activity + failed attempts.
- PHI access events (who viewed which patient record).
- Data export events (CSV downloads).
- BAA status summary.
- Webhook integrity (signature failures, replays).
- Error digest summary.

**Configure recipients:** Settings → Compliance → add email(s).
**Schedule:** first of every month, 2am UTC.
**On-demand:** **Send Now** button — useful for quarterly reviews or incident response.`,
  },
  {
    id: "cm-hipaa",
    category: "compliance",
    title: "HIPAA Rules of Engagement for Staff",
    audience: "all",
    body: `Minimum-necessary PHI access. Only view what you need for the task at hand.

**Do**
- Reference patients by order ID in chat when possible.
- Log out when stepping away from your workstation (Cmd/Ctrl+L on a managed device).
- Use the shipping tracking link to share delivery info — do NOT forward the tracking number via unsecured channels.

**Don't**
- Screenshot patient dashboards and paste into personal Slack/email.
- Save patient files to your local drive outside the portal.
- Discuss patient-identifying details in public spaces (cafe, bus, shared office audible range).
- Share your login with anyone, ever. Each staff user must have their own GF-ID.

**Breach response**
- Suspected PHI exposure? Immediately notify Super Admin + Compliance Officer. Every staff member has an obligation to report within 24 hours.`,
  },
  {
    id: "cm-refill-reminders",
    category: "compliance",
    title: "Refill Reminders (Automated)",
    audience: "admin",
    body: `Scheduled cron sends reminder emails to patients approaching refill window.

**Settings → Refill Reminders**
- **Enabled** toggle.
- **Days before due** (default 7) — how early to send.
- **Recipients** — patients on active subscription only.

**On-demand:** **Send Refill Reminders Now** button — useful after config changes or if the cron missed a day.`,
  },

  // -------- FAQ (all) --------
  {
    id: "faq-common",
    category: "faq",
    title: "Frequently Asked Questions",
    audience: "all",
    body: `**Q: I approved an order but LabFlo shows \`labflo_synced: false\`. Why?**
A: LabFlo API returned non-200. Check **System Health** for LabFlo status. If green, click **Resync** on the eRx record. If red, wait and retry — the sync is idempotent and safe.

**Q: A patient says they never got the shipping email but I see the order is shipped.**
A: 1. Check their email address on file (typos happen). 2. Check LabFlo Webhooks tab — filter by that ERX — confirm the webhook arrived. 3. If the webhook is there but no email, open an IT ticket — SendGrid may have bounced the address.

**Q: Can I un-approve an order?**
A: No. eRx is a signed record. You can mark the order cancelled (stops the LabFlo flow if they haven't shipped yet) and issue a new order if needed. For already-shipped orders, contact LabFlo directly.

**Q: Why do some orders show a "Pre-Screened (PS)" badge?**
A: It means automated screening (allergy + medical-history + drug-interaction checks) did not raise any flags on that patient's record for the requested medication. It is **not** an automatic approval — the clinician must still click Approve. If the badge is missing, treat the order as requiring careful review of the flagged item(s).

**Q: How do I unlock a locked-out staff user?**
A: Admin → **Users** → find user → **Unlock** button (shown when \`login_attempts\` counter is >= 10). You can also wait 15 min for auto-unlock.

**Q: Where do patient complaints / support tickets go?**
A: Staff Chat is internal only. Patients use their in-app chat with the **customer_service** role staff. For urgent issues, patients have a support email configured in the footer.

**Q: Can I delete a patient record?**
A: HIPAA generally requires retention for 7 years. You can **deactivate** the account (they can't log in) but data is retained. A true deletion (right-to-be-forgotten) requires Super Admin + Compliance Officer approval.

**Q: A patient called asking "where did my receipt actually go?"**
A: Open them via the Patient Detail Modal → **Receipts** tab → click the purple "Re-sent N times…" caption under any receipt → the inline drilldown shows every send (recipient, when, sender, IP). If a forward went to a wrong address, just forward it again to the correct one — every send is audit-logged.

**Q: A patient asks me to confirm what pharmacy is making their medication.**
A: Today it's always **Polomar Specialty Pharmacy** (US-licensed 503A/503B compounder, our only authorized LabFlo tenant). The "Compounded by Polomar Specialty Pharmacy" badge appears on their receipt PDF, shipping email, dashboard, and the public landing page — all from the same live LabFlo source. Direct them to **\`/pharmacy-info\`** for COA samples and licensure documents.

**Q: Where can I send a prospect / curious lead to read about pricing without making them sign up?**
A: Send them to **\`/medications-info\`** (no auth needed). It compares Semaglutide vs Tirzepatide side-by-side (mechanism, avg. clinical weight loss with citations, available strengths) plus all 3 pricing tiers (Single vial / Monthly / Royal 90-day) and a 6-question FAQ. Prices are pulled live from the same engine that runs checkout, so they always match.`,
  },
  {
    id: "faq-pitfalls",
    category: "faq",
    title: "Common Pitfalls (Learn from others' mistakes)",
    audience: "all",
    body: `**Don't edit an email template and forget to Send Test**
Variables like \`{{tracking_url}}\` are case-sensitive. A typo silently ships broken emails to real patients.

**Don't approve an order from the queue without reading the questionnaire**
The AA badge flags allergies/medical history. It does not flag every red flag (patient-reported side effects, pregnancy status, etc.). Expand the row and read the questionnaire.

**Don't share your User ID/password — ever**
Each action is audit-logged to your account. If someone else uses your login, their actions look like yours. If you suspect shared credentials, rotate immediately.

**Don't delete a medication — deactivate it**
Deletion orphans historical orders. Set Active=false; they disappear from patient view but historical data stays clean.

**Don't assume LabFlo is down when orders show pending**
The LabFlo queue can take 30–60min on high-volume days. Check System Health first, then the LabFlo Webhook log before escalating.

**Don't forget to renew expiring BAAs**
The BAA widget on Admin dashboard flags expirations. An expired BAA with a PHI-handling vendor is a HIPAA violation — renew 60 days before expiry.`,
  },

  // -------- EMERGENCY PAUSE + SYSTEM OPS --------
  {
    id: "ops-emergency-pause",
    category: "admin-ops",
    title: "Emergency Pause — blocking all new orders + refills",
    audience: "admin",
    body: `**When to use this**
- Partner pharmacy is out-of-stock or closed for a few days
- Pharmacy fulfillment queue is backed up beyond acceptable lead times
- Suspicious-order spike during a payments incident
- Any scheduled system maintenance window

**How it works**
- Admin Dashboard → **System Ops** tab → "Emergency pause" card.
- Type a patient-visible message (up to 500 chars). Example: *"Pharmacy closed through Monday — we'll resume orders Tuesday morning. Your existing subscription is unaffected."*
- Click **Pause all orders & refills**, then confirm in the amber inline prompt.

**What happens immediately**
1. \`POST /api/orders\` and \`POST /api/orders/refill\` start returning HTTP 503 with your message — so even rogue clients are blocked at the API layer.
2. A sticky amber banner appears on every page (marketing + dashboards) displaying your message. Patients can dismiss it but the block is still enforced.
3. Staff-created custom orders (\`/orders/custom\`) are intentionally **not** blocked so prescribers/CS can still manage existing flows.

**Resuming**
- Same card → **Resume orders**. Status updates within 60s on patient devices (the banner polls every minute).

**Audit trail**
- Both \`orders_paused\` and \`orders_resumed\` events are logged to the HMAC-chained audit log with your user + the custom message text.`,
  },
  {
    id: "ops-audit-integrity",
    category: "admin-ops",
    title: "Audit Log HMAC Chain — integrity verification",
    audience: "admin",
    body: `**Why it exists (HIPAA § 164.312(b))**
Every audit log entry contains an HMAC-SHA256 hash that chains to the previous entry. If a rogue admin or attacker edits, deletes, or inserts a log row directly in MongoDB, the chain breaks and we detect it instantly.

**How to verify before a HIPAA audit**
1. Admin Dashboard → **System Ops** → *Audit-log HMAC chain integrity* card.
2. Click **Quick scan (last 500)** — runs in <1 second.
3. For an audit: click **Full scan** — walks every chained entry (takes ~5–10 s on 100k entries).

**Reading the result**
- **Chain valid** (green) → nothing tampered. Scanned = total rows walked; Chained = new-era entries; Legacy = pre-chain entries (expected, harmless).
- **Tampered** (red) → shows the exact ID + timestamp + reason (\`entry_hash mismatch\` or \`prev_hash mismatch\`). Escalate to IT immediately.

**Never**
- Never directly edit \`audit_logs\` rows in Mongo — you'll break the chain and cause false positives on the next scan.
- Never delete individual audit rows. Archival (past 1 year) and 6-year retention purges happen automatically via the nightly job.`,
  },
  {
    id: "ops-audit-archival",
    category: "admin-ops",
    title: "Audit Archival + 6-year Retention",
    audience: "admin",
    body: `**Automatic schedule**
- Nightly at 03:30 UTC, a scheduler job moves entries older than **365 days** from the hot \`audit_logs\` collection to \`audit_logs_archive\` (cold storage).
- Entries older than **2,190 days (6 years)** are purged from the archive — the HIPAA minimum retention horizon.

**On-demand trigger**
- System Ops tab → *Archive now* button. Rarely needed outside of testing.

**Querying archived logs**
- Archived entries keep their \`prev_hash\` / \`entry_hash\`, so the HMAC chain still verifies across hot + cold collections.
- To answer "did staff member X ever view patient Y more than a year ago?", query \`audit_logs_archive\` with the same filters as \`audit_logs\`.

**Retention override**
- If your state requires >6 years (some do for minors' records), set \`AUDIT_RETENTION_DAYS\` in the backend .env to a higher value (e.g. 7300 for 20 years). Never lower than 2190.`,
  },

  // -------- PHI ACCESS LOG + REVEAL GATE --------
  {
    id: "hipaa-phi-access",
    category: "compliance",
    title: "PHI Access Logging — what you should know",
    audience: "all",
    body: `**What we log, automatically, on your behalf:**
Every GET request that touches patient PHI writes an audit entry with \`action="phi_access"\`. This covers:
- Opening a patient profile (Admin → Users → click row)
- Opening a patient chart (Prescriber Dashboard → patient)
- Viewing a patient's clinical notes, documents, or wellness logs
- Running the patient questionnaire export

**What the patient sees:**
Every patient has a "Who has accessed my record?" card on their profile showing the last 50 staff views — role, resource viewed, timestamp, and any reason you entered. Assume every click is visible to them.

**What this means for you:**
- Click only on the patients you genuinely need to review.
- Don't "browse" profiles — browsing without a clinical reason is a HIPAA minimum-necessary violation.
- If you accidentally open the wrong patient, close immediately and note it in a clinical note so the reason is captured.

**What patients can download (HIPAA § 164.524):**
Patients can one-click download a ZIP of their entire record — profile, orders, questionnaires, wellness logs, document metadata, and the full access log. This includes every view you've made of their record.`,
  },
  {
    id: "hipaa-reveal-gate",
    category: "compliance",
    title: "Minimum-Necessary Reveal Gate (DOB / SSN / Address / Phone)",
    audience: "all",
    body: `**Why fields are masked by default:**
DOB, SSN last-4, full address, and phone number are hidden on patient detail views by default. This enforces HIPAA's "minimum necessary" rule — you see identifiers only when you have a clinical or operational reason.

**To reveal a field:**
1. Click the small **reveal** link next to the masked value.
2. A modal asks for a short reason (3+ characters). Examples:
   - *"Verifying identity for refill approval"*
   - *"Pharmacy returned call, need to confirm SSN last-4"*
   - *"Patient requested address change — confirming old address"*
3. Click **Reveal + log reason**.
4. The value appears inline with a **hide** button to re-mask.

**What's logged:**
- \`action="phi_reveal"\` with your user, role, the specific field, timestamp, and the reason **verbatim**.
- The reason is visible to the patient on their "Who accessed my record?" page.

**Pitfalls:**
- "Looked curious" or "test" or placeholder reasons will flag you in the next audit log review. Use real, specific justifications.
- Never screenshot or paste revealed PHI outside the GLPFlo portal.
- If you accidentally reveal a field, click **hide** immediately — but the log entry cannot be undone.`,
  },

  // -------- FIT QUIZ + FUNNEL ANALYTICS --------
  {
    id: "marketing-fit-quiz",
    category: "admin-marketing",
    title: "Fit Quiz Funnel — what the numbers mean",
    audience: "admin",
    body: `**Where it lives:**
Admin Dashboard → **Loyalty & Marketing** tab → *Fit Quiz — funnel analytics* card.

**The 4 KPI tiles:**
1. **Quizzes taken** — total quiz completions in the selected window (7 / 30 / 90 days).
2. **Emails captured** — count + % of quizzes whose visitor opted in for emailed results. Healthy benchmark: 20–35%. Below 10% means the quiz is too scary or the email-opt-in is too pushy.
3. **Great / Good fit** — count + % of quizzes that produced a favorable fit tier. Low % means we're attracting too many BMI <27 visitors — revisit acquisition channels.
4. **2-wk safety gate** — how many quizzes were **forced to Low dose** because the visitor hadn't taken a GLP-1 in >2 weeks. Important for clinical defensibility; a high number is GOOD (shows we enforce titration).

**Viral loop strip:**
The gradient pink/purple strip below the KPIs shows *Shared-projection signups* — signups who arrived via the "Share my projection" button on someone else's quiz. This is true word-of-mouth attribution: multiply these signups' LTV by 1.3 to account for their higher organic retention.

**Fit distribution bar:**
Horizontal stacked bar showing great / good / borderline / not_ideal mix. Target: ≥60% great+good for healthy top-of-funnel. If borderline + not_ideal climb above 40%, our acquisition is reaching too many non-qualifying visitors.

**Top recommended plans:**
Which dose tier the algorithm suggested most. Use this to forecast inventory demand.

**Dose-reason breakdown:**
- **Continuation** — patient attested previous dose within 2 weeks (staying on current dose)
- **Algorithmic** — BMI + goal-based pick
- **New user (forced Low)** — first-time GLP-1 patient
- **Restart after >2 wk gap (forced Low)** — returning patient whose tolerance reset

**Recent submissions table:**
Last 20 quizzes with BMI, fit tier, recommended plan, reason, and email (if captured). Exportable to CSV via browser Print → Save as PDF for contest or board reports.`,
  },
  {
    id: "marketing-share-attribution",
    category: "admin-marketing",
    title: "Shared-Projection Attribution Tracking",
    audience: "admin",
    body: `**How it works:**
When a quiz-taker hits the "Share my projection" button, the share link contains \`?src=shared_projection\`. Any visitor who arrives at /register via that link gets their \`share_src\` tagged on signup. The metric shows up in the Fit Quiz funnel card (viral loop strip).

**Bigger picture:**
This is the single cleanest top-of-funnel attribution signal we have. A patient who converts via shared-projection has self-selected:
- Saw a peer (friend/spouse/colleague) take the quiz and share
- Saw real clinical-trial numbers on a real body weight
- Arrived with high intent + social proof

Treat these signups like referrals in your retention playbook. The LTV uplift is typically 20–40%.

**Reusing the \`?src=\` tag for campaigns:**
Any paid-ad or email link can use \`?src={tag}\` and it'll show up in the admin \`all_tracked_signups\` count. Examples: \`?src=google_ads_jan\`, \`?src=podcast_tim_ferriss\`. If you want these broken out individually, ping the eng team for a dashboard extension.`,
  },

  // -------- PATIENT ACCOUNT NUMBER --------
  {
    id: "cs-patient-account-number",
    category: "faq",
    title: "Patient Account Numbers (GL######)",
    audience: "all",
    body: `Every patient is assigned a short, human-friendly account number at registration: **GL######** (e.g. \`GL000001\`).

**Where patients see it**
- Top of the **Patient Dashboard** as an emerald chip: \`Account GL000003 · since Feb 2026\` — right next to "Welcome back, {First Name}".
- Hover tooltip: *"Your GLPFlo account number — share this with support for faster help"*.

**Where staff see it**
- **Admin → Users** search and **Customer Service → Patient lookup** both accept the account number as a search query. Just type \`GL000003\` instead of (or in addition to) a name or email.
- It also appears on the receipt PDF and the order confirmation email so a patient can copy it from there.

**When to use it**
- A patient calls in support and can't remember their email or you don't want to spell it back over the phone? Ask for their account number — it's six digits, unambiguous, and unique tenant-wide.
- Always preferred over asking for a date of birth or last 4 of phone in your opening message (avoids unnecessary PHI exposure).

**Important**
- The account number is **public to the patient** — it's printed on receipts and shown in their dashboard. Treating it as a customer reference number, NOT as authentication. Always still verify identity before disclosing PHI (DOB + at least one other factor).`,
  },


  // -------- PATIENT PAUSE NOTICE --------
  {
    id: "cs-patient-paused-ticket",
    category: "faq",
    title: "Handling patient tickets during an order pause",
    audience: "all",
    body: `**When the pause is active and a patient writes in:**
1. Acknowledge the pause exists ("Yes, we're temporarily pausing new orders while our partner pharmacy restocks — you'll see a banner across the app").
2. Confirm the expected resume time from the message in System Ops.
3. Reassure: *"Your existing subscription is unaffected. Shipments already in progress will arrive on their normal schedule."*
4. Do NOT try to create a custom order on their behalf to "bypass" the pause — this defeats the purpose and may cause LabFlo inventory issues.

**If a patient insists on placing an order right now:**
- Escalate to super-admin. Only the admin who paused can make a judgement call to resume early or handle a one-off.

**When the pause is lifted:**
- Outbound apology email to anyone who tried to order during the pause window is automatic (generated from the audit log of 503 responses). No action needed.`,
  },

  // -------- HIPAA Phase 3 (Archival + Patient Data Export) --------
  {
    id: "hipaa-phase3-archival",
    category: "compliance",
    title: "HIPAA Phase 3 — Audit Archival + Patient Data Export",
    audience: "admin",
    body: `**What changed:**
We now automatically archive old audit entries and offer patients a self-service HIPAA § 164.524 data export.

**Audit archival (nightly at 03:30 UTC):**
- Entries older than \`AUDIT_ARCHIVE_AFTER_DAYS\` (default 365) move from \`audit_logs\` → \`audit_logs_archive\`.
- Entries past \`AUDIT_RETENTION_DAYS\` (default 2190 = 6 years, HIPAA minimum) are purged.
- The HMAC chain (\`prev_hash\` / \`entry_hash\`) is **preserved verbatim** in the archive so you can still verify integrity across both collections.

**Endpoints you'll use:**
- \`GET /api/compliance/audit-archive-stats\` — shows hot/archive counts, cutoff days.
- \`POST /api/compliance/audit-archive-now\` — manual trigger (admin-only). Idempotent: a 2nd call right after returns \`archived=0\`.

**Patient data export (§ 164.524 right of access):**
- Endpoint: \`GET /api/compliance/my-data\` (patient-only).
- Streams a ZIP: \`profile.json\` (scrubbed of password/MFA/Stripe IDs), \`orders.json\`, \`questionnaires.json\`, \`wellness_logs.json\`, \`documents.json\`, \`access_log.json\`, \`README.txt\`.
- Every export writes an \`action="data_export"\` audit entry so you see it on retroactive reviews.

**Pitfall:** Patients asking "I want a copy of my chart" — direct them to the "Download my data" button in the Patient Help Guide, don't manually extract.`,
  },

  // -------- HIPAA Phase 4 (SIEM + Break-Glass + Suspicious Activity + URL Trail) --------
  {
    id: "hipaa-phase4-polish",
    category: "compliance",
    title: "HIPAA Phase 4 — SIEM, Break-Glass, Suspicious Activity, URL Trail",
    audience: "admin",
    body: `**Four independent controls — all live in production:**

**1. Nightly SIEM batch export (04:00 UTC)**
- Helper: \`export_yesterdays_audit_to_siem()\` streams yesterday's audit entries as NDJSON to \`SIEM_WEBHOOK_URL\`, HMAC-SHA256 signed.
- Manual trigger: \`POST /api/compliance/siem-export-now\` (admin). Auto-skips with \`status="skipped"\` when no webhook is configured.

**2. Break-glass emergency access** *(admin + prescriber only)*
- \`POST /api/compliance/break-glass/start\` requires a reason (10–500 chars) and optional patient_id. 4-hour auto-expiring window.
- Instantly fires a red-themed SendGrid alert to every address on the compliance-recipients list.
- Only one active session per user at a time (re-start returns the existing session).
- A global sticky red banner shows across every staff screen during an active window with countdown + [End now].
- Use for: on-call coverage, ER escalations, after-hours incident investigation. **Never for routine lookups** — inappropriate use is grounds for termination.

**3. Suspicious Activity triage**
- Location: Admin Dashboard → **Reports & Health** → *Suspicious Activity* sub-tab.
- Aggregates \`new_device_signin\`, \`phi_reveal\` (high-freq), \`data_export\`, \`break_glass_started\`, \`audit_chain_anomaly\` events over the selected window.
- Per-entry actions: **Acknowledge** / **Investigated** / **Dismiss** with a 500-char triage note. Everything is itself audit-logged.

**4. Staff URL trail (PHI routes only)**
- Lightweight middleware that writes one row to a separate \`staff_url_trail\` collection for every staff GET to a matched PHI route (patient profile, eRx, documents, notes, wellness admin, receipts).
- Kept outside the HMAC-chained \`audit_logs\` to keep the chain cheap and readable.
- Forensic query: \`GET /api/compliance/url-trail?patient_id=X&staff_id=Y&days=N\` (admin).

**Weekly digest:**
Every Monday the weekly compliance snapshot email now includes an "Open suspicious-activity queue" + "Break-glass sessions this week" section so stale events get surfaced automatically.`,
  },

  // -------- Smarty HIPAA Address Verification --------
  {
    id: "ops-smarty-address",
    category: "admin-ops",
    title: "Smarty HIPAA Address Verification — HIPAA-eligible replacement for Mapbox",
    audience: "admin",
    body: `**Why this matters:**
Mapbox is **not HIPAA-eligible**. We've code-swapped to **Smarty** (smarty.com — BAA available on Cloud plans) as the primary address provider. Mapbox stays as an outage-only fallback so registration never breaks during a transient 5xx.

**How to flip it on:**
1. Sign the Smarty BAA (paid us-core-cloud plan — free trial doesn't qualify).
2. Subscribe to **us-core-cloud** + **us-autocomplete-pro-cloud** licenses.
3. Paste \`SMARTY_AUTH_ID\` and \`SMARTY_AUTH_TOKEN\` into \`backend/.env\`, restart backend.
4. Verify with \`GET /api/address/provider\`:
   - Before: \`{"provider":"mapbox","hipaa_eligible":false}\`
   - After: \`{"provider":"smarty","hipaa_eligible":true}\`

**Deliverability badge (auto-activates when Smarty goes live):**
- 🟢 **Residential** — OK to ship temperature-controlled medication.
- 🟡 **Commercial** — someone needs to be on-site Mon–Fri for refrigerated packages.
- 🔴 **PO Box — not deliverable** — UPS/FedEx don't deliver compounded GLP-1 to USPS PO Boxes, so the save is **blocked** before checkout.

**Pitfall:** Don't panic if a patient's residential address shows Commercial — sometimes home-based businesses register as commercial with USPS. The badge is a nudge, not a hard block (except for PO Box).`,
  },

  // -------- Pharmacy Partner Program --------
  {
    id: "ops-pharmacy-partners",
    category: "admin-ops",
    title: "Pharmacy Partner Program — Intake + Admin Queue",
    audience: "admin",
    body: `**Public page:** \`/pharmacy-affiliate\` — linked from the global footer and the landing-page footer.

**What happens when a pharmacy submits:**
1. \`POST /api/pharmacy-affiliate/inquiry\` validates the form (email, min-length message, rate limit 5/IP/hour + 3/email/day).
2. Inquiry stored in \`pharmacy_affiliate_inquiries\`.
3. SendGrid alert fires to \`ADMIN_EMAIL\` with the full inquiry HTML.
4. \`pharmacy_affiliate_inquiry_submitted\` audit entry written.

**Admin queue:** Admin Dashboard → **Pharmacy Partners** (top-level sidebar item, red badge shows new-inquiry count) → **Affiliate Inquiries** sub-tab.
- KPI strip (Total / New / Contacted / Signed / Declined).
- Filter pills + cards per inquiry with clickable email/phone.
- Per-row actions: **Reply** (opens prefilled mailto), **Mark contacted**, **Mark signed**, **Decline**, **Reopen**.

**Weekly digest section:**
Anything sitting in \`status="new"\` for >48h surfaces in the Monday compliance digest under "Pharmacy follow-ups overdue" so nothing gets buried.`,
  },

  // -------- Fit Quiz (drug + mg dose picker) --------
  {
    id: "marketing-fit-quiz-dose",
    category: "admin-marketing",
    title: "Fit Quiz — Prior GLP-1 Dose Capture (drug + mg)",
    audience: "admin",
    body: `**Updated intake logic:**
When a visitor says "Yes I've tried a GLP-1" AND "within the last 2 weeks," the quiz now asks:

1. **Which medication?** — Semaglutide *(Ozempic, Wegovy)* · Tirzepatide *(Mounjaro, Zepbound)*
2. **Which dose were you on?** — actual mg values:
   - **Semaglutide:** 0.25 / 0.5 / 1.0 / 1.7 / 2.4 mg
   - **Tirzepatide:** 2.5 / 5 / 7.5 / 10 / 12.5 / 15 mg

**Why this matters:**
Previously the quiz only captured "low / mid / high" — we've kept that mapping under the hood (each mg maps to a tier) so the continuation algorithm and plan deep-link stay identical. Patients now attest to the **real** dose they were on, which is required for safe continuation review by the prescriber.

**What's stored:**
- \`previous_drug\`: \`sema\` | \`tirz\`
- \`previous_dose_mg\`: string like "0.5" or "7.5"
- \`previous_dose\`: derived tier (\`low\` / \`mid\` / \`high\`) — unchanged for downstream logic.

**Pitfall:** If a patient selects a dose that doesn't match what they actually took, the intake form will re-ask and flag mismatches — don't bypass this.`,
  },

  // -------- ADMIN: PRICING TAB DEEP DIVE --------
  {
    id: "admin-pricing-margins",
    category: "admin-marketing",
    title: "Pricing tab — margin model + per-dose costs",
    audience: "admin",
    body: `**One canonical pricing surface.** Footer link **Pricing & medications** points to **/pricing** (the old **/medications-info** page now redirects there).

**How prices are derived (the 35 / 50 / 59 model):**
Each drug has a list of **dose groups** (Low / Mid / High). Each group stores a **monthly cost** — that's our wholesale cost basis. The 3 tier prices auto-compute from cost + the **Global pricing margins**:
- **Royal Tier (2-month commitment):** \`cost ÷ (1 − 0.35)\` → 35% margin
- **Bundle Subscribe:** \`cost ÷ (1 − 0.50)\` → 50% margin
- **One-time Monthly:** \`cost ÷ (1 − 0.59)\` → 59% margin
- All rounded to nearest **$1** (configurable via *Round to $* field).

**Where to edit:**
\`Loyalty & Marketing → Pricing → Global pricing margins\` card. Bump any % and the per-dose preview updates live in **amber "Preview"** mode. Click **Save margins** to apply.

**Per-dose cost is editable:**
Inside each drug row, click *Edit* and the dose-group editor lets you set the **Cost** for Low / Mid / High independently. Once cost > 0, the 3 tier prices become read-only (they're auto-derived) — set cost to 0 if you ever need a manual price override.

**Important:** Royal Tier is **2-month commitment** ("60-day supply, two fresh vials") — never call it 3-month or 90-day. The compendium and patient-facing copy both say 2-month / 60-day.`,
  },
  {
    id: "admin-pricing-forecast",
    category: "admin-marketing",
    title: "Gross-profit forecast dashboard (with what-if mode)",
    audience: "admin",
    body: `Above the pricing rows, the **Last 30-day gross-profit** card shows live financial KPIs from paid orders:

**KPIs:**
- **Revenue** = sum(\`amount\`) of paid orders bucketed by drug × tier
- **COGS** = orders × dose cost (mid-dose used as the representative basis)
- **Gross profit** = revenue − COGS
- **Effective margin %** = GP ÷ revenue × 100

**Month-over-month deltas:** Tiny coloured pills next to each KPI (▲ green = up, ▼ rose = down) compare against the **prior 30-day window**. **COGS color is inverted** — rising COGS shows red because that's bad for the business.

**What-if "Preview" mode:** If you start editing margins (the indigo card directly below this one) before clicking Save, the dashboard re-prices the same volume at your draft margins and turns **amber** with a "Preview" badge. Lets you A/B before committing.

**Expand button (eye icon):** Per-drug per-tier breakdown table — each tier row shows the tier's margin %, current/hypothetical price, orders, revenue, COGS, gross profit.

**Operational note:** The dashboard is read-only and reflects what's already happened. It can't predict cohort effects (e.g., a margin bump driving cancellations) — use it for snapshot $-impact, not behavioural forecasting.`,
  },
  {
    id: "admin-pricing-audit",
    category: "compliance",
    title: "Pricing cost audit trail — who changed what, when",
    audience: "admin",
    body: `Every time an admin edits a dose-group cost, two things happen:

1. **Inline trail** — the row stores \`cost_updated_at\` + \`cost_updated_by\`. The Pricing tab shows a tiny *"just now · GLPFlo Admin"* line under the cost cell. Hovering reveals the full UTC timestamp.
2. **HIPAA audit log** — a row is written to the \`audit_logs\` collection with action=\`pricing_cost_changed\` and details=\`"<drug> <dose> cost $X → $Y"\`. This is the same collection the **Compliance tab** queries and the **nightly SIEM webhook** exports.

**Why both surfaces?** The inline trail is for operational speed — staff glancing at a row should see who last touched it. The HIPAA log is for compliance reporting — accounting / external auditors get a full price-history they can query.

**What's NOT audited:** Top-level prices (legacy \`monthly_price\`, etc.) when changed without changing the dose-group cost. The cost is the source of truth — change it, get the audit row.

**Pitfall:** If you change a margin in the Global pricing margins card, that's not a cost edit — no audit row is written there. (Margin changes are also a less-frequent event so they're tracked separately by the \`pricing_margins\` config doc with \`updated_at\` + \`updated_by\`.)`,
  },

  // -------- ADMIN: PHARMACY PARTNERS CRUD --------
  {
    id: "admin-pharmacy-crud",
    category: "admin-ops",
    title: "Per-pharmacy CRUD (Pharmacy Partners → Pharmacies)",
    audience: "admin",
    body: `**Where:** \`Pharmacy Partners → Pharmacies\` sub-tab.

**What it manages:** One row per compounding pharmacy partner. Polomar Specialty Pharmacy is auto-seeded as the first record (so the patient-facing **/pharmacy-info** page is never blank).

**Fields per pharmacy:**
- Name + slug (URL-safe, auto-generated from name) + tagline + bio
- Address line 1, city, state, ZIP, phone, website
- License # / state / expiration
- Capabilities — free-form chips (e.g., "503A sterile compounding")
- Logo upload — uploads to object storage, served at \`/api/pharmacies/{id}/logo\`
- Position (sort order) + Active toggle (Hide/Show on the public page)
- Documents — upload Certificates of Analysis, drug study sheets, etc.

**Public surface:** The patient page **/pharmacy-info** renders one card per *active* pharmacy with the capabilities chips, license badge, logo, and downloadable documents.

**Pitfall:** Deleting a pharmacy ALSO cascade-deletes all its uploaded documents from the \`pharmacy_documents\` collection. There's a confirm dialog — pay attention.

**Legacy docs:** The old \`Pharmacy Partners → Legacy Docs\` sub-tab still exists for any pharmacy_info_docs that haven't been migrated to the per-pharmacy model. Patients see those under "Additional pharmacy resources" at the bottom of /pharmacy-info.`,
  },

  // -------- COMPLIANCE: ANNUAL TRAINING CERT --------
  {
    id: "compliance-training-cert",
    category: "compliance",
    title: "Annual HIPAA training — your Certificate of Completion",
    audience: "all",
    body: `**HIPAA § 164.530(b) requirement.** Every workforce member must be trained on PHI handling and refreshed annually. That's what this manual is.

**Your progress card** (top of the Training Manual):
- Live progress bar shows X / N sections marked read
- Click **"I've read this"** at the bottom of each expanded section to mark it
- At 100% the **Get certificate** button activates — clicking it generates a PDF Certificate of Completion (your name + role + date + GLPFlo seal + 365-day expiry)
- Your cert PDF can be reprinted any time via the **My certificate** button

**Annual expiry (365 days):** The certificate is valid for one year. After that, the admin Training Status roster flags you as "Expiring soon" (≤30d) or "Expired" — you'll be expected to re-read the manual and regenerate the cert.

**Admin roster** (\`Team → Training Status\`):
- Lists every active staff member with status: **Current** · **Expiring ≤30d** · **Expired** · **Not started**
- Sections-read count per person
- Last completion date + next expiry
- Reprint-cert button (admins can pull anyone's PDF for HR / audit files)

**Pitfall:** Don't speed-click to the bottom — the read marks persist per-section, and an audit could ask "did you actually read the de-identification section?" Take the time.`,
  },

  // -------- ADMIN MARKETING: PROMOTIONAL DEALS --------
  {
    id: "admin-promo-deals",
    category: "admin-marketing",
    title: "Switch & Save + New Patient Deal — admin controls",
    audience: "admin",
    body: `Two patient-facing promotional deals, both editable in **Loyalty & Coupons → Loyalty rules**:

**Switch & Save** — for continuing GLP-1 patients
- $20 off the first paid order (default, configurable)
- **Stackable** with Florida residency discount + Butterfly Easter Egg
- Auto-applied when a visitor lands at \`/register?promo=switch-save\` — the purple card on /pricing pre-builds that link
- Backend mints a coupon row with \`source: switch_save\`, \`first_order_only: true\` at register time
- Admin fields: dollar amount · social-proof baseline · Active toggle

**New Patient Deal** — for first-time GLPFlo patients
- $150 flat one-time price (configurable)
- Single-vial, minimum-dose, **non-subscription** order
- Drug picker (Semaglutide 0.25mg or Tirzepatide 2.5mg starter)
- Patient must check the attestation box before checkout
- Backend gates: zero prior paid orders + valid drug + attestation true
- Admin fields: flat price · social-proof baseline · Active toggle

**Social-proof badges:**
Both promo cards on /pricing show a tiny green-pulse badge ("X patients started with this deal in the last 30 days" / "X patients switched from a competitor this week"). Numbers are real counts + an admin-editable **baseline** so the badge isn't empty during cold-start. Set baselines to 0 to show only real data, or bump them when you launch a campaign.

**Florida-only ribbon:**
The Switch & Save card auto-shows a yellow "⚡ Popular with Florida patients" ribbon when the visitor's IP geolocates to FL. Backend uses ipapi.co with 24h cache, no PII stored. GLPFlo currently ships to Florida only, so the ribbon is essentially always on for eligible traffic.

**Referral coupon stackability** (recap, since it diverged):
- Referrer (existing patient) → \`stackable: true\` (stacks with FL + sub discounts)
- Referred (new patient) → \`stackable: false\` (replaces FL, but Butterfly still stacks)`,
  },
  {
    id: "admin-banners-99",
    category: "admin-marketing",
    title: "$99 first-month banner — removed",
    audience: "admin",
    body: `The legacy *"First Month Only $99 - Limited Time Offer!"* green banner was removed in the production cutover.

**What was done:**
- Seed entry \`banner-welcome\` deleted from the startup seed
- Startup also runs a one-time regex purge against any existing banner row matching "$99" + "first" + "month" so production DBs clean up on next deploy
- Admin banner form placeholder is now "Switch & Save — $20 off your first order"

**If you need a new green banner:** Use the **Banners** tab — set a new active rolling banner with whatever current promo copy makes sense. The Switch & Save / New Patient Deal hero cards on /pricing handle the heavy lifting now.`,
  },
  {
    id: "admin-search-panel",
    category: "admin-ops",
    title: "Admin Search Panel — server-side search across 9 admin tabs",
    audience: "admin",
    body: `Every high-volume admin list now has the **same** power-search bar at the top. It works on **Orders, Users, Audit Logs, Error Reports, Loyalty Coupons, Referrals, Tracking Queue, LabFlo Webhooks, and Pharmacy Documents**.

**Anatomy of the panel:**
- **Search input** — free-text, debounced 300ms. Searches across every relevant field for that tab (e.g. Orders → patient name, email, DOB, address, medication, order ID).
- **Filters button** — opens a chip grid with structured filters (status dropdowns, date range, US state, has-eRx, payment status, etc.). The badge shows how many filters are active.
- **Saved filters dropdown** — pick a previously saved search to re-apply with one click.
- **Save button** — appears once you've typed something or applied a filter. Stash this exact search for future re-use.
- **Clear button** — wipes search + filters and returns the tab to its initial state.
- **"Showing X of Y" counter** — accurate result count even when paginated (uses backend \`X-Total-Count\` response header).

**Per-tab filter highlights:**
- **Orders** — status, payment, days, state, has eRx (yes/no), refund pending
- **Users** — role, active/inactive
- **Audit Logs** — action type, time range
- **Error Reports** — time range (state pills control open/ack/resolved)
- **Coupons** — type, status (active/redeemed/expired)
- **Referrals** — status
- **Tracking** — status
- **Webhooks** — new_status
- **Documents** — text-only

> Server-side search means the panel scales to 10,000+ rows without slowing your browser. The backend filters in MongoDB before the data ever crosses the wire.

**Pro tip:** Type "florida" in Orders search — it matches via the patient address enrichment even though "florida" isn't on the order document itself.`,
  },
  {
    id: "admin-saved-filters",
    category: "admin-ops",
    title: "Saved filters — private named searches per admin",
    audience: "admin",
    body: `**Saved filters are private to you** — your saved "Open FL last 7d" is invisible to other admins, and theirs are invisible to you. There is no team-sharing (by design — keeps personal workflows from cluttering everyone else's dropdown).

**How to use:**
1. On any of the 9 search-enabled tabs, type a query or set filters.
2. Click **Save**, name it (max 60 chars, e.g. *"Open · FL · L7d"*), submit.
3. The filter appears in the **Saved filters** dropdown above the search input + as a chip below.
4. Click the chip name (or pick from the dropdown) to instantly re-apply.
5. Click the **trash icon** on the chip to delete.

**Constraints:**
- Names must be unique **within (your account, scope)** — same name twice on the same tab → 409 error.
- Scope is locked at save time; a filter saved on Orders only shows up on Orders.
- Each filter stores **only the search payload** — not any data — so they survive backend changes.

**Where they live:** \`db.saved_filters\` keyed by your Mongo \`_id\`. Backend route prefix \`/api/admin/saved-filters\`.

**Pinning** is a separate feature — see the **Pinned filters** entry.`,
  },
  {
    id: "admin-pinned-filters",
    category: "admin-ops",
    title: "Pinned filters — your dashboard at the top of Overview",
    audience: "admin",
    body: `**Pin up to 3 of your saved filters** to the top of the **Admin Overview** tab. Each pinned card shows the saved filter's name + a **live match count** that auto-refreshes every 60 seconds. One click on a card jumps you straight into that tab with the filter pre-applied.

**Use cases:**
- *"FL Open Orders L7d"* → know at a glance how big the Florida prescriber queue is
- *"Failed Webhooks 24h"* → spike alarm for LabFlo integration breakage
- *"Pending Refunds"* → finance backlog visibility

**How to pin / unpin:**
1. Save a filter on any search-enabled tab (see the Saved filters entry).
2. In the saved-filter chip strip, click the **Pin icon** next to the name.
3. It turns amber and a "1/3 pinned to dashboard" counter appears.
4. Click the **PinOff icon** to unpin.

**Constraints:**
- **Max 3 pinned filters per admin**, total — across all 9 scopes. The 4th pin attempt returns a friendly 400 error. The UI also disables the pin button once you hit 3.
- The pin button is also private — only you can pin/unpin your own saved filters.

**How the live counts work:**
- Backend endpoint \`GET /api/admin/saved-filters/pinned\` rebuilds each filter's Mongo query and runs \`count_documents()\` for the relevant collection.
- For Orders, only fields that map to indexed DB columns count (the post-enrichment \`state\` filter is skipped server-side for speed — the actual tab still applies it on click).
- For Coupons with \`status: active\`, used+expired are properly excluded.

**Deep-link mechanics:**
- Clicking a pinned card writes the filter payload to \`sessionStorage.pinned_filter_apply:{scope}\`, then calls \`setActiveTab(destinationKey)\`.
- The destination tab's \`AdminSearchPanel\` reads that sessionStorage value on mount, applies it, then clears the key — so a refresh doesn't re-apply.

> Empty state: until you pin your first filter, the widget shows a dashed-border helper card prompting you to save + pin.`,
  },
  {
    id: "admin-cookie-consent",
    category: "compliance",
    title: "Cookie banner + Online tracking disclosure (HIPAA-tracking compliance)",
    audience: "admin",
    body: `The **CookieBanner** appears on the first visit for every device and is GDPR / CCPA / HHS Online Tracking Bulletin compliant. It strictly gates PostHog analytics and Sentry error tracking — neither tool loads until the visitor accepts.

**Banner controls (3 buttons):**
- **Accept all** → analytics + error tracking enabled, banner dismissed
- **Reject all** → only strictly-necessary cookies enabled (auth, CSRF, theme)
- **Customize** → opens a fine-grained modal letting the user toggle individual categories

**Persistence:** \`localStorage.glpflo_cookie_consent_v1\` stores the user's choice indefinitely. Wiping it (e.g. via the "Cookie settings" footer link) re-opens the banner on next load.

**Where to find the "re-open consent" link:**
- **Public footer** (every public page) — bottom-right, *Cookie settings*
- **Landing page footer** — same link inline

**\`/online-tracking\` disclosure page** lists every third-party tracking technology in plain English (PostHog product analytics, Sentry error reporting, Stripe payment processor cookies, etc.), what data is collected, retention period, and how to opt out. This page satisfies the HHS Online Tracking Bulletin requirement for any platform serving PHI.

> Don't bypass the consent gate when adding new analytics — wire any new instrumentation through the central \`useAnalytics()\` hook so it respects the user's choice.`,
  },
  {
    id: "patient-consent-flows",
    category: "compliance",
    title: "Explicit patient consent modals — telehealth + GLP-1",
    audience: "admin",
    body: `Before a patient can submit their first order, two explicit consent modals block checkout until acknowledged:

**Telehealth consent** (one-time, per patient):
- HIPAA-compliant notice that they understand this is asynchronous telemedicine
- Right to discontinue care at any time
- Risks/benefits language vetted by counsel

**GLP-1 specific informed consent** (one-time, per patient):
- Pancreatitis, gallbladder disease, thyroid C-cell tumors disclaimers
- Pregnancy / breastfeeding contraindication
- Medullary thyroid cancer / MEN-2 family history check

**Backend storage:** Both are stamped to \`db.users.consents.{telehealth, glp1}\` as ISO timestamps. Orders endpoint (\`POST /api/orders/custom\`) returns a **409 with \`{error: "consents_required", missing: [...]}\`** if any consent is missing — the patient-side checkout intercepts that and pops the modal automatically.

**Re-prompt logic:** If counsel updates the consent language (versioned by year), bump the version field and consents will re-prompt on next checkout. We won't auto-back-fill historical consents — patients must re-confirm.

**Audit log:** Every consent acceptance is written to \`db.audit_logs\` with action \`consent_accepted\` + the consent type + IP + user-agent. Export via the standard audit log CSV.`,
  },
  {
    id: "admin-baa-register",
    category: "compliance",
    title: "BAA Registry — track Business Associate Agreements",
    audience: "admin",
    body: `Find at **Admin → Compliance → BAA Register**. This tab tracks every vendor that handles PHI on our behalf (SendGrid, Twilio, Vonage, Stripe, LabFlo, MongoDB Atlas, etc.) with their signed BAA on file.

**Per-vendor fields:**
- Vendor name + contact email
- BAA effective date
- BAA renewal date (next required signature)
- Document URL (uploaded PDF or external link)
- Notes / scope of access

**Weekly compliance email digest:** Every Monday at 9 AM ET, an APScheduler job emails the compliance team a list of BAAs expiring within 60 days. Configurable via \`COMPLIANCE_ALERT_EMAILS\` in backend .env.

**HIPAA requirement:** Per 45 CFR § 164.504(e), we must have a signed BAA in place **before** any PHI is shared with a vendor. Adding a vendor that processes PHI without a logged BAA = compliance violation.

> If a vendor flat-out refuses to sign a BAA, they can't be used. No exceptions. Period.`,
  },
  {
    id: "admin-pending-refunds",
    category: "admin-ops",
    title: "Pending Refunds tab + weekly stale-refund digest",
    audience: "admin",
    body: `Found at **Admin → Operations → Pending Refunds**. Lists every order where \`refund_pending: true\` is set — typically refunds approved by a prescriber that haven't been pushed to Stripe yet.

**Per-row actions:**
- **Issue refund** → calls Stripe \`refunds.create()\` for the full or partial amount, marks the order \`refund_pending: false\` + \`refunded_at\` timestamp
- **Cancel pending** → clears the flag without touching Stripe (e.g. patient changed their mind)
- **View order** → deep-link to the full order detail

**Weekly digest email** (Tuesdays at 9 AM ET): APScheduler scans for refunds pending more than 7 days and emails the finance team a CSV. Targets: \`FINANCE_ALERT_EMAILS\` env var.

**Why this matters:** Stale refund-pending flags are an audit risk — they indicate either staff forgot to push the refund, or the prescriber-approved refund was never reviewed. Either way, finance + compliance want to know.`,
  },
  {
    id: "admin-margin-lock",
    category: "admin-marketing",
    title: "Margin Lock toggle on the Pricing tab",
    audience: "admin",
    body: `Each pricing tier on **Admin → Pricing** has a \`margin_lock\` boolean. Default = \`true\`.

**When margin_lock = true (default):**
- Tier price auto-derives from per-dose pharmacy cost × tier multiplier
- Cannot manually override the price field — it's read-only
- Protects against accidental price erosion if pharmacy bumps costs

**When margin_lock = false:**
- Direct manual price editing is enabled
- Useful for promotional pricing or special bulk deals
- The audit log records who toggled it + when

**Restoring from rollback** — If pricing data is ever wiped by a rollback, a one-time \`restore_pricing_from_git.py\` script in \`/app/scripts/\` reads the last-known-good values from Git history.

> Standard operating policy: leave margin_lock=true except during a 24-hr promotional window with finance sign-off.`,
  },
  {
    id: "admin-assignment-sla",
    category: "admin-ops",
    title: "Assignment SLA — 36-hour reassignment + 18-hour halfway warning",
    audience: "admin",
    body: `**Admin → Settings → Assignment SLA** (testid: \`assignment-sla-card\`) controls how long a prescriber can hold a claimed order before it is auto-reassigned to the open queue.

**Tunable knobs**
- \`warning_hours\` — when to write \`sla_warning_sent_at\` to the order and surface the in-app halfway-warning chip. Default **18**.
- \`reassign_hours\` — when to release the claim and bounce it back to the queue with \`assignment_source=sla_reassigned\`. Default **36**.
- \`enabled\` — kill switch to pause the sweep without losing settings.

**How it runs**
APScheduler job \`assignment_sla_sweep\` (see \`/app/backend/routes/assignment_sla.py\`) ticks every 30 minutes. Each tick:

1. Finds orders with \`assigned_to\` set whose \`assigned_at\` exceeds the warning threshold and no \`sla_warning_sent_at\`. Writes the warning timestamp.
2. Finds orders past the reassign threshold. Clears \`assigned_to\`, sets \`assignment_source=sla_reassigned\`, posts a system note to the order timeline.

**Visibility**
Reassigned orders match the \`sla_reassigned\` triage filter and surface to the top of any prescriber's Triage Mode view. The order timeline shows the full SLA chain (claim → warn → reassign).

> **Pitfall:** Don't set \`reassign_hours\` below \`warning_hours + 12\` — the warning needs enough time to be actionable. The UI enforces \`reassign_hours > warning_hours\` but doesn't enforce a minimum gap.`,
  },
  {
    id: "admin-prescriber-permissions",
    category: "admin-ops",
    title: "Prescriber tier permissions (allowed_tiers) — who can claim what",
    audience: "admin",
    body: `**Admin → Users → Prescriber Permissions** card (testid: \`prescriber-permissions-card\`) lets you scope which medication tiers a non-MD prescriber is allowed to claim and write eRx for.

**How it works**
- Each non-MD prescriber (NP / PA / ND) has an \`allowed_tiers: string[]\` field on their user record.
- The **Claim** button is hidden on the prescriber's queue for any row whose medication tier is not in their \`allowed_tiers\`.
- Server endpoints (\`POST /orders/<id>/claim\`, \`PUT /orders/<id>/action\`) double-enforce — a tampered client request returns 403.
- MDs / DOs / admins are unrestricted — the field is ignored for them.

**Per-prescriber commission overrides** (testid: \`per-prescriber-commission-card\`)
Live next to tier permissions. Lets you set a flat per-order commission rate for an individual that overrides the tenant default. Useful for top performers or hardship cases.

> **Pitfall:** Removing a tier from \`allowed_tiers\` does NOT retroactively unassign already-claimed orders. The prescriber finishes what they've claimed; the restriction only applies to **new** claims.`,
  },
  {
    id: "admin-commission-supervisor-splits",
    category: "admin-ops",
    title: "Supervisor splits — paying the supervising MD on NP/PA orders",
    audience: "admin",
    body: `When a non-MD prescriber approves an order, the system can automatically create a paired commission record for their **supervising MD** at a configured percentage.

**Setup (on the prescriber's user record)**
- \`supervisor_id\` — \`_id\` of the supervising prescriber user.
- \`supervisor_split_pct\` — float 0–1 (e.g. 0.15 = 15 %). The supervisor receives \`split_pct\` × the original per-order amount; the practising NP/PA keeps the full base amount **on top** of that (the split is additive, not a deduction).

**Where it appears**
- The supervisor's **My Commission This Month** widget shows split rows tagged with the originating practitioner's name.
- Monthly payroll preview separates "Earned (claims)" from "Earned (supervisor split)" so 1099s can be cut cleanly.

**Refill routing**
Refills route back to the **previous prescriber on file**. If they're inactive, the refill falls through to the queue. The supervisor split (if any) re-applies based on whoever ends up writing the refill — not the original prescriber's supervisor.

> See \`/app/backend/routes/commission.py\` for the canonical calculation. Snapshots are taken at approval time, so changing the split percentage today does **not** rewrite yesterday's commissions.`,
  },
  {
    id: "admin-vonage-click-to-call",
    category: "admin-ops",
    title: "Vonage click-to-call — CRM-style phone integration (stub mode)",
    audience: "admin",
    body: `**Click-to-Call** buttons appear in several admin surfaces:
- **Staff Chat** — next to every patient's name
- **Prescriber patient lists**
- **Admin Orders tab** — compact phone icon next to the patient name

**Click → POST \`/api/voice/click-to-call\`** which:
1. Initiates an outbound Vonage call to the staff's registered phone
2. When answered, bridges through to the patient's number
3. Logs the call attempt + connect time + duration to \`db.call_logs\`

**Current status: STUB MODE** — the backend returns a fake 200 response until real Vonage API keys are provisioned. UI still works; calls just don't dial out.

**To take Vonage live:**
1. User signs Vonage BAA (logged in BAA Register).
2. Drop \`VONAGE_API_KEY\` + \`VONAGE_API_SECRET\` + \`VONAGE_APPLICATION_ID\` + \`VONAGE_PRIVATE_KEY_PATH\` into backend .env.
3. \`/app/backend/services/voice.py\` auto-detects the keys and flips to live mode.

> Call audio is **not** recorded by default (HIPAA-sensitive). If recording is later desired, double-check the patient consent UX first.`,
  },
  {
    id: "admin-testimonials",
    category: "admin-marketing",
    title: "Testimonials gallery (/results) + Admin Testimonials tab",
    audience: "admin",
    body: `**Public-facing**: \`/results\` is a paginated gallery of published patient testimonials with before/after photos (if shared), star ratings, weight lost, weeks on program, and medication. It auto-pulls from \`db.testimonials\` where \`published: true\`.

**Admin tab: Loyalty & Marketing → Testimonials.** From here:
- **Manually upload** a testimonial (paste rating, headline, body, optional photo URLs, set display name preference)
- **Approve / unapprove** patient-submitted testimonials (queue of \`published: false, release_signed: true\`)
- **Edit** any field, including display name (Full / First-initial / Anonymous)
- **Delete** outright

**Required for publishing:**
- \`release_signed: true\` (patient signed off the release language)
- Manual approval flip to \`published: true\`

> All testimonials need a release-on-file before they go live. Even staff-uploaded ones — paste the release from email or have the patient initial a digital form.`,
  },
  {
    id: "admin-star-testimonial-automation",
    category: "admin-marketing",
    title: "Star Testimonial Request automation — milestone-triggered review collection",
    audience: "admin",
    body: `An APScheduler weekly job runs **Tuesdays at 10:00 UTC** to find patients hitting key milestones and email them a tokenized "share your story" link.

**Milestone triggers:**
- 20+ lbs lost (cumulative since starting)
- 12+ weeks since their first paid/approved order

**The flow:**
1. Job queries \`db.users\` for milestone hits (with the milestone tag) and \`testimonial_submitted_at\` not already set.
2. Generates an HMAC-bound one-time token: \`/share-your-story/:userId/:token\`
3. Sends a templated email via SendGrid (if key present; otherwise no-op).
4. Patient opens the link, fills rating + headline + body + optional photo + display-name choice + release consent box.
5. Submission creates a \`db.testimonials\` row with \`published: false, release_signed: true, source: "patient_submitted"\`.
6. Admin reviews + publishes via the **Testimonials** tab (see related entry).

**Tokens are one-use:**
- Valid for 60 days
- Marked consumed on submission — re-visit shows "already submitted"
- HMAC validation prevents tampered token IDs

**Failure modes (handled):**
- Patient already submitted → 409 on POST
- Missing release consent → 400
- Body too short (<20 chars) → 422

> When SendGrid is live (key provisioned), this becomes a **passive marketing engine** — testimonials trickle in automatically every Tuesday.`,
  },
  {
    id: "admin-fact-myth-reviews-physicians-reorder",
    category: "admin-marketing",
    title: "Reorder controls on Fact-or-Myth, Reviews, Physicians",
    audience: "admin",
    body: `In addition to **FAQ** (which has always had reorder controls), three more admin tabs now let you drag the order of items as they appear on the public landing/marketing pages:

- **Fact-or-Myth** (the landing carousel)
- **Reviews** (the homepage testimonial strip)
- **Physicians** (the \`/our-physicians\` page)

**How:**
- Each item has up / down arrow buttons in its row.
- Click up/down → backend swaps the \`position\` field with the neighbor via two parallel \`PUT\` calls.
- Public list endpoints (\`/api/fact-or-myth\`, \`/api/reviews\`, \`/api/physicians\`) now sort by \`position ASC, created_at DESC\` so the new order is immediately visible to visitors.

**Auto-positioning:** Adding a new item via the admin form assigns the next-available position so it lands at the end of the list. You can manually re-rank afterward.`,
  },
  {
    id: "admin-orders-search-search-by-dob",
    category: "admin-ops",
    title: "Orders search — DOB and address are now searchable",
    audience: "admin",
    body: `The Orders search bar (Admin → Orders) now matches not just \`patient_name\` and \`patient_email\` but also **\`patient_dob\`** and **\`patient_address\`** — fields that aren't directly on the order document.

**How it works under the hood:**
1. \`GET /api/orders\` first runs the primary MongoDB query (name/email/medication/order-id/erx-id regex \`$or\`).
2. The backend then does a **batch enrichment** lookup: gathers all returned orders' \`patient_id\`s, fetches \`date_of_birth\` + address fields from \`db.users\` in a single grouped query, and stamps them onto each order.
3. For free-text searches that don't match name/email, a **secondary post-enrichment pass** rescans up to \`limit * 4\` extra orders' patients for DOB/address regex hits and merges them in.

**Performance:** Single grouped \`$in\` lookup keyed by patient_id — no N+1 query storm even with 200 orders × 200 unique patients.

**Examples that now work:**
- Search "1985" → all orders for patients born in 1985
- Search "Tampa" → all orders shipping to a Tampa address
- Search "33601" → all orders in that ZIP

> Result list also shows DOB + address inline in the patient column for easy visual scanning.`,
  },
  {
    id: "admin-users-search",
    category: "admin-ops",
    title: "Users tab search — name, email, employee ID, phone",
    audience: "admin",
    body: `Admin → Users tab now has a server-side search bar that filters across:
- \`name\` (case-insensitive regex)
- \`email\`
- \`employee_id\` (e.g. GF000001)
- \`phone\` (matches even partial digits)

Combined with the existing role pill bar (All / Patient / Prescriber / Admin / Team Member / Tech Support) + Active/Inactive filter from the search-panel chips.

**Use cases:**
- Find a patient by partial phone number: "555-0123" matches +1 (415) 555-0123
- Find all staff in a department: type a partial \`employee_id\` prefix
- Locate by email domain: "@glpflo.org" returns all staff

\`X-Total-Count\` response header drives the "Showing X of Y" counter.`,
  },
  {
    id: "admin-medications-no-pricing",
    category: "admin-ops",
    title: "Medications tab — pricing intentionally moved out",
    audience: "admin",
    body: `**Admin → Inventory → Medications** intentionally does NOT show prices in the table or the Add/Edit form anymore. A purple info banner at the top calls this out: *"Pricing is managed in the Pricing tab — not here."*

**Why:** The Pricing tab uses the live margin-locked tier math (Single vial / Monthly / Royal) — having a "price" field on the medication record would either be silently ignored or quietly override that math, creating support headaches.

**What stays on the Medication record:**
- Name, type (GLP-1 / Peptide / Other)
- Category (glp1 / peptide / other)
- Description, dosage instructions
- Strengths (comma-separated list shown as pill badges to patients)
- Active flag (visibility to patients)

**Where to set prices:** Admin → Pricing tab → adjust per-tier values with the Margin Lock toggle.

> This separation also matches the public flow — the new \`/browse-medications\` page lists medications without any price, then funnels to \`/pricing\` for the price disclosure.`,
  },
  {
    id: "admin-packaging-tab",
    category: "admin-marketing",
    title: "Packaging Assets tab — downloadable dieline + spec + mockup",
    audience: "admin",
    body: `Find at **Admin → Loyalty & Marketing → Packaging**. Lists every packaging asset published by engineering for the **Royal Tier** vial box:

- **\`royal-box-mockup.png\`** — photorealistic hero shot for marketing decks
- **\`royal-box-dieline.pdf\`** — print-ready 1:1 dieline for the folding-carton printer
- **\`royal-box-dieline.svg\`** — editable vector source (Illustrator / Figma)
- **\`royal-box-dieline-preview.png\`** — raster preview for screen viewing
- **\`royal-box-spec.md\`** — full engineering + print spec: BOM, dimensions, Pantone codes, finishes, cost estimates at 500/5k/25k qty

**Security:**
- Admin/super_admin role only (403 for everyone else)
- Whitelisted filenames (path-traversal hardened — \`../etc/passwd\` returns 404)
- Every download is audit-logged with the staff member's name + IP

**Workflow:**
1. Download the PDF + spec.md.
2. Send to a folding-carton printer (PakFactory / Packlane / BoxUp / Sticker Mule).
3. Send the spec to a plastic thermoformer (Dordan / Plastic Ingenuity / Profile Plastics) for the V-cradle insert.
4. Vendor returns a digital proof → approve → tool + run.

**Current design (v2):** Basic printed Reverse Tuck End (RTE) folding carton — no foil, no velvet, no magnet, full-color CMYK on all 6 faces. Universal V-cradle insert fits BOTH 16mm and 22mm vials via tangent contact.`,
  },
  {
    id: "admin-public-browse-medications",
    category: "admin-marketing",
    title: "Public /browse-medications page (no auth, no prices)",
    audience: "admin",
    body: `The footer "Browse medications" link on the landing page now points to **\`/browse-medications\`** — a public catalog that requires NO sign-in and shows NO prices.

**Why this exists:** Previously the link routed to the auth-gated checkout flow, which kicked anonymous visitors to /login before they could even see what we offer.

**What's on the page:**
- All medications where \`active: true\`
- Each card: name, type pill, description, dosage instructions, strength badges
- NO price disclosure (that's the dedicated \`/pricing\` page's job)
- CTA at the bottom: *"Start the quiz"* → routes to register / quiz funnel

**Content management:** Auto-syncs with whatever you set in **Admin → Medications**. Add a medication there → it appears here. Set \`active: false\` → it disappears.

**SEO benefit:** Public page = Google-indexable. Bot-friendly meta tags + structured data are wired in.`,
  },
  {
    id: "admin-scrollable-lists",
    category: "admin-ops",
    title: "Scrollable height-locked lists across admin (max-h 600px)",
    audience: "admin",
    body: `Every long-list admin table now has a fixed-height (~600px) scrollable container with a **sticky table header**. The page stops growing vertically once the list overflows that box — scroll inside the box to see more rows.

**Where this is applied:**
- Coupons + Referrals (Loyalty & Marketing)
- Orders, Users, Audit Logs, Error Reports
- Medications, FAQ, Fact-or-Myth, Reviews, Physicians
- Tracking Queue, LabFlo Webhooks, Pharmacy Documents

**Why pixel-height (not "limit to 120 rows"):**
- Predictable visual layout across screen sizes
- No pagination drudgery for small datasets
- Sticky header means columns are always visible while scrolling
- Inner scroll keeps the tab nav + page chrome stable above

**Testids:** All scrollable containers have a \`*-scroll-container\` data-testid for QA + screenshot tests.`,
  },
  {
    id: "admin-email-domain-sweep",
    category: "compliance",
    title: "Email contacts — production uses .org, never .com",
    audience: "admin",
    body: `As of the production cutover, **every email address in app code, seed data, and templates uses the \`.org\` TLD** — \`prescribing@glpflo.org\`, \`care@glpflo.org\`, \`support@glpflo.org\`, etc.

**The \`.com\` TLD is no longer ours.** If you see an outbound email or marketing copy with \`@glpflo.com\`, it's a regression — please flag.

**Catch points:**
- New email templates (Email Templates tab) → double-check the From/Reply-To
- Seed scripts → only fixed addresses are \`*.org\`
- Backend services that send email (SendGrid templates, refill reminders, compliance digests) — all wired to \`*.org\` From addresses

**Why this matters:** Patients see the From address. Two domains creates trust issues + DKIM/SPF/DMARC misalignment risks.

> Verified by a code-wide grep on the production cutover (zero \`@glpflo.com\` references in app source).`,
  },

  // ============================================================
  // RECENT UPDATES — appended chronologically as features ship.
  {
    id: "ru-postcard-mailer-v2",
    required: false,
    category: "admin-marketing",
    title: "Postcard mailer v2 — editorial redesign (added 2026-02-19)",
    audience: "admin",
    body: `**v2 replaces v1** (the cartoonish emoji-tile version is archived under \`/app/marketing/archive/v1-cartoonish-20260530/\`). Editorial, medical-trust look — white-dominant with purple accent, geometric stroke icons instead of emoji.

**Files:** \`/app/marketing/postcard_front.svg\` + \`postcard_back.svg\` + \`POSTCARD_README.md\` (plus PNG previews). All 5 mirrored to \`/app/frontend/public/marketing/\` for live URLs.

**Front delivers all the requested copy:**
- Brand bar + "Telehealth · GLP-1 weight management · compounded semaglutide & tirzepatide"
- Headline "Lose the weight. Keep your life."
- Sub "Prescription compounded semaglutide and tirzepatide, delivered straight to your door from a U.S. telehealth team."
- **Editable price line** "STARTER SEMAGLUTIDE & TIRZEPATIDE · As low as $100 / month" — text node id \`price-amount\` (inside \`price-line\` group) is the single edit point.
- Trust row of 5 stroke-icon items: U.S.-licensed clinician · telehealth-to-door · 100% U.S.-based customer support · free Companion App (Rewards + shot reminders + body tracking, no subscription needed) · open pricing / no hidden fees.
- "What arrives at your door" panel listing: medication · syringes + alcohol pads · medication info pamphlet · re-usable cold foam pack.
- Footer: \`glpflo.org\` · \`(888) 774-3320\` · \`hello@glpflo.org\`.

**Back delivers:**
- "No surprise fees. No middle-men. No noise." editorial headline
- 3-column value proof (U.S. clinicians · U.S. support · free Companion App — install with no subscription)
- ROYAL50 $50-off promo band
- Contact strip
- USPS-compliant bottom half (return address · indicia · PRESORTED FIRST-CLASS endorsement · recipient block · IMb barcode reserved zone)

**To edit prices in the future**: Affinity → Layers → search \`price-amount\` → edit the visible "As low as $100" string. The "/ month" unit is a separate tspan so changing the dollar amount won't shift layout.`,
  },
  {
    id: "ru-postcard-mailer",
    required: false,
    category: "admin-marketing",
    title: "Postcard mailer v1 (archived 2026-02-19)",
    audience: "admin",
    body: `**v1 archived** — was the cartoonish emoji-tile version. Replaced by v2 (see above). Source lives at \`/app/marketing/archive/v1-cartoonish-20260530/\` if you want to compare or revert. v2 is the active artwork for all new print runs.`,
  },

  // Newest at the top of this block. Each entry is fully self-
  // contained so staff can search and find the most current
  // behaviour without cross-referencing older sections.
  // ============================================================
  {
    id: "ru-shipping-waitlist",
    required: false,
    category: "admin-marketing",
    title: "Shipping waitlist + auto-launch-day emails (added 2026-02-19)",
    audience: "admin",
    body: `When a patient lands on the checkout page from a state we don't currently ship to, the red **ShippingCoverageBadge** banner embeds an inline **"Notify me when {ST} ships"** form. Submissions go to \`POST /api/pharmacies/shipping/waitlist\` (public, no auth needed) and upsert into \`db.state_waitlist\` keyed on \`(email, state)\` — idempotent, anonymous-friendly.

**Auto-launch-day pipeline:**
- Every \`PUT /api/admin/pharmacies/{id}\` snapshots the global \`ships_to_states\` union BEFORE and AFTER the save.
- For every newly-covered state in the diff, the backend calls \`notify_state_waitlist(state)\`, which finds waitlist rows for that state where \`notified_at\` is unset, sends each one a SendGrid email, and stamps \`notified_at\` so re-saves never double-send.

**Where admins see it:**
- **Admin → Overview → "Top waitlisted states" tile** (top of page, self-hides when zero pending). Ranks the top 5 states by pending signups + click-throughs to **Pharmacy Partners**.
- **\`GET /api/admin/shipping-waitlist\`** returns \`{ rows, by_state, total }\` for ad-hoc analysis.
- **Manual blast**: \`POST /api/admin/shipping-waitlist/notify-state/{ST}\` re-runs the notify pass on demand.

**Patient UX:**
- Logged-in patients see email auto-prefilled from \`/auth/me\`.
- Anonymous visitors can still join — no account required.
- After signup, form is replaced by a green pill: *"You're on the list — we'll email {email} when {ST} ships."*

> Never delete waitlist rows directly — \`notified_at\` is the audit trail.`,
  },
  {
    id: "ru-pharmacy-state-shipping",
    required: false,
    category: "admin-ops",
    title: "Pharmacy Partner state shipping allow-list (added 2026-02-19)",
    audience: "admin",
    body: `Pharmacy partners now carry an explicit \`ships_to_states\` array on \`db.pharmacies\` — the list of US states they're licensed to ship into. **Fail-closed**: empty array = can't ship anywhere until admin populates it.

**Where to edit:** **Admin → Pharmacy Partners → click pharmacy row → "States this pharmacy can ship to"** chip grid (50 states + DC). Select-all / Clear-all helpers; live "N/51 states" badge.

**Strict 2-letter normalization** (\`routes/pharmacies.py:_normalize_states\`): \`"Florida"\` → dropped, \`"invalid"\` → dropped, \`"fl"\`/\`"FL "\` → \`"FL"\`.

**eRx send guard** (\`services/pharmacy_shipping.py\`): bridges \`pharmacy_tenants\` ↔ \`pharmacies\` by name then slug. \`POST /api/orders/{id}/action approve\` returns **HTTP 409** with structured detail when the patient's state isn't on the partner's list:

\`\`\`json
{
  "code": "pharmacy_not_licensed_for_state",
  "message": "Polomar Specialty Pharmacy is not licensed to ship to CA. Select a different pharmacy partner before sending this eRx.",
  "pharmacy_name": "Polomar Specialty Pharmacy",
  "patient_state": "CA",
  "allowed_states": ["FL", "GA", "NY", "TX"]
}
\`\`\`

The Prescriber Dashboard surfaces this as a long-duration toast and keeps the modal open so they can pick a different pharmacy.

**Pre-flight check**: \`GET /api/pharmacy-tenants/{id}/can-ship?patient_id=...\` returns the same payload without side-effects.

**Legacy permissive fallback**: pharmacies without an admin-managed Partner record skip the check entirely until admin creates one.`,
  },
  {
    id: "ru-shipping-coverage-badge",
    required: false,
    category: "admin-marketing",
    title: "Patient checkout shipping coverage badge (added 2026-02-19)",
    audience: "all",
    body: `The patient **Medication Selection / Checkout** page shows an inline shipping-coverage badge above Pay. Auto-syncs with active partners' \`ships_to_states\`.

**Three visual states:**
- 🟢 **Covered**: emerald pill — *"We ship to {ST} · N states covered"*.
- 🟠 **No address on file**: amber pill — prompts patient to add state on profile.
- 🔴 **Not covered**: red banner — *"We can't ship to {ST} yet"* + waitlist signup form. **Pay button is disabled** with explanatory tooltip — patient can't submit an unfulfillable order.

**Live admin sync**: frontend polls \`GET /api/pharmacies/shipping/coverage\` every 30 s. Any admin edit reaches an open patient checkout tab within half a minute without reload.

**Result**: 409 eRx-send failures on the prescriber side drop to ~zero — patients are pre-warned + gated before they ever pay.`,
  },
  {
    id: "ru-prescriber-regimen-grouping",
    required: false,
    category: "prescriber",
    title: "Multi-order regimen grouping + above-starter alert banner (added 2026-02-19)",
    audience: "prescriber",
    body: `When multiple pending_review orders belong to the **same patient**, they cluster at the top of **Orders → Pending Review** so the regimen is reviewed as a unit.

**What you'll see:**
- Purple "Regimen · N/M" pill on each row in a cluster (testid \`regimen-badge-{orderId}\`).
- Soft purple left-border + tinted background on every clustered row.
- **Yellow "Heads up — regimen-level review" banner** above the first row when the cluster has 2+ orders AND at least one above-starter dose request.

**Important**: approve/deny remains **per-order**. No bulk approve — each prescription is signed individually so clinical judgement applies line-by-line. Grouping is a visual aid only.

**Filter behaviour**: grouping only applies to **pending_review**. Approved/Denied keep chronological order. Solo-patient orders (groupSize = 1) render unchanged.`,
  },
  {
    id: "ru-duplicate-address-flag",
    required: false,
    category: "admin-ops",
    title: "Duplicate-address soft flag (anti-fraud, added 2026-02-19)",
    audience: "admin",
    body: `When two or more **patient** accounts share a normalized address (\`street|city|state|zip5\`), all matching accounts get \`users.duplicate_address_flag: true\`. Soft flag — accounts stay usable but auto-applied household-stackable discounts are suppressed.

**Triggers**: \`POST /api/address/acknowledge\` and \`PUT /api/profile\` (when any address_* field changes).

**Suppressed for flagged accounts** in \`check_discounts\`:
- FL state discount.
- Non-stackable referral coupons.
- Non-stackable Switch & Save coupons.

**Still applies**: stackable streak rewards (earned individually) + manually-redeemed marketing codes (admin-distributed codes the user typed in).

**Admin visibility**: yellow ShieldAlert badge at top of **PatientDetailModal** lists all matched accounts. Admin clears via the badge's **"Clear flag"** button → \`POST /api/admin/users/{user_id}/duplicate-address/clear\` (admin + customer_service). Audit logged.

**When to clear**: spouses, married couples, adult child with parent, roommates with independent medical needs.

> **Bi-directional**: clearing one side does NOT clear the others — clear each manually if all are legitimate.`,
  },
  {
    id: "ru-admin-stat-clicks",
    required: false,
    category: "admin-ops",
    title: "Clickable admin stat cards everywhere (added 2026-02-19)",
    audience: "admin",
    body: `Every stat tile in the staff portal is now actionable.

**Admin Overview:**
- Patients/Prescribers cards → Users tab.
- Pending Orders/Payments cards → Orders tab.
- Recent Orders rows (testid \`recent-order-{id}\`) → opens PatientDetailModal directly.

**Tracking Queue:** Shipped/Delivered/InProgress cards toggle the matching status filter (purple/emerald/amber ring on active).

**Pending Refunds:** Awaiting/Total $ Owed → Pending tab. Processed (30d) → Processed tab.

**Above-Starter Dose Digest** (Wellness → Severe Symptoms): all 5 tiles (Total / @ Starter / As Req'd / Denied / Pending) open a drill-down dialog listing the orders in that bucket.

**Top waitlisted states**: each row click → Pharmacy Partners tab.`,
  },
  {
    id: "ru-loyalty-data-driven",
    required: false,
    category: "admin-marketing",
    title: "Loyalty / FL / referral amounts fully data-driven (added 2026-02-19)",
    audience: "admin",
    body: `All marketing dollar amounts read from a single Mongo doc: \`db.app_config.loyalty_rules\`. **No code changes or deploys** required to tweak amounts — edits propagate to landing copy, pricing page, and checkout math within seconds.

**Where to edit:** **Admin → Loyalty & Coupons → Rules** card.

**What auto-syncs from \`/api/loyalty-rules/public\`:**
- LandingPage hero refer-a-friend badge + share modal + Web Share API title.
- \`ReferralCard\` (patient dashboard) and \`CompanionReferralBanner\` (Companion app).
- PricingPage Switch & Save copy.
- Checkout math — \`check_discounts\` evaluates the patient's best coupon vs. any stackable rewards on every call (auto-applied discounts are suppressed for duplicate-address-flagged accounts).

**Key fields:**
| Key | Default | Notes |
|---|---|---|
| \`referral_amount\` | 20 | $ off (both sides) |
| \`referral_cap\` | 10 | Max rewarded referrals per user |
| \`switch_save_amount\` | 20 | Switch & Save coupon $ |
| \`new_patient_price\` | 150 | "New patient" deal price |

> A/B test \`switch_save_amount\` from 20 → 25, watch conversion for 48 hrs, roll back if it doesn't pencil out.`,
  },
  {
    id: "ru-fact-myth-reorder",
    required: false,
    category: "admin-marketing",
    title: "Fact-or-Myth reorder bug fix (2026-02-19)",
    audience: "admin",
    body: `Fact-or-Myth admin **move up / move down** now works reliably even on legacy entries with null positions.

**What changed:**
- New endpoint **\`POST /api/admin/fact-or-myth/reorder\`** takes ordered \`{ ids: [...] }\` and rewrites every entry's \`position\` to its 1-based index. Deterministic — no swap races.
- GET endpoint **lazy self-heals**: legacy entries with null/missing position get sequential values assigned on first call. Idempotent.
- Frontend up/down buttons compute full new order client-side and ship one POST.`,
  },
  {
    id: "ru-coa-pipeline-2026-02-26",
    required: false,
    category: "admin-ops",
    title: "COA pipeline: bulk upload, email auto-ingest, order auto-link, SMS short-link (2026-02-26)",
    audience: "admin",
    body: `End-to-end Certificate of Analysis (COA) pipeline. Patients can now verify the exact vial they received via four surfaces, and pharmacies have three intake paths.

**Patient-facing surfaces** (blind-link safe — never sent until COA is on file):
- Shipping email → green "Verify your vial" button
- Shipping SMS → short link \`glpflo.org/c/{coa_id}\`
- Patient dashboard → "Verify your vial" tile (linked) or "pending" tile (lot known, COA not yet uploaded)
- Public lookup at \`/coa\` → search by lot number (digits-only OK, ignores dashes/dots/spaces)

**Admin intake paths** (Admin → COA Management):
1. **Single upload** — drag one PDF, set lot/pharmacy/medication
2. **Bulk upload** — drop up to 50 PDFs; filename pre-fills lot + medication (regex)
3. **Email auto-ingest** — pharmacies email PDFs to \`coa-{pharmacy}@parse.glpflo.com\`. Sender allow-list enforced; lot parsed from subject or filename. Add inbound mappings in the COA Management tab.

**Auto-link to orders**:
- Forward link: LabFlo ship webhook with \`lot_number\` → stamps \`coa_id\` on the order at ship time
- Reverse backfill: every COA upload (single/bulk/email) sweeps \`db.orders\` for matching lots and links retroactively
- Pharmacy-id discipline: Pharm-A COA never overwrites a Pharm-B order

**Audit log actions**: \`coa_upload\`, \`coa_bulk_upload\`, \`coa_inbound_processed\`, \`coa_inbound_rejected_unknown_address\`, \`coa_inbound_rejected_unknown_sender\`.

**Key endpoints**:
- \`POST /api/admin/coas\` (single), \`POST /api/admin/coas/bulk\`, \`POST /api/webhooks/inbound-coa-email\`
- \`GET /api/orders/{order_id}/coa\` (patient/staff)
- \`GET /api/coa/search?lot=...\` (public)`,
  },
  {
    id: "ru-id-verification-2026-02-26",
    required: false,
    category: "compliance",
    title: "Patient Identity Verification — now powered by Stripe Identity (updated 2026-06)",
    audience: "all",
    body: `Identity proofing for patient onboarding now runs entirely on **Stripe Identity** (document + selfie). This REPLACED the old in-house "photo of government ID + 5-second video attestation" flow.

**Patient side** (Profile → Identity Verification, or auto-prompted before the first order):
1. Tap **Verify my identity** → redirected to Stripe's hosted, secure page
2. Capture a government ID (DL, state ID, passport) — Stripe guides the capture
3. Take a selfie — Stripe matches face-to-ID for liveness
4. Stripe verifies automatically, usually in under a minute — no manual prescriber review needed

**Prescriber / staff side** (Patient detail → Identity Verification panel):
- The panel now shows the **Stripe Identity result** (Verified / Processing / Action-needed) + timestamp instead of a photo/video to review
- Verification is **automatic** — staff no longer manually approve each ID. A **manual override** (Mark verified / Reject) remains for edge cases
- Legacy patients who verified under the old flow still show their archived ID photo + video

**Ordering gate**: \`POST /api/orders\` returns **412 \`id_verification_required\`** until \`id_verification.verified\` is true. That flag is set by the Stripe webhook (\`identity.verification_session.verified\`) plus a status-poll fallback.

**Privacy win**: the ID document is handled 100% by Stripe — GLPFlo never sees or stores it, shrinking our PHI footprint. Patient rights under HIPAA § 164.524 unchanged (Profile → Privacy → Download my data).`,
  },
  {
    id: "ru-stripe-migration-2026-06",
    required: false,
    category: "admin-ops",
    title: "Payments migrated to Stripe + new Admin Payments tab (2026-06)",
    audience: "admin",
    body: `Checkout moved from Authorize.Net to **Stripe Checkout** (hosted, PCI-simple — the patient enters their card on Stripe's page, we never touch card data). Charges are **one-time**; recurring tiers create a first charge and renewals are handled externally for now.

**New: Admin → Orders → Payments tab** (\`stripe-payments\`):
- Lists recent Stripe payments newest-first with **summary cards** (gross collected, paid orders, refunded, total records)
- **Filter** by status (All / Paid / Refunded / Partial / Failed) and **search** by invoice, patient email, or medication
- **One-click Refund** on any paid row → calls Stripe \`Refund.create()\` (full refund; supports partial via API). Row flips to *refunded* / *partially refunded*

**How this differs from "Pending Refunds"**: the Payments tab issues an *immediate* Stripe refund on a specific payment. The **Pending Refunds** tab is the finance backlog of prescriber-approved refunds waiting to be pushed. Both ultimately call Stripe.

**Money flow**: GLPFlo is the single merchant — it collects 100% into its own Stripe account (payout to the GLPFlo bank), then settles with the clinic/pharmacies separately. No Stripe Connect. **Radar** fraud scoring is on by default.

**Hosting note (Atlantic.net + Emergent)**: all Stripe config is env-driven (\`STRIPE_SECRET_KEY\`, \`STRIPE_WEBHOOK_SECRET\`, \`FRONTEND_URL\`). Each host needs its own Stripe **webhook endpoint** registered at \`/api/stripe/webhook\` (events: \`checkout.session.*\`, \`charge.refunded\`, \`identity.verification_session.*\`) with its signing secret in that host's \`.env\`.`,
  },
  {
    id: "ru-meet-the-owners-2026-02-26",
    required: false,
    category: "admin-marketing",
    title: "Meet the Owners public page + super-admin CMS (2026-02-26)",
    audience: "admin",
    body: `New public page at \`/meet-the-owners\` mirrors the Our Physicians page structure (founder card grid with photo + bio + title).

**On first load**: 3 placeholder cards are auto-seeded (Co-Founder 1/CEO, 2/COO, 3/CMO) so the page is alive even before the founders' real bios are entered.

**Editing** (super-admin only):
- \`POST /api/admin/owners\` — create
- \`PUT /api/admin/owners/{id}\` — edit (clears the seed flag once touched)
- \`DELETE /api/admin/owners/{id}\` — remove
- \`POST /api/admin/owners/{id}/headshot\` — upload photo

**Audit-logged**: \`owner_create\`, \`owner_update\`, \`owner_delete\`.

**Linked from**: landing-page footer ("Meet the Owners"), and naturally indexed by Google via the same SEO patterns as Our Physicians.

**Backlog**: admin UI tab for inline editing is still pending — for now, super-admin uses the API or a quick mongosh edit.`,
  },
  {
    id: "ru-youtube-library-2026-02-26",
    required: false,
    category: "admin-marketing",
    title: "YouTube Video Library — hybrid auto-pull + super-admin curation (2026-02-26)",
    audience: "admin",
    body: `Patient-facing video library wired to our YouTube channel \`UC2NOTygbPH-3JCkElwzNEWw\`.

**How videos arrive**:
- Nightly APScheduler job (\`nightly_youtube_refresh\` at 02:15 UTC) pulls every upload from the channel via the YouTube Data API v3 into \`db.youtube_videos\`
- Manual trigger: \`POST /api/admin/youtube/refresh\` (super-admin only)
- Requires \`YOUTUBE_API_KEY\` env var (Google Cloud Console → Public data → enable YouTube Data API v3 → create API key)

**Super-admin curation**:
- \`POST /api/admin/youtube/feature/{video_id}\` — pin to top (optional \`position\` int)
- \`POST /api/admin/youtube/hide/{video_id}\` — filter out of patient view
- \`DELETE /api/admin/youtube/override/{video_id}\` — clear overrides
- Overrides persist in \`db.youtube_overrides\` across cache refreshes — surviving the nightly job

**Patient surfaces**:
- Profile → GLPFlo Video Library card (searchable, inline iframe playback)
- Companion App install page → "GLPFlo on YouTube" deep-link card
- Landing page footer → "GLPFlo on YouTube" external link

**Graceful degradation**: when \`YOUTUBE_API_KEY\` is missing, refresh returns 503 \`no_api_key\`. Patient UI shows a friendly "New videos coming soon!" placeholder with a direct channel link.

**Backlog**: super-admin UI tab for feature/hide toggle still pending — for now, use the API directly.`,
  },
  {
    id: "ru-faq-baseline-2026-02-26",
    required: false,
    category: "admin-marketing",
    title: "FAQ baseline seed expanded (COA, ID, missed dose, video library, owners) (2026-02-26)",
    audience: "admin",
    body: `Six new baseline FAQ entries auto-seed on backend startup if missing — covers the recently shipped features so patients can self-serve answers.

**New FAQ slugs**: \`verify-vial-coa\`, \`coa-not-found\`, \`identity-verification\`, \`missed-dose\`, \`video-library\`, \`meet-owners\`.

**Idempotent**: re-runs are no-ops. Manual edits in **Admin → FAQ** are preserved (we only insert if the slug is missing).

**Where they surface**: \`/faq\` public page (Google indexes via FAQPage JSON-LD), chatbot context (the bot can quote these via the canned response system).

**To customize copy**: Admin → FAQ → edit the row. Your changes stay; the seed never overwrites.`,
  },
  {
    id: "ru-faq-chatbot-sync-2026-02-26",
    required: false,
    category: "admin-marketing",
    title: "FAQ → Chatbot auto-sync — single source of truth (2026-02-26)",
    audience: "admin",
    body: `Every FAQ create / update / delete in **Admin → FAQ** is now mirrored into the chatbot canned-response store automatically.

**How it works**: Mirror rows are keyed by \`slash="faq-{slug}"\` and stamped with \`mirrored_from="faq"\`. The bot can never quote a stale FAQ answer because the mirror updates on every edit.

**Inactive FAQs**: deactivating a FAQ doesn't delete the mirror — it sets \`active=false\` + \`auto_reply_eligible=false\` on the mirror row. Re-activating instantly re-lights the chatbot.

**Startup sweep**: Backend boot runs a full reconciliation — mirrors every active FAQ, prunes orphan mirror rows whose source FAQ was deleted directly in mongosh.

**When to use a hand-curated chat template instead**: If a topic needs chat-specific nuance (e.g. tone adjustment, different phrasing for an SMS-driven inbound), create a row directly in **Admin → Chatbot → Canned Responses** with a non-\`faq-\` slash. The mirror only owns \`faq-*\` slashes.`,
  },
  {
    id: "ru-testimonial-14-day-2026-02-26",
    required: false,
    category: "admin-marketing",
    title: "Auto-prompt for video testimonial — 14 days post-shipment (2026-02-26)",
    audience: "admin",
    body: `Existing testimonial pipeline now includes a **14-day post-first-shipment** milestone alongside the existing 12-week and 20-lb milestones.

**Scheduler**: \`daily_testimonial_request_job\` runs at **10:00 UTC daily** (was Tuesday-only). Picks up patients whose first shipment was ≥14 days ago and who haven't already received a testimonial prompt.

**Channels**: SMS + email sent in lock-step. Patient gets the SMS first (5× open rate vs email) with a short link \`glpflo.org/s/{userId}/{token}\` that points to the existing **Share Your Story** page.

**Dedup**: A patient hitting the heavier 12-week or 20-lb milestones takes precedence (those convert better). The 14-day prompt only fires for patients who haven't already been bucketed into the bigger milestones.

**Gate**: One-time only. \`testimonial_request_sent_at\` on the user doc prevents re-prompts forever. There's no UI to reset this (intentional — prevents accidental spam).

**SMS template key**: \`testimonial_request\` in \`services/sms.py\`. Edit copy in **Admin → SMS Templates** if needed.`,
  },
  {
    id: "ru-admin-cms-tabs-2026-02-26",
    required: false,
    category: "admin-marketing",
    title: "New super-admin tabs: Owners CMS + YouTube Library curation (2026-02-26)",
    audience: "admin",
    body: `Two new super-admin tabs landed under **Admin → Content**.

**Meet the Owners** (\`admin-owners-tab\`):
- Full CRUD UI for the public \`/meet-the-owners\` page.
- Add / Edit dialog: Name, Title, Bio, Headshot upload.
- Reorder with Move Up / Move Down (writes \`position\` field, mirrors how PhysiciansManagementTab works).
- **Placeholder** amber pill on seed cards — auto-clears the moment you edit one.
- All actions audit-logged: \`owner_create\`, \`owner_update\`, \`owner_delete\`.
- **Super-admin only** (regular admins get a 403 toast).

**YouTube Video Library** (\`admin-youtube-tab\`):
- Auto-pull from channel \`UC2NOTygbPH-3JCkElwzNEWw\` nightly at 02:15 UTC.
- **Refresh now** button triggers an immediate pull; toasts \`no_api_key\` / \`channel_not_found\` reasons clearly if misconfigured.
- **Feature** a video (gold ⭐ pin at the top of the patient library). Edit position number to control ordering across multiple featured.
- **Hide** a video (filtered out of patient view; admin still sees it here so you can un-hide later).
- Overrides live in \`db.youtube_overrides\` and survive cache refreshes.
- **Super-admin only**.

**Requires**: \`YOUTUBE_API_KEY\` env var (Google Cloud Console → enable YouTube Data API v3 → "Public data" → create API key → paste into backend env). Without the key, the patient library degrades gracefully to an empty-state with a direct channel link.`,
  },
  {
    id: "ru-team-capability-filter-2026-02-26",
    required: false,
    category: "admin-ops",
    title: "Team table: search-as-you-type capability filter (2026-02-26)",
    audience: "admin",
    body: `New client-side filter on **Admin → Users**. Type a capability key (e.g. \`manage_pharmacy\`, \`refund_admin\`, \`view_phi\`) and the table filters to just the staff who carry that capability.

**Visibility**: The filter input only appears when the role filter is empty, admin, customer_service, or it (since patients/prescribers don't carry capabilities — keeps the UI honest).

**Composability**: Works on top of the existing backend filters (role/active/search). Live match-count badge on the right of the input.

**Use cases**:
- "Who has refund_admin?" — Type \`refund\` to see refund-authorized staff before approving a >$500 refund.
- "Who can view PHI?" — Type \`view_phi\` for the HIPAA quarterly access review.
- "Who hasn't been assigned \`manage_pharmacy\` yet?" — Switch role to \`customer_service\` then type \`manage_pharmacy\` to see who's missing it.

**Performance**: Pure client-side filter on the loaded user list (no backend round-trip per keystroke).`,
  },
  {
    id: "ru-template-trifecta-2026-02-26",
    required: false,
    category: "admin-marketing",
    title: "Templates trifecta: Email / SMS / Chatbot — Create + Duplicate parity (2026-02-26)",
    audience: "admin",
    body: `All three messaging template stores now share the same admin action shape: **Edit · Copy · Delete**.

**New on Email Templates**:
- "New Template" button (emerald, header-right) creates a custom email template from scratch. Lands with \`custom: true\` and a "Custom" pill so it's easy to tell apart from automated seed templates.
- Delete button (trash icon, rose) visible on custom rows only. **Seed templates refuse delete** with a clear error ("can only be deactivated") — they back automated triggers like \`order_approved\`, \`tracking_created\`, etc.
- Edit dialog now exposes the \`name\` field when editing a custom template (seed names stay locked).

**Duplicate (all 3 surfaces)**:
- Copy icon next to Edit on every row. Forks the source into the New dialog with the body pre-filled and \`(copy)\` appended to the title.
- Trigger forced to \`custom\` so duplicates don't fight the original for ownership of an automated event.
- For chatbot canned responses, the slash is cleared (it's the unique key — admin sets a new one to save).

**Audit logging**: \`email_template_create\`, \`email_template_delete\`. SMS + chatbot already had their CRUD audit hooks from the previous phase.

**Workflow tip**: Start by duplicating the most-similar seed template, then tweak. It's faster than starting from blank AND inherits the brand voice / styling.`,
  },
  {
    id: "ru-dialog-scroll-safety-2026-02-26",
    required: false,
    category: "it-triage",
    title: "Dialog scroll-safety: <ScrollableDialogContent> wrapper + ESLint guardrail (2026-02-26)",
    audience: "it",
    body: `Bug fix + platform-wide hygiene pass for the "modal grows past the viewport, body scroll is locked by Radix, user is trapped" problem.

**Three layers of defense**:
1. **Inline fix on the original bug** (CreateEmployeeModal) — added \`max-h-[90vh] overflow-y-auto\` to DialogContent.
2. **Hygiene sweep** of all 29 admin dialogs — patched the 20 that were missing the pattern.
3. **\`<ScrollableDialogContent>\`** wrapper at \`/app/frontend/src/components/ui/scrollable-dialog-content.jsx\` — drop-in replacement for shadcn's \`<DialogContent>\` that bakes the pattern in by default.
4. **ESLint guardrail** — \`no-restricted-syntax\` rule in \`eslint.config.js\` warns on every raw \`<DialogContent>\` usage with a pointer to the wrapper. Warning-level so existing inline-patched usages don't break CI.

**For new dialogs**: Always use \`<ScrollableDialogContent>\`. Example:
\`\`\`jsx
import { ScrollableDialogContent } from "@/components/ui/scrollable-dialog-content";

<Dialog open={open} onOpenChange={onClose}>
  <ScrollableDialogContent className="max-w-lg">
    <DialogHeader>…</DialogHeader>
    …
  </ScrollableDialogContent>
</Dialog>
\`\`\`

**Slow migration of the existing 74 raw usages** — touch them as part of unrelated PRs in those files; don't do a big-bang migration (noisy git history). When warning count hits zero we'll flip the rule to \`error\` to lock the guarantee.`,
  },
  {
    id: "ru-stepathon-rewards-2026-02-28",
    required: false,
    category: "admin-marketing",
    title: "Step-a-Thon — Shareable badges + 1-step-=-1-point Rewards system (2026-02-28)",
    audience: "admin",
    body: `Every step a patient logs on any Step-a-Thon (manual entry or in-browser pedometer) earns them **1 point**. Points roll up lifetime across every event the patient ever participates in and are redeemable from \`/stepathon/rewards\`.

**Patient-facing flow**:
- \`/stepathon/:slug\` event page now mounts a **shareable badge card** when the patient hits a tier (Mover / Champion / Legend / etc.). The badge is a 1080×1080 PNG rendered server-side by Pillow — patient can download or one-tap share via the native Web Share API (with the actual PNG attached on iOS/Android, text-only fallback on desktop).
- \`/stepathon/rewards\` shows the points hero (earned · spent · available), the redeemable catalogue, and a redemption history with click-to-copy coupon-code pills.

**Admin surface** — Admin → Content → **Step-a-Thon** now has 3 sub-tabs:
1. **Events** (existing) — create / edit step challenges.
2. **Rewards catalogue** — full CRUD. Three reward types:
   - \`coupon\` — coupon code revealed in the patient's redeem response. Use this for stack-with-checkout codes like WALK10, STEPSHIP.
   - \`free_refill\` — refill credit in cents. Auto-applies at the patient's next checkout — **no patient action**. Manual fulfilment flag in queue is just for accounting.
   - \`merch\` — physical merch. Marking fulfilled in the queue triggers our standard fulfilment workflow (sizing email → ship).
   Each row has stock cap (0 = unlimited), description, optional image_url, status (active / paused).
3. **Redemption queue** — fulfilment workspace. Filter by Pending / Fulfilled / Cancelled. Click **Fulfill** to mark a row complete (writes \`fulfilled_at\`). Click **Cancel** to refund the patient's points (refunds the spent points so they can re-redeem).

**Seeded starter catalogue** (idempotent — \`services/stepathon_rewards_seed.py\` runs at startup, keyed by \`seed_slug\`, never duplicates):
- $10 off your next refill · 10,000 pts · WALK10
- Free shipping on next refill · 25,000 pts · STEPSHIP
- $25 refill credit · 50,000 pts · auto-applies
- Normalize the Journey tee · 75,000 pts · stock=100
- $100 Legend refill credit · 100,000 pts · auto-applies

**Security notes**:
- The public \`GET /api/stepathon/rewards\` endpoint **strips coupon_code and refill_credit_cents** before serializing. Only the patient who actually redeems sees the code (in the redeem response + their own \`/rewards/me\` history).
- Admin endpoints (\`/admin/rewards\`, \`/admin/rewards POST/PUT\`) include the secrets — gated by \`_require_admin\` (admin role only; super-admin inherits).

**CS playbook**:
- "Where's my coupon?" → patient should see it under \`/stepathon/rewards\` → Your redemption history → tap the green pill to copy.
- "I redeemed by mistake" → Admin → Step-a-Thon → Redemption queue → find row → **Cancel** (refunds points, sets status=cancelled).
- "Where do I see merch sizing?" → Once fulfilled, ping ops@glpflo.org with the redemption_id; fulfilment workflow happens out of band.

**Endpoints**:
- Patient: \`GET /api/stepathon/rewards\`, \`GET /rewards/me\`, \`POST /rewards/{id}/redeem\`
- Patient (badges): \`GET /api/stepathon/events/{slug}/badge.png?tier=<label>\` (cookie-auth for personalised PNG, anon for generic)
- Admin: \`GET/POST/PUT /api/stepathon/admin/rewards\`, \`GET/PATCH /api/stepathon/admin/redemptions\`

**Audit log actions**: \`stepathon_reward_create\`, \`stepathon_reward_redeem\`.`,
  },

  // ====================================================================
  // 2026-08-09 additions
  // ====================================================================
  {
    id: "coa-bulk-edit-delete",
    category: "admin-ops",
    title: "COA — Bulk Edit & Bulk Delete",
    audience: ["admin"],
    updatedAt: "2026-08-09",
    body: `**When to use**:
Multiple COAs share the same wrong pharmacy name, or a batch of expired lots needs to be retired in one shot.

**How to bulk edit**:
1. **Admin → COA Management**.
2. Tick the row checkboxes for every COA you want to change (or the header checkbox to select all visible rows).
3. A floating dark pill appears at the bottom of the viewport: **"N selected"** with **Bulk edit** and **Delete** buttons.
4. **Bulk edit** opens a modal — fill in *only* the fields you want to change (Pharmacy, Medication, Expiration, Notes) and optionally check **Also mark all as reviewed**. Blank fields are LEFT ALONE per row (partial update).
5. Apply — a toast shows how many rows changed.

**How to bulk delete**:
1. Same selection UX.
2. Click the rose **Delete** button in the pill.
3. Branded confirm dialog appears. Confirm to soft-delete — the PDFs stay in storage for audit chains but the COAs disappear from patient searches.

> Bulk operations are capped at **200 COAs per request** — the API returns 400 above that limit.

**Endpoints**:
- \`POST /api/admin/coas/bulk-edit\` — body: \`{ coa_ids: [...], updates: { medication_name?, pharmacy_name?, expiration_date?, notes?, mark_reviewed? } }\`
- \`POST /api/admin/coas/bulk-delete\` — body: \`{ coa_ids: [...] }\`

**Audit log actions**: \`coa_bulk_edited\`, \`coa_bulk_edited_and_reviewed\`, \`coa_bulk_deactivate\`.`,
  },
  {
    id: "admin-self-password-rotation",
    category: "admin-ops",
    title: "Rotate your own admin password (SEC-001)",
    audience: ["admin", "prescriber"],
    updatedAt: "2026-08-09",
    body: `**Why this matters**:
The initial admin password ships in the \`.env\` file (bootstrap only) and every admin should rotate it after their first login. This closes SEC-001 by making the DB the sole source of truth.

**Quick path (recommended)**:
1. Open **Admin Overview** — first tab you land on after login.
2. Click the **"Change your password"** card (purple key icon, right at the top).
3. Modal opens with a form: current password → new password → confirm.
4. Save. **Every other session for your user is revoked** (the current tab stays alive). \`must_change_password\` is cleared.

**Alternate paths**:
- **My Profile → Security → Password Change** (older location, still works).

**Strength policy**: 12+ chars, mixed case, digit, symbol. Same rules as patient signup.

> If backend logs show \`SEC-001: ADMIN_PASSWORD is still set in .env\` after everyone has rotated, clear the env var — it's only used for the initial DB seed.

**Endpoint**: \`POST /api/admin/change-password\` — \`{ old_password, new_password }\`.
**Audit action**: \`admin_self_change_password\`.`,
  },
  {
    id: "delivery-window-admin",
    category: "admin-ops",
    title: "Handling patient 3-day delivery window requests",
    audience: ["admin"],
    updatedAt: "2026-08-09",
    body: `**Where they come in**:
Patients submit a preferred 3-day shipping window from Dashboard → *Traveling? Request a delivery window* (or the app Home). Every request lands in **Admin → Delivery Exceptions** with the patient, requested window, and their optional note.

**Actioning a request**:
1. Open the row → review the patient's requested date range and note.
2. **Mark acknowledged** so the patient sees a confirmation in their Documents.
3. Coordinate with the pharmacy (LabFlo integration + shipping team) to hit the window.
4. If the window can't be honoured (out of stock, weekend cutoff), flag it and message the patient via chat.

> One request per shipment. If a patient needs recurring windows, escalate to ops — we handle those out of band.`,
  },
  {
    id: "wellness-visibility-2026-08",
    category: "admin-ops",
    title: "New patient wellness data — where to find it",
    audience: ["admin", "prescriber"],
    updatedAt: "2026-08-09",
    body: `Patients on the mobile app can now log:

- **Meals** — calories + macros (protein / carbs / fat).
- **Workouts** — cardio, strength, walking, custom; duration + calories.
- **Symptom check-ins** — from the home banner, even between subscriptions. Severe symptoms auto-escalate to prescriber queue.
- **Weight** — same as before, but the tab now shows the last 7 days at the top.

**Where you'll see it**:
- **Prescriber → Patient Detail Modal → Wellness tab** — merged timeline of the above, most recent first.
- **Admin → Users → click patient row → same modal**.

Symptom check-ins mirror to the existing Severe Symptoms queue when severity ≥ high. No new admin actions required — the queue you already use catches them.`,
  },
  {
    id: "mobile-boot-splash-theme",
    category: "getting-started",
    title: "Mobile app: post-login boot splash + Light/Dark theme",
    audience: ["all"],
    updatedAt: "2026-08-09",
    body: `**What patients see now**:
After sign-in, a ~2.6s branded boot animation plays (aurora background + \"GLPFlo\" wordmark + typewriter tagline \"normalize the journey\"). Fades and lands on Dashboard.

**Preferences (Patient → Profile → Preferences)**:
- **Appearance** — Light / Dark / System (persists to device).
- **Boot animation sound** — toggle on/off. When off, the typewriter tick is silent (haptics still fire).

**Support scripts**:
- \"I don't want the animation\" → Currently no toggle to disable the animation itself — only its sound. It's a hard-coded ~2.6s branded intro. If you want to skip it, close the app before signing in and use web instead.
- \"App looks weird\" → Ask the patient to check **Profile → Preferences → Appearance**. If they set it to Light and don't like it, System usually matches their OS.
- \"App icon changed\" → Correct — the aurora Aurora icon + splash are the new brand. If they're on an older build, ask them to update from the App Store / Play Store.`,
  },

  // -------- ADMIN OPS · RECENT RULES & PIPELINES (August 2026) --------
  {
    id: "admin-pricing-rules-2026",
    category: "admin-ops",
    title: "Pricing rules — 6 flat prices, no per-dose (2026 refresh)",
    audience: "admin",
    body: `GLPFlo's public price is the SAME at every dose strength. Only three tier prices exist per drug:

- **Monthly** — no auto-renew
- **1-Month Subscribe** (auto-derived as Monthly − $8, or set manually)
- **Royal** — best rate, auto-renewing series

So the whole catalogue is **6 prices total** (2 drugs × 3 tiers).

**What changed under the hood**
- The old \`dose_groups\` array (Low / Mid / High per-dose prices) is gone from the admin UI.
- The pricing API rejects any POST or PUT that includes a \`dose_groups\` key with HTTP 400.
- On every backend startup the migration \`\$unset\` any lingering \`dose_groups\` array on \`pricing_tiers\` docs. Safe to re-run.
- A green **Rate Locked** pill ("Price never rises with your dose") is shown on \`/pricing\`, the mega-compare page, and the patient checkout screen.

**Where to edit**
Admin → Pricing → Product Pricing tab. Only the three tier prices are editable. Solo Subscribe auto-derives as Monthly − $8 (edit Monthly if you want to move it).`,
  },
  {
    id: "admin-pharmacy-vial-costs-2026",
    category: "admin-ops",
    title: "Pharmacy vial costs (COGS) — 3 sizes per drug",
    audience: "admin",
    body: `Patient price is flat, but the pharmacy charges GLPFlo a different wholesale cost per vial size. The admin card captures every combination:

**Semaglutide** — 1 mL / 2.4 mL / 3.6 mL vials
**Tirzepatide** — 1 mL / 2 mL / 3.6 mL vials

Storage key format under \`pharmacy_costs.costs[pharmacy_id][drug_slug][key]\`:
- \`vial_1_0mL\`, \`vial_2_4mL\`, \`vial_2_0mL\`, \`vial_3_6mL\`

**Where to edit**
Admin → Pricing → **Costs & Fees** tab. Select a pharmacy tab first (each partner has its own six cost values). Costs are snapshotted onto each order as the COGS the moment the prescriber routes to that pharmacy.

> Adding a new pharmacy? It appears here automatically once it's connected via LabFlo — no code change needed.`,
  },
  {
    id: "admin-deploy-atlanticnet",
    category: "admin-ops",
    title: "Atlantic.net production pipeline (Windows Server)",
    audience: "admin",
    body: `The live \`glpflo.org\` site runs on our own **Windows Server at Atlantic.net**. The Emergent preview URL is the dev/staging environment — production is completely separate.

**Stack**
- IIS as the front-end web server + reverse proxy to FastAPI on \`127.0.0.1:8001\`
- PM2 (installed as a Windows service) runs \`glpflo-backend\` on 8001 and survives reboots
- Let's Encrypt via **win-acme** for HTTPS, auto-renews on schedule
- Local MongoDB Community for the production database

**CI/CD**
- Windows Task Scheduler runs \`C:\\Websites\\GLPFlo\\scripts\\windows\\glpflo-update.ps1\` every 3 minutes
- Script does: \`git fetch\` → if new commits, \`git pull\` → \`pip install\` if backend changed, \`yarn install && yarn build\` if frontend changed → \`pm2 restart glpflo-backend\` → \`iisreset\`
- Log: \`C:\\GLPFloLogs\\auto-update.log\`

**Encrypted backups**
- \`glpflo-backup.ps1\` runs daily at 02:00 via Task Scheduler
- \`mongodump\` → tar → **AES-256** encrypt (key in \`C:\\GLPFloSecrets\\backup.key\`) → keep 30 days
- \`glpflo-restore.ps1\` decrypts + \`mongorestore\`s a chosen snapshot

**web.config**
- Lives in \`frontend/public/web.config\` so every \`yarn build\` copies it into \`build/\`
- Contains **two** rewrite rules: (1) proxy \`^api/(.*)\` → \`http://127.0.0.1:8001/api/{R:1}\`, (2) React Router fallback to \`/index.html\`
- Written UTF-8 **without BOM** (IIS is picky)

**Deploy checklist**
1. Merge to \`GLPFloLLCv1.0\` on GitHub (Emergent's Save-to-GitHub currently pushes here).
2. Within 3 minutes the update script runs. Watch \`auto-update.log\` for \`update available: <sha>\`.
3. Hard-refresh \`glpflo.org\` in the browser (Ctrl+Shift+R) — CDN/HSTS caches can lag.`,
  },
  {
    id: "admin-esign-vault",
    category: "compliance",
    title: "E-sign flow & Signed Contracts vault",
    audience: "admin",
    body: `Every BAA, Medical Director agreement, and SOP acknowledgement is signable in-app and stored in the Compliance tab vault.

**How it works**
- Admin generates the pre-filled PDF (BAA / MD Agreement) from the Compliance tab.
- Recipient signs via \`/api/esign/{doc_type}\` — captures typed signature, timestamp (UTC), and originating IP.
- Executed copy + metadata land under \`agreements\` in Mongo.
- Compliance tab shows the "Signed Contracts" vault — one card per executed document with the download link and the audit metadata.

**SOP Book**
- \`GET /api/admin/sops/book\` renders a live single-PDF book of every active SOP with a clickable TOC + PDF bookmarks.
- Medical Director signs the whole book at once via \`POST /api/admin/sops/sign-all\` — writes a director signature into each SOP row's \`director_signature\` sub-doc.
- Digital signature audit trail is included at the end of the rendered book.

**Where to edit**
- Compliance tab → **SOP Library** (add/edit SOPs)
- Compliance tab → **BAA & Agreements** (generate + upload signed PDFs)
- Compliance tab → **Signed Contracts** (read-only vault, sorted newest-first)

**Never** email an SOP or agreement outside the vault — the audit trail is what carries the HIPAA weight.`,
  },
  {
    id: "admin-store-submission",
    category: "admin-ops",
    title: "App Store & Play Store submission checklist",
    audience: "admin",
    body: `The mobile app ships from Emergent's **Publish** button (top-right of chat) — never use EAS CLI.

**Apple App Store**
- Bundle ID: \`com.emergent.pharmacyroutinghub.tjcnsu\` (also the Android package)
- Apple ID: **6799727727**
- Screenshots (exact dimensions):
  - 6.5" iPhone: **1284 × 2778**
  - 13" iPad: **2064 × 2752**
- Copyright field (App Info → General Info): \`YEAR OwnerName\` — do **NOT** type \`©\`, Apple prepends it.
- Advertising Identifier (IDFA): **No** unless you enable ad tracking.
- Content Rights: **Yes**.

**Google Play Console**
- Registration fee: one-time $25.
- Format: **.aab** (not .apk).
- App content declarations that must be green:
  - App access, Ads (No), Content rating (17+), Target audience (18+), Data safety, Health apps declaration.
- Health features to check: Activity & fitness, Nutrition & weight management, Diseases & conditions management, Healthcare services and management, Medication and treatment management.
- Health Connect permissions require per-permission justification text (Steps, Distance, Active calories, Heart rate). See the pinned answers in this manual.

**Test-reviewer accounts**
- Google/Apple both require **login credentials** in the "App access" section. Use the test patient in \`/app/memory/test_credentials.md\` (or create a dedicated review account). Never share admin credentials.`,
  },

  {
    id: "ru-banners-tab-consolidation-2026-09-06",
    required: false,
    category: "admin-marketing",
    title: "Banners tab consolidation — every marketing strip toggleable from one place (2026-09-06)",
    audience: "admin",
    body: `The **Admin → Content → Banners** tab is now the single control panel for every top-of-page strip on the marketing site. Previously scattered controls have all been rolled into one screen so CS + marketing admins can turn any strip on or off without needing a code push.

**What's newly toggleable from Banners**
- **Currently-serving states strip** (new card at the top of the tab) — enabled toggle, editable state list (PA, WI, GA, FL by default), label prefix, trailing note. Config lives at \`db.app_config._id="serving_states"\`; public endpoint \`GET /api/public/serving-states\`. Patient help guide entry added under **shipping-coverage** so patients know to check the strip before starting intake.
- **Top-of-page strips card** with 3 toggles:
  - **Orders-paused strip** — wired to the existing \`/system/pause\` + \`/system/resume\` endpoints (audit-logged, super-admin only). Includes inline **Update message** editor while paused.
  - **Compare-plans strip** — the black COMPARE row (Starter $79.99 / Semaglutide $149 / Tirzepatide $249). Config: \`strip_toggles.compare_plans_enabled\`.
  - **Million-Mile Movement ticker** — purple community counter. Config: \`strip_toggles.million_mile_enabled\`.

**New backend endpoints**
- \`GET  /api/public/serving-states\` · \`GET/PUT /api/admin/serving-states\`
- \`GET  /api/public/strip-toggles\` · \`GET/PUT /api/admin/strip-toggles\`
- Existing: \`GET /api/system/pause-status\` · \`POST /api/system/pause\` · \`POST /api/system/resume\`

**Frontend wiring**
- \`ServingStatesStrip.js\` — fetches \`/public/serving-states\`, hides when disabled.
- \`LandingComparePlansStrip.js\` — reads \`strip_toggles.compare_plans_enabled\` (falls back to legacy \`/landing/flags\`).
- \`MillionMileTicker.js\` — reads \`strip_toggles.million_mile_enabled\` on mount.
- \`admin/BannersManagementTab.js\` — adds \`ServingStatesEditor\` + \`StripTogglesEditor\` cards above the existing promo-banners CRUD list.

**Promo strip (below-hero) enhancements** — the below-hero rotating strip (Free Shipping / No Membership Fees) already lives in the Banners tab list as \`promo-strip\` type. Each row supports **subtitle**, **icon** (Truck / Badge $ / Sparkles / Shield / Gift / Percent), colored background, and Pause/Activate. Rows auto-rotate every 5 s if 2+ are active; hidden entirely if 0 active. Seeded on startup: \`strip-free-shipping\`, \`strip-no-membership\`. Copy migration bumps any legacy "Cold-chain overnight …" subtitle to "Expedited cold shipping".

**Ops guardrails**
- Serving-states input auto-uppercases, dedupes, and caps at 20 codes.
- Strip-toggles PUT ignores unknown keys — safe to send partial payloads.
- All 3 endpoint families are cheap (single \`app_config\` doc read) so on-mount fetch adds negligible latency to first paint. Frontend components return \`null\` while loading to avoid flashing a stale server-side default.`,
  },

  {
    id: "ru-tirz-hero-plaque-2026-09-06",
    required: false,
    category: "admin-marketing",
    title: "New-patient hero plaque — Tirz $99.99 added (2026-09-06)",
    audience: "admin",
    body: `The landing-page hero right-hand column now shows **two** stacked purple plaques (mobile + desktop):

- **Semaglutide · First Month of Royal 3-Mo Tier — $79.99** (pink→purple gradient)
- **Tirzepatide · First Month of Royal 3-Mo Tier — $99.99** (deep-purple gradient)

Both plaques carry the same subtitle: *"for your first month · new GLPFlo patients only"*. The old blue **"Now Serving Florida"** plaque has been removed (superseded by the admin-editable serving-states strip at the top of the page).

**Backend price migration** — startup guard in \`server.py\` bumps any stored \`new_patient_price_tirz\` between $80.00 and $99.98 up to $99.99 on every pm2 restart. Admin-set custom prices ≥ $99.99 are preserved. The migration is idempotent and safe to re-run.

**New Patient Deal card + dialog copy** — CTA changed from *"Start — $X single vial"* to **"Start — first month of Royal 3-mo tier"**. Dialog description now reads *"First month of the Royal 3-month tier at $X. Months 2 and 3 bill at the regular Royal-tier price. Pick your medication:"* — matches the pricing-tab language. Patient help entry (\`new-patient-deal\`) updated to reflect the new copy + eligibility rules.

**Pricing-page layout** — Switch & Save + New Patient Deal promo cards moved **above** the "One price. Everything included." hero (previously below). Extra \`pb-10 sm:pb-14\` padding added so the hero doesn't touch the promo cards.

**Sticky-nav treatment** — the top nav on Landing + Pricing is now fully solid white (removed \`bg-white/80 backdrop-blur\`) for a cleaner scroll experience over the new hero plaques.`,
  },


];

// ===================================================================
// CHANGELOG — keep in sync with /app/memory/PRD.md. Newest first.
// Re-exported so any "What's new" widget can render a friendly feed.
// ===================================================================
export const TRAINING_CHANGELOG = [
  { date: "2026-08-18", entries: [
    "Pricing schema locked: only 3 tier prices per drug (Monthly, Solo Subscribe, Royal). Per-dose Low/Mid/High grid removed from admin UI; API now rejects any POST/PUT that includes `dose_groups`. Legacy dose_groups arrays auto-stripped from every pricing_tiers doc on backend startup.",
    "Pharmacy vial costs (COGS) now captures the true wholesale cost by vial size: Semaglutide **1 mL / 2.4 mL / 3.6 mL**, Tirzepatide **1 mL / 2 mL / 3.6 mL** — 6 inputs per pharmacy. Cost keys: `vial_1_0mL`, `vial_2_4mL`, `vial_2_0mL`, `vial_3_6mL`.",
    "New **Rate Locked** green pill (`Price never rises with your dose — locked at every strength.`) rendered on `/pricing`, `/pricing-vs-ro-vs-hers-vs-henry-meds`, and the patient checkout screen. Reinforces the flat-price promise at every purchase touchpoint.",
    "Companion App: content container now uses `max-w-md md:max-w-2xl lg:max-w-3xl` responsive width so the whole app expands on desktop instead of showing as a 448 px phone column. Bottom tab bar tracks the same width. Home food quick-actions expand to `md:grid-cols-4`; Rewards grid expands to `md:grid-cols-3 lg:grid-cols-4`.",
    "Fact or Myth library expanded from 4 → 12 seed entries (cold-chain, alcohol swab, syringe reuse, missed dose, titration, FDA-approved compounded, alcohol interaction, room-temp storage). Seed is now **additive by id** — new entries flow into production on next backend restart without touching admin-created rows.",
    "Landing page: 'Now Serving Florida' plaque restyled — deep navy → royal-blue gradient with cyan divider lines and sky text (was fuchsia/purple).",
    "Removed the 'Royal-anchored model — $20 per Royal order...' explainer strip from the Commission Rates card. The 4 rate inputs remain but no more verbose ROI text.",
    "Medical Director Agreement PDF generator + BAA pre-fill + typed e-sign flow (`/api/esign/{doc_type}`) + unified Signed Contracts vault in the Compliance tab.",
    "SOP Book: live PDF endpoint with clickable TOC, bookmarks, and a Director **Sign All** signature action; audit trail visible in the Compliance tab.",
    "Atlantic.net production live: web + backend deployed on Windows Server with IIS reverse-proxy → FastAPI on 8001 + PM2 windows service + Let's Encrypt (win-acme) + Task Scheduler CI/CD auto-pull every 3 min + daily 2 AM AES-256 encrypted DB backups.",
    "IIS `web.config` now lives in `frontend/public/` so every `yarn build` copies it verbatim into `build/` — contains both the React Router rewrite AND the reverse-proxy rule for `/api/*` → `http://127.0.0.1:8001/api/*`. Fixes 404s on `/cliniclogin`, `/admin`, `/pricing`, etc. and keeps `/api` reachable from IIS.",
    "Dark-mode fixes: Fit Quiz Projection card and Loyalty & Rewards inputs now properly render on dark theme (previously mostly-white on dark backgrounds).",
    "iOS App Store submission assets produced: 1284×2778 iPhone 6.5\" and 2064×2752 iPad 13\" screenshots (upscaled from real device screenshots with a color-matched blurred iPad canvas). Play Store assets: 512×512 icon and 1024×500 feature graphic.",
    "Legacy `/companion/medications-info` retired (now a 301-style stub) — public medication info flows through `/pricing` and `/pricing-vs-ro-vs-hers-vs-henry-meds`.",
  ]},
  { date: "2026-08-09", entries: [
    "Mobile app now runs a ~2.6s post-login boot splash (aurora + typewriter 'normalize the journey'), respects reduced-motion, and includes a Profile toggle for the boot tick sound.",
    "Mobile app now supports Light / Dark / System appearance — Profile → Preferences → Appearance. Persists to storage; the whole app tree re-renders on flip.",
    "OS launch splash updated with the aurora 'G' + baked-in 'NORMALIZE THE JOURNEY' wordmark (visible before JS boots).",
    "COA Management: multi-select checkboxes + floating action bar with **Bulk edit** (partial-field update up to 200 rows) and **Delete** (soft-delete + audit). Search across pharmacy, medication, lot.",
    "SEC-001: new self-service `POST /api/admin/change-password` (session revocation + strength check) — surfaced as a big card at the top of Admin Overview, no more hunting through My Profile.",
    "Backend startup now warns when `.env::ADMIN_PASSWORD` is still set after DB seed — nudge to rotate + clear the env.",
    "Web landing: extracted 8 self-contained sub-components under `src/components/landing/` (SectionCTA, Footprints, RollingBanner, WeightLossCalculator, GLP1ProgressGraph, FactOrMythCarousel, LandingYouTubeCarousel) — LandingPage.js down from 1783 → 1160 lines.",
    "Mobile dashboard: extracted 10 sub-components under `src/dashboard/` (Greeting, PastDue, SymptomCheckIn, IdentityVerification, CoaLookup, SetupProgress, DeliveryWindow, QuickActions, ReferFriend, CustomerCare) — dashboard.tsx down from 358 → 98 lines.",
    "Aurora app icon applied (`/app/mobile/assets/images/icon.png` + `adaptive-icon.png`) — effective on next Publish build.",
    "Perf pass: added Mongo indexes on sessions, coas(is_active), wellness_* (patient_id+logged_at), step_point_grants, subscriptions, push_tokens; new per-IP throttle helper `enforce_ip_rate_limit()` wired into `/auth/login` (15/min), `/auth/employee-login` (10/min), `/auth/forgot-password` (5/min).",
  ]},
  { date: "2026-02-26", entries: [
    "COA pipeline: bulk admin upload, SendGrid Inbound Parse email auto-ingest, auto-link to shipped orders via LabFlo lot_number, patient-facing 'Verify your vial' tiles in dashboard + shipping email + SMS short-link (glpflo.org/c/{id})",
    "Patient Identity Verification: GovID photo + 5s video attestation, prescriber review queue with 'unverified only' filter + ID badges, one-click 'Nudge to upload ID' SMS+email (6h cooldown)",
    "Meet the Owners public page (/meet-the-owners) with 3 placeholder cards auto-seeded; super-admin CMS via /api/admin/owners (audit-logged)",
    "YouTube Video Library: hybrid auto-pull (nightly APScheduler at 02:15 UTC) + super-admin feature/hide overrides. Surfaces on Profile, Companion App, landing footer. Requires YOUTUBE_API_KEY env",
    "New chatbot FAQ templates: info-coa-not-found, info-missed-dose; new suggested chip 'Why can't I find my COA?'",
    "FAQ baseline seed expanded with 6 entries (COA verify, COA missing, ID verification, missed dose, video library, owners) — idempotent on startup",
    "Patient Help Guide updated with 6 sections: verify-vial, coa-missing, identity-verification, video-library, meet-owners, share-your-story",
    "FAQ ↔ Chatbot auto-sync — every FAQ create/update/delete in admin tab mirrors to chatbot canned responses (slash='faq-{slug}')",
    "14-day post-shipment testimonial milestone — daily 10:00 UTC scheduler, SMS + email dispatch, /s/{uid}/{token} short-link, dedupes behind 12-week and 20-lb milestones",
    "Team table search-as-you-type capability filter (only shows for staff roles)",
    "Owners CMS admin tab — full CRUD with headshot upload, reorder, placeholder seed pill",
    "YouTube Library admin tab — refresh-now button, feature/hide toggles, position editor, clear empty-state when API key missing",
    "Email Templates: new POST endpoint to create custom templates, DELETE custom-only (seed templates refuse delete), Custom pill, Duplicate button across all 3 messaging surfaces (Email/SMS/Chat)",
    "Bug fix: Create Staff modal was scroll-locked when role=Admin expanded the Capabilities section",
    "Dialog scroll-safety hygiene pass — patched 20 admin dialogs missing max-h+overflow",
    "New <ScrollableDialogContent> wrapper at /components/ui/scrollable-dialog-content.jsx — drop-in replacement that bakes in scroll safety",
    "ESLint guardrail (no-restricted-syntax) warns on raw <DialogContent>, points to the wrapper",
  ]},
  { date: "2026-02-21", entries: [
    "Prescriber keyboard shortcuts overlay (?, T, J, K, Enter, A, D, Esc) — power-user queue navigation",
    "Training Manual: new sections for Triage Mode + filter ordering, 36 h SLA auto-reassignment + 18 h halfway warning, Live-Lock pill, Compensation Engine v3 (pay-per-order, supervisor splits, tier permissions, refill routing) and keyboard shortcuts",
  ]},
  { date: "2026-02-20", entries: [
    "Prescriber 'My Clinics' tab — manage in-person clinic addresses for in-office administration",
    "eRx modal 'Ship to Clinic' vs 'Ship to Patient' toggle (uses default clinic by default)",
    "Admin-managed Dispense Templates — prescribers can append standardised 'dispense X syringes' text to Rx Notes in one click",
    "Patient dashboard: 'What's in your GLPFlo delivery' card (post-payment) — global default + per-order overrides",
    "Patient registration: anti-fraud identity disclaimer (fake names → forfeiture of discounts + account termination)",
    "Patient Documents: new 'Prior Prescription' upload type (lets prescribers skip starter dose)",
    "Public footer now displays the configured physical / mailing address",
    "Documents tab patient list searchable by first/last name + DOB",
    "SendGrid Inbound Parse webhook (hello@glpflo.org → auto-reply with signup links) — DNS MX + SendGrid dashboard config required out-of-band",
  ]},
  { date: "2026-02-19", entries: [
    "Postcard mailer v2 (editorial) — replaces v1; price line, telehealth-to-door, free Companion App, what's-in-box, US-licensed clinicians + US customer service, phone/email/website",
    "Direct-mail postcard SVG artwork (front + back, editable in Affinity) — ROYAL50 promo code seeded",
    "Top waitlisted states tile on Admin Overview (lost-demand heatmap)",
    "Shipping waitlist + auto-launch-day email pipeline on coverage expansion",
    "Patient ShippingCoverageBadge with 30 s live polling + Pay button gating",
    "Pharmacy Partner ships_to_states allow-list + eRx send guard (409)",
    "Prescriber regimen visual grouping + above-starter alert banner",
    "Duplicate-address soft flag (anti-fraud)",
    "All staff-portal stat cards clickable (overview/tracking/refunds/digest)",
    "Recent Orders rows clickable → opens PatientDetailModal",
    "Loyalty/FL discount/referral amount fully data-driven via loyalty_rules",
    "Fact-or-Myth reorder bug fixed (bulk endpoint + lazy self-heal)",
    "Refer-a-friend amount $20 across all surfaces (was $25 in stale spots)",
    "Forgot-password link restored in landing footer Patients column",
  ]},
];

// Compute categories visible for a given role
export function getVisibleCategoriesForRole(role) {
  if (role === "admin" || role === "it") return TRAINING_CATEGORIES;
  if (role === "prescriber") {
    return TRAINING_CATEGORIES.filter(
      (c) => c.audience === "all" || c.audience === "prescriber"
    );
  }
  if (role === "customer_service") {
    return TRAINING_CATEGORIES.filter(
      (c) => c.audience === "all" || c.audience === "customer_service"
    );
  }
  return [];
}

export function getVisibleSectionsForRole(role) {
  if (role === "admin" || role === "it") {
    // Exclude sections explicitly tagged for patient audiences — those are
    // patient-help-center entries that share this file but are NOT part of
    // the staff training curriculum. They have no matching staff category
    // so they would otherwise count toward the denominator while being
    // invisible to staff, blocking 100% completion forever.
    return TRAINING_SECTIONS.filter((s) => s.audience !== "patient");
  }
  if (role === "prescriber") {
    return TRAINING_SECTIONS.filter(
      (s) => s.audience === "all" || s.audience === "prescriber"
    );
  }
  if (role === "customer_service") {
    return TRAINING_SECTIONS.filter(
      (s) => s.audience === "all" || s.audience === "customer_service"
    );
  }
  return [];
}
